import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import S3FileUpload from './S3FileUpload';
import S3FileManager from './S3FileManager';
import QuickEditModal from './QuickEditModal';
import TagManager from './TagManager';
import TemplateManager from './TemplateManager';
import OrganizationInsights from './OrganizationInsights';
import PhotoModal from './PhotoModal';
import FilePreviewModal from './FilePreviewModal';
import { getContextualChatResponse, debugFileVisibility, getUserFiles, bulkDeleteFiles, sendSuggestionFeedback, buildUrl, getUploadUrl, uploadFileToS3, confirmUpload, getUserTags, getSortingTemplates, createTag, createSortingTemplate, getIntegrationStatus, getProviderAuthUrl, disconnectProvider, listGoogleDriveFiles, listGoogleDriveChildren, issueShareLink, sendShareEmail } from '../../services/api';

// Component to format AI messages with proper structure
const FormattedAIMessage = ({ message }) => {
  const formatText = (text) => {
    // Split by lines first
    const lines = text.split('\n').filter(line => line.trim() !== '');
    const formatted = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check for headers (### or ##)
      if (line.startsWith('### ')) {
        formatted.push(<h4 key={i} className="font-semibold text-gray-900 dark:text-white mt-4 mb-2 text-sm">{line.substring(4)}</h4>);
      } else if (line.startsWith('## ')) {
        formatted.push(<h3 key={i} className="font-semibold text-gray-900 dark:text-white mt-4 mb-2">{line.substring(3)}</h3>);
      } else if (line.startsWith('# ')) {
        formatted.push(<h2 key={i} className="font-bold text-gray-900 dark:text-white mt-4 mb-2 text-lg">{line.substring(2)}</h2>);
      }
      // Check for numbered lists (1. 2. 3.)
      else if (/^\d+\.\s/.test(line)) {
        const nextLines = [];
        let j = i;
        while (j < lines.length && /^\d+\.\s/.test(lines[j].trim())) {
          nextLines.push(lines[j].trim());
          j++;
        }
        formatted.push(
          <ol key={i} className="list-decimal list-inside space-y-1 mb-2 ml-2">
            {nextLines.map((item, idx) => (
              <li key={idx} className="text-sm">{item.substring(item.indexOf('.') + 1).trim()}</li>
            ))}
          </ol>
        );
        i = j - 1; // Skip the processed lines
      }
      // Check for bullet lists (- or *)
      else if (line.startsWith('- ') || line.startsWith('* ')) {
        const nextLines = [];
        let j = i;
        while (j < lines.length && (lines[j].trim().startsWith('- ') || lines[j].trim().startsWith('* '))) {
          nextLines.push(lines[j].trim());
          j++;
        }
        formatted.push(
          <ul key={i} className="list-disc list-inside space-y-1 mb-2 ml-2">
            {nextLines.map((item, idx) => (
              <li key={idx} className="text-sm">{item.substring(2)}</li>
            ))}
          </ul>
        );
        i = j - 1; // Skip the processed lines
      }
      // Check for bold text (**text**)
      else if (line.includes('**')) {
        const parts = line.split('**');
        const formattedParts = parts.map((part, idx) => 
          idx % 2 === 1 ? <strong key={idx}>{part}</strong> : part
        );
        formatted.push(<p key={i} className="text-sm mb-2">{formattedParts}</p>);
      }
      // Regular paragraphs
      else {
        formatted.push(<p key={i} className="text-sm mb-2">{line}</p>);
      }
    }
    
    return formatted;
  };

  return (
    <div className="text-gray-700 dark:text-gray-300 space-y-1">
      {formatText(message)}
    </div>
  );
};

// Utility function for file size formatting
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default function DashboardSectionCustomComponents1() {
    // Silence AI auto-narration during background operations (uploads, tagging, etc.)
    const AI_SILENT_UPLOADS = true;
    const navigate = useNavigate();
    const [isDarkMode, setIsDarkMode] = useState(() => {
        // Initialize state from localStorage immediately to prevent flash
        if (typeof window !== 'undefined') {
            const savedTheme = localStorage.getItem('theme');
            return savedTheme === 'dark';
        }
        return false;
    });
    
    // User profile state
    const [user, setUser] = useState(null);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const [loading, setLoading] = useState(true);
    const [subscriptionLoading, setSubscriptionLoading] = useState(true);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    
    // File upload state
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const [fileRefreshTrigger, setFileRefreshTrigger] = useState(0);
    
    // AI Assistant state
    const [aiLoading, setAiLoading] = useState(false);
    const [aiQuestion, setAiQuestion] = useState('');
    const [aiMessages, setAiMessages] = useState([
        {
            type: 'ai',
            message: "Hello! I'm your AI assistant with contextual awareness. I can see your uploaded files and help you organize them. What would you like to do?"
        }
    ]);

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showSearchResults, setShowSearchResults] = useState(false);

    // AI Organization state
    const [showAISuggestionsModal, setShowAISuggestionsModal] = useState(false);
    const [aiSuggestions, setAISuggestions] = useState(null);
    const [isAnalyzingFiles, setIsAnalyzingFiles] = useState(false);
    const [aiSuggestionType, setAISuggestionType] = useState(null); // 'upload', 'batch', 'tags', 'templates'
    
    // Duplicate cleanup state
    const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
    const [duplicateGroups, setDuplicateGroups] = useState([]);
    const [isDuplicateAnalysis, setIsDuplicateAnalysis] = useState(false);

    // Navigation tabs state
    const [activeTab, setActiveTab] = useState('overview');
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [isDesktopSidebarVisible, setIsDesktopSidebarVisible] = useState(true);

    // Upload options modal state
    const [showUploadOptionsModal, setShowUploadOptionsModal] = useState(false);
    const [pendingUploadFiles, setPendingUploadFiles] = useState(null);
    const [availableTags, setAvailableTags] = useState([]);
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [newTagName, setNewTagName] = useState('');
    const [newTemplateName, setNewTemplateName] = useState('');
    const [newTemplatePath, setNewTemplatePath] = useState('');
    const [isSavingNewTag, setIsSavingNewTag] = useState(false);
    const [isSavingNewTemplate, setIsSavingNewTemplate] = useState(false);

    // Photos state
    const [photoFiles, setPhotoFiles] = useState([]);
    const [photoFolders, setPhotoFolders] = useState([]);
    const [showCreatePhotoFolder, setShowCreatePhotoFolder] = useState(false);
    const [newPhotoFolderName, setNewPhotoFolderName] = useState('');
    const [selectedPhotoFolder, setSelectedPhotoFolder] = useState(null);
    const [currentPhotoFolderPath, setCurrentPhotoFolderPath] = useState('');
    const [photoDragActive, setPhotoDragActive] = useState(false);
    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [photoUrls, setPhotoUrls] = useState({}); // Cache for presigned URLs
    const [sharingFile, setSharingFile] = useState(null); // unified share modal
    // Photo multi-select + bulk delete
    const [selectedPhotoKeys, setSelectedPhotoKeys] = useState(new Set());
    const [lastSelectedPhotoIndex, setLastSelectedPhotoIndex] = useState(null);
    const [showPhotoBulkDeleteConfirm, setShowPhotoBulkDeleteConfirm] = useState(false);
    const [photoBulkDeleting, setPhotoBulkDeleting] = useState(false);

    // Google Drive integration state
    const [isGDriveConnected, setIsGDriveConnected] = useState(false);
    const [showGDriveBrowser, setShowGDriveBrowser] = useState(false);
    const [gdriveChildren, setGdriveChildren] = useState([]);
    const [gdriveFiles, setGdriveFiles] = useState([]);
    const [gdrivePathStack, setGdrivePathStack] = useState([{ id: 'root', name: 'My Drive' }]);
    const [gdriveLoading, setGdriveLoading] = useState(false);

    // File preview state
    const [showFileModal, setShowFileModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [quickEditTarget, setQuickEditTarget] = useState(null);
    // Files folder management state
    const [fileFolders, setFileFolders] = useState([]);
    const [showCreateFileFolder, setShowCreateFileFolder] = useState(false);
    const [newFileFolderName, setNewFileFolderName] = useState('');
    const [selectedFileFolder, setSelectedFileFolder] = useState(null);
    // Audio files state
    const [audioFiles, setAudioFiles] = useState([]);
    const [audioFolders, setAudioFolders] = useState([]);
    const [showCreateAudioFolder, setShowCreateAudioFolder] = useState(false);
    const [newAudioFolderName, setNewAudioFolderName] = useState('');
    const [selectedAudioFolder, setSelectedAudioFolder] = useState(null);
    const [audioDragActive, setAudioDragActive] = useState(false);
    const [showAudioModal, setShowAudioModal] = useState(false);
    const [selectedAudio, setSelectedAudio] = useState(null);
    // Audio multi-select + bulk delete
    const [selectedAudioKeys, setSelectedAudioKeys] = useState(new Set());
    const [lastSelectedAudioIndex, setLastSelectedAudioIndex] = useState(null);
    const [showAudioBulkDeleteConfirm, setShowAudioBulkDeleteConfirm] = useState(false);
    const [audioBulkDeleting, setAudioBulkDeleting] = useState(false);

    // Video files state
    const [videoFiles, setVideoFiles] = useState([]);
    const [videoFolders, setVideoFolders] = useState([]);
    const [showCreateVideoFolder, setShowCreateVideoFolder] = useState(false);
    const [newVideoFolderName, setNewVideoFolderName] = useState('');
    const [selectedVideoFolder, setSelectedVideoFolder] = useState(null);
    const [videoDragActive, setVideoDragActive] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);
    // Video multi-select + bulk delete
    const [selectedVideoKeys, setSelectedVideoKeys] = useState(new Set());
    const [lastSelectedVideoIndex, setLastSelectedVideoIndex] = useState(null);
    const [showVideoBulkDeleteConfirm, setShowVideoBulkDeleteConfirm] = useState(false);
    const [videoBulkDeleting, setVideoBulkDeleting] = useState(false);

    // Folder deletion state (all sections)
    const [folderDeleteTarget, setFolderDeleteTarget] = useState(null); // { kind: 'files'|'photos'|'audio'|'videos', path: string }
    const [folderDeleteCount, setFolderDeleteCount] = useState(0);
    const [isDeletingFolder, setIsDeletingFolder] = useState(false);

    // Template creation state
    const [showCreateTemplate, setShowCreateTemplate] = useState(false);
    const [newTemplate, setNewTemplate] = useState({
        name: '',
        directoryPath: '',
        description: '',
        fileTypes: ['all'], // ['photos', 'audio', 'videos', 'documents', 'all']
        variables: [], // [{name: 'Child', source: 'tag', tagName: 'child_name', required: true}]
        enabled: true
    });
    const [customTemplates, setCustomTemplates] = useState([]);
    const [showTemplateVariableModal, setShowTemplateVariableModal] = useState(false);
    const [pendingTemplateApplication, setPendingTemplateApplication] = useState(null);

    const extractVirtualPath = (file) => {
        return (
            file.virtual_path ||
            file.ai_virtual_path ||
            (file.ai && (file.ai.virtual_path || file.ai.virtualPath)) ||
            (file.ai_analysis && (file.ai_analysis.virtual_path || file.ai_analysis.virtualPath)) ||
            (file.metadata && (file.metadata.virtual_path || file.metadata.virtualPath)) ||
            ''
        );
    };

    // Breadcrumb renderer for folder navigation (always visible)
    const renderBreadcrumb = (path, onNavigate, rootLabel = 'Root') => {
        const clean = (path || '').replace(/^\/+|\/+$/g, '');
        const segments = clean ? clean.split('/').filter(Boolean) : [];

        // Build clickable segments with cumulative paths
        const items = [];
        items.push(
            <button
                key="root"
                className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                onClick={() => onNavigate('')}
            >
                <svg className="w-3.5 h-3.5 opacity-80" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 7v8a2 2 0 002 2h10a2 2 0 002-2V8a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                {rootLabel}
            </button>
        );
        let acc = '';
        segments.forEach((seg, idx) => {
            acc = acc ? `${acc}/${seg}` : seg;
            items.push(
                <span key={`sep-${idx}`} className="mx-1 text-xs text-gray-400">/</span>
            );
            items.push(
                <button
                    key={`seg-${idx}`}
                    className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    onClick={() => onNavigate(acc)}
                >
                    <svg className="w-3.5 h-3.5 opacity-80" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 7v8a2 2 0 002 2h10a2 2 0 002-2V8a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                    {seg}
                </button>
            );
        });

        return (
            <div className="mb-3 flex items-center flex-wrap gap-y-1">{items}</div>
        );
    };

    // Clear photo selection when path changes
    useEffect(() => {
        setSelectedPhotoKeys(new Set());
        setLastSelectedPhotoIndex(null);
    }, [currentPhotoFolderPath]);

    // Helpers for photo selection within current view
    const getCurrentPhotoList = () => getFilesForPath(photoFiles, currentPhotoFolderPath, 'photos');
    const isAllPhotosSelected = () => {
        const list = getCurrentPhotoList();
        return list.length > 0 && list.every(p => selectedPhotoKeys.has(p.file_key));
    };
    const handlePhotoSelectAll = () => {
        const list = getCurrentPhotoList();
        if (isAllPhotosSelected()) {
            setSelectedPhotoKeys(new Set());
        } else {
            setSelectedPhotoKeys(new Set(list.map(p => p.file_key)));
        }
    };
    const handlePhotoCheckboxChange = (index, photo, e) => {
        const isSelected = e.target.checked;
        const isShift = e && e.nativeEvent && e.nativeEvent.shiftKey;
        setSelectedPhotoKeys(prev => {
            const newSelected = new Set(prev);
            const list = getCurrentPhotoList();
            if (isShift && lastSelectedPhotoIndex !== null && list.length > 0) {
                const start = Math.min(lastSelectedPhotoIndex, index);
                const end = Math.max(lastSelectedPhotoIndex, index);
                for (let i = start; i <= end; i++) {
                    const key = list[i]?.file_key;
                    if (!key) continue;
                    if (isSelected) newSelected.add(key); else newSelected.delete(key);
                }
            } else {
                if (isSelected) newSelected.add(photo.file_key); else newSelected.delete(photo.file_key);
            }
            return newSelected;
        });
        setLastSelectedPhotoIndex(index);
    };
    const handlePhotoShiftToggle = (index, photo) => {
        const list = getCurrentPhotoList();
        const currentlySelected = selectedPhotoKeys.has(photo.file_key);
        const willSelect = !currentlySelected;
        setSelectedPhotoKeys(prev => {
            const newSelected = new Set(prev);
            if (lastSelectedPhotoIndex !== null && list.length > 0) {
                const start = Math.min(lastSelectedPhotoIndex, index);
                const end = Math.max(lastSelectedPhotoIndex, index);
                for (let i = start; i <= end; i++) {
                    const key = list[i]?.file_key;
                    if (!key) continue;
                    if (willSelect) newSelected.add(key); else newSelected.delete(key);
                }
            } else {
                if (willSelect) newSelected.add(photo.file_key); else newSelected.delete(photo.file_key);
            }
            return newSelected;
        });
        setLastSelectedPhotoIndex(index);
    };
    const handlePhotoBulkDeleteRequest = () => {
        if (selectedPhotoKeys.size === 0) return;
        setShowPhotoBulkDeleteConfirm(true);
    };
    const handlePhotoBulkDeleteConfirm = async () => {
        if (selectedPhotoKeys.size === 0) return;
        setPhotoBulkDeleting(true);
        setShowPhotoBulkDeleteConfirm(false);
        const keys = Array.from(selectedPhotoKeys);
        try {
            try {
                await bulkDeleteFiles(keys);
            } catch (err) {
                // Fallback: best-effort per-file delete
                const { deleteFile } = await import('../../services/api');
                await Promise.all(keys.map(k => deleteFile(k).catch(() => null)));
            }
            // Remove from local state
            setUploadedFiles(prev => prev.filter(f => !selectedPhotoKeys.has(f.file_key)));
            setPhotoFiles(prev => prev.filter(f => !selectedPhotoKeys.has(f.file_key)));
            setSelectedPhotoKeys(new Set());
        } catch (e) {
            alert(`Failed to delete some photos: ${e.message}`);
        } finally {
            setPhotoBulkDeleting(false);
        }
    };
    const handlePhotoBulkDeleteCancel = () => setShowPhotoBulkDeleteConfirm(false);
    // Load initial files for search functionality
    useEffect(() => {
        const loadInitialFiles = async () => {
            try {
                const files = await getUserFiles();
                console.log('Initial files loaded for search:', files.length);
                console.log('[Folders Debug] Raw API sample (first 5):', (files || []).slice(0,5).map(f => ({ filename: f.filename, virtual_path: f.virtual_path, ai_suggested_name: f.ai_suggested_name })));
                const processedFiles = files.map(file => ({
                    ...file,
                    original_name: file.filename, // Map filename to original_name for consistency
                    id: file.file_key,
                    virtual_path: extractVirtualPath(file)
                }));
                
                setUploadedFiles(processedFiles);
                
                // Filter image files for photos section
                const imageFiles = processedFiles.filter(file => {
                    const ext = file.filename?.toLowerCase().split('.').pop();
                    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
                });
                setPhotoFiles(imageFiles);

                // Filter audio files for audio section
                const audioFilesList = processedFiles.filter(file => {
                    const ext = file.filename?.toLowerCase().split('.').pop();
                    return ['mp3', 'wav', 'flac', 'm4a', 'aac', 'ogg', 'wma'].includes(ext);
                });
                setAudioFiles(audioFilesList);

                // Filter video files for videos section
                const videoFilesList = processedFiles.filter(file => {
                    const ext = file.filename?.toLowerCase().split('.').pop();
                    return ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm', 'm4v'].includes(ext);
                });
                setVideoFiles(videoFilesList);
                // Derived folder snapshots at root
                console.log('[Folders Debug] Photos derived subfolders @root:', getSubfoldersFromFiles(imageFiles, '', 'photos'));
                console.log('[Folders Debug] Files derived subfolders @root:', getSubfoldersFromFiles(processedFiles, '', 'files'));
                console.log('[Folders Debug] Audio derived subfolders @root:', getSubfoldersFromFiles(audioFilesList, '', 'audio'));
                console.log('[Folders Debug] Videos derived subfolders @root:', getSubfoldersFromFiles(videoFilesList, '', 'videos'));
                
            } catch (error) {
                console.error('Failed to load initial files:', error);
            }
        };

        if (user) {
            loadInitialFiles();
        }
    }, [user]);

    // Fetch user profile data
    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const token = localStorage.getItem('access_token');
                if (!token) {
                    navigate('/login');
                    return;
                }

                const response = await fetch(buildUrl('/me'), {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const userData = await response.json();
                    setUser(userData);
                } else {
                    // Token invalid, redirect to login
                    localStorage.removeItem('access_token');
                    navigate('/login');
                }
            } catch (error) {
                console.error('Failed to fetch user profile:', error);
                navigate('/login');
            } finally {
                setLoading(false);
            }
        };

        fetchUserProfile();
    }, [navigate]);

    // Load integration status (Drive)
    useEffect(() => {
        const load = async () => {
            try {
                const status = await getIntegrationStatus();
                const connected = !!(status && status.google_drive);
                setIsGDriveConnected(connected);
                if (connected) {
                    try {
                        const data = await listGoogleDriveFiles();
                        const files = Array.isArray(data?.files) ? data.files : [];
                        setGdriveFiles(files);
                    } catch (_) { setGdriveFiles([]); }
                } else {
                    setGdriveFiles([]);
                }
            } catch (_) {}
        };
        load();
    }, []);

    const gdriveToCategory = (mime = '') => {
        const m = (mime || '').toLowerCase();
        if (m.startsWith('image/')) return 'photos';
        if (m.startsWith('audio/')) return 'audio';
        if (m.startsWith('video/')) return 'videos';
        return 'files';
    };

    const gdriveItemToTile = (f) => ({
        id: f.id,
        name: f.name,
        mimeType: f.mimeType,
        size: Number(f.size || 0),
        modifiedTime: f.modifiedTime,
        viewUrl: `https://drive.google.com/file/d/${f.id}/view`
    });

    const gdriveByCategory = {
        files: gdriveFiles.filter(f => gdriveToCategory(f.mimeType) === 'files').map(gdriveItemToTile),
        photos: gdriveFiles.filter(f => gdriveToCategory(f.mimeType) === 'photos').map(gdriveItemToTile),
        audio: gdriveFiles.filter(f => gdriveToCategory(f.mimeType) === 'audio').map(gdriveItemToTile),
        videos: gdriveFiles.filter(f => gdriveToCategory(f.mimeType) === 'videos').map(gdriveItemToTile),
    };

    // Unique immediate parent folders from Google Drive
    const gdriveParentFolders = React.useMemo(() => {
        const map = new Map();
        (gdriveFiles || []).forEach((f) => {
            const pid = (f.parents && f.parents[0]) || null;
            const pname = f.parent_name || null;
            if (pid && pname && !map.has(pid)) {
                map.set(pid, pname);
            }
        });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [gdriveFiles]);

    const openGDriveFolderById = async (folderId, name) => {
        try {
            setShowGDriveBrowser(true);
            const stack = [{ id: 'root', name: 'My Drive' }];
            if (folderId && name) stack.push({ id: folderId, name });
            setGdrivePathStack(stack);
            setGdriveLoading(true);
            const data = await listGoogleDriveChildren({ parentId: folderId || 'root' });
            setGdriveChildren(Array.isArray(data?.files) ? data.files : []);
        } catch (_) {
            setGdriveChildren([]);
        } finally {
            setGdriveLoading(false);
        }
    };

    // Per-category parent folders from Google Drive (deduped by parent id)
    const gdriveParentFoldersByCategory = React.useMemo(() => {
        const build = (predicate) => {
            const m = new Map();
            (gdriveFiles || []).forEach((f) => {
                const mt = (f.mimeType || '').toLowerCase();
                if (!predicate(mt)) return;
                const pid = (f.parents && f.parents[0]) || null;
                const pname = f.parent_name || null;
                if (pid && pname && !m.has(pid)) m.set(pid, pname);
            });
            return Array.from(m.entries()).map(([id, name]) => ({ id, name }));
        };
        return {
            photos: build((mt) => mt.startsWith('image/')),
            audio: build((mt) => mt.startsWith('audio/')),
            videos: build((mt) => mt.startsWith('video/')),
        };
    }, [gdriveFiles]);

    const openGDriveBrowser = async (reset = false) => {
        try {
            if (reset) setGdrivePathStack([{ id: 'root', name: 'My Drive' }]);
            setShowGDriveBrowser(true);
            setGdriveLoading(true);
            const parent = (reset ? { id: 'root' } : gdrivePathStack[gdrivePathStack.length - 1]);
            const data = await listGoogleDriveChildren({ parentId: parent.id });
            setGdriveChildren(Array.isArray(data?.files) ? data.files : []);
        } catch (_) {
            setGdriveChildren([]);
        } finally {
            setGdriveLoading(false);
        }
    };

    const driveNavInto = async (folder) => {
        const isFolder = (folder?.mimeType || '').toLowerCase() === 'application/vnd.google-apps.folder';
        if (!isFolder) return;
        const next = [...gdrivePathStack, { id: folder.id, name: folder.name }];
        setGdrivePathStack(next);
        setGdriveLoading(true);
        try {
            const data = await listGoogleDriveChildren({ parentId: folder.id });
            setGdriveChildren(Array.isArray(data?.files) ? data.files : []);
        } catch (_) {
            setGdriveChildren([]);
        } finally {
            setGdriveLoading(false);
        }
    };

    const driveNavUp = async () => {
        if (gdrivePathStack.length <= 1) return;
        const next = gdrivePathStack.slice(0, -1);
        setGdrivePathStack(next);
        setGdriveLoading(true);
        try {
            const data = await listGoogleDriveChildren({ parentId: next[next.length - 1].id });
            setGdriveChildren(Array.isArray(data?.files) ? data.files : []);
        } catch (_) {
            setGdriveChildren([]);
        } finally {
            setGdriveLoading(false);
        }
    };
    // Check subscription status
    useEffect(() => {
        const checkSubscription = async () => {
            try {
                const token = localStorage.getItem('access_token');
                if (!token) return;

                // Check if user just completed payment
                const urlParams = new URLSearchParams(window.location.search);
                const paymentStatus = urlParams.get('payment');
                const sessionId = urlParams.get('session_id');
                
                console.log('🔍 URL params:', { paymentStatus, sessionId });

                const apiBase = buildUrl();

                // If user just completed payment, verify the session first
                if (sessionId) {
                    console.log('🔄 Verifying Stripe session:', sessionId);
                    try {
                        const sessionResponse = await fetch(buildUrl(`/subscription/verify-session/${sessionId}`), {
                            headers: {
                                'Authorization': `Bearer ${token}`
                            }
                        });

                        if (sessionResponse.ok) {
                            const sessionData = await sessionResponse.json();
                            console.log('✅ Session verified:', sessionData);
                            
                            // Clean up URL parameters
                            window.history.replaceState({}, document.title, window.location.pathname);
                            
                            // Show success message briefly
                            console.log('🎉 Payment successful! Welcome to Ordio!');
                            setPaymentSuccess(true);
                            setTimeout(() => setPaymentSuccess(false), 5000);
                        }
                    } catch (error) {
                        console.error('Session verification error:', error);
                    }
                }

                // Add a small delay if payment was just completed to allow webhook processing
                if (paymentStatus === 'success' || sessionId) {
                    console.log('⏳ Payment completed, waiting for webhook processing...');
                    await new Promise(resolve => setTimeout(resolve, 2000));
                }

                const response = await fetch(buildUrl('/subscription/'), {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (response.ok) {
                    const subData = await response.json();
                    console.log('📊 Subscription check:', subData);
                    
                    // Normalize and broaden acceptable states
                    const normalizedStatus = (subData?.subscription_status || '').toLowerCase();
                    const hasActivePlan = (normalizedStatus === 'active' || normalizedStatus === 'trialing');
                    
                    // If user just completed payment but subscription isn't active yet, try test webhook
                    if (!hasActivePlan && (paymentStatus === 'success' || sessionId)) {
                        console.log('🔄 Payment completed but subscription not active, triggering webhook test...');
                        try {
                            // Try to infer intended plan from recent selection (if any)
                            let intendedPlanType = 'individual';
                            try {
                                const sel = JSON.parse(localStorage.getItem('selectedPlan') || '{}');
                                if (sel && (sel.plan === 'family' || sel.plan === 'individual')) {
                                    intendedPlanType = sel.plan;
                                }
                            } catch (_) {}

                            await fetch(buildUrl('/subscription/test-webhook'), {
                                method: 'POST',
                                headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ plan_type: intendedPlanType })
                            });
                            
                            // Wait a moment and check again
                            await new Promise(resolve => setTimeout(resolve, 1000));
                            const retryResponse = await fetch(buildUrl('/subscription/'), {
                                headers: {
                                    'Authorization': `Bearer ${token}`
                                }
                            });
                            
                            if (retryResponse.ok) {
                                const retrySubData = await retryResponse.json();
                                console.log('📊 Retry subscription check:', retrySubData);
                                const retryStatus = (retrySubData?.subscription_status || '').toLowerCase();
                                const retryHasActivePlan = (retryStatus === 'active' || retryStatus === 'trialing');
                                
                                if (retryHasActivePlan) {
                                    console.log('✅ Subscription activated after webhook test!');
                                    // Clean up URL parameters
                                    window.history.replaceState({}, document.title, window.location.pathname);
                                    setSubscriptionLoading(false);
                                    return;
                                }
                            }
                        } catch (error) {
                            console.error('Test webhook error:', error);
                        }
                    }
                    if (!hasActivePlan) {
                        // Only redirect if this wasn't a recent payment completion
                        if (!paymentStatus && !sessionId) {
                            console.log('❌ No active subscription, redirecting to plan selection');
                            navigate('/plan-selection');
                            return;
                        } else {
                            console.log('⚠️ Payment completed but subscription not yet active. Please contact support if this persists.');
                        }
                    } else {
                        console.log('✅ Active subscription found:', subData.plan_type);
                        // Clean up URL parameters on successful load
                        if (paymentStatus || sessionId) {
                            window.history.replaceState({}, document.title, window.location.pathname);
                        }
                    }
                } else {
                    // Only redirect if this wasn't a recent payment completion
                    if (!paymentStatus && !sessionId) {
                        console.log('❌ Failed to fetch subscription, redirecting to plan selection');
                        navigate('/plan-selection');
                    } else {
                        console.log('⚠️ Payment completed but unable to verify subscription. Please refresh the page.');
                    }
                }
            } catch (error) {
                console.error('Error checking subscription:', error);
                // Only redirect if this wasn't a recent payment completion
                const urlParams = new URLSearchParams(window.location.search);
                const paymentStatus = urlParams.get('payment');
                const sessionId = urlParams.get('session_id');
                
                if (!paymentStatus && !sessionId) {
                    navigate('/plan-selection');
                }
            } finally {
                setSubscriptionLoading(false);
            }
        };

        if (user) {
            checkSubscription();
        }
    }, [user, navigate]);

    // Initialize theme on component mount
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme');
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        
        // If there's no saved preference, default to light mode
        if (!savedTheme) {
            setIsDarkMode(false);
        } else {
            setIsDarkMode(savedTheme === 'dark');
        }
        
        // Apply initial theme immediately
        const htmlElement = document.documentElement;
        const bodyElement = document.body;
        
        if (savedTheme === 'dark') {
            htmlElement.classList.add('dark');
            bodyElement.classList.add('dark');
        } else {
            htmlElement.classList.remove('dark');
            bodyElement.classList.remove('dark');
        }
    }, []);
    // Apply theme changes to the document
    useEffect(() => {
        const htmlElement = document.documentElement;
        const bodyElement = document.body;
        
        if (isDarkMode) {
            htmlElement.classList.add('dark');
            bodyElement.classList.add('dark');
            localStorage.setItem('theme', 'dark');
        } else {
            htmlElement.classList.remove('dark');
            bodyElement.classList.remove('dark');
            localStorage.setItem('theme', 'light');
        }
    }, [isDarkMode]);

    const toggleDarkMode = () => {
        setIsDarkMode(!isDarkMode);
    };

    // Profile dropdown handlers
    const toggleProfileDropdown = () => {
        setShowProfileDropdown(!showProfileDropdown);
    };

    const handleLogout = () => {
        localStorage.removeItem('access_token');
        navigate('/login');
    };

    const handleSettings = () => {
        navigate('/settings');
        setShowProfileDropdown(false);
    };
    
    // File upload handlers
    const handleFileUpload = async (files, uploadOptions = null, onProgress = null, shouldAnalyze = true) => {
        if (!files || files.length === 0) return;
        
        setUploadLoading(true);
        try {
            const uploadedResults = [];
            let fileIdx = 0;
            for (const file of Array.from(files)) {
                try {
                    const contentType = file.type || 'application/octet-stream';
                    // Choose multipart if file is large (>= 100MB), else single PUT
                    const LARGE_FILE_THRESHOLD = 100 * 1024 * 1024;
                    if (!AI_SILENT_UPLOADS) setAiMessages(prev => [...prev, { type: 'ai', message: `Uploading ${file.name}...` }]);
                    if (file.size >= LARGE_FILE_THRESHOLD) {
                        const { uploadLargeFileMultipart } = await import('../../services/api');
                        const res = await uploadLargeFileMultipart(file, { partSizeBytes: 16 * 1024 * 1024, concurrency: 6 }, (p) => {
                            if (onProgress) onProgress(fileIdx, p);
                        });
                        uploadedResults.push({
                            filename: file.name,
                            file_key: res.key,
                            upload_id: res.upload_id,
                        });
                        if (onProgress) onProgress(fileIdx, 100);
                    } else {
                        const { upload_id, presigned_url, content_type } = await getUploadUrl(file.name, contentType);
                        const { uploadFileToS3WithProgress, confirmUpload } = await import('../../services/api');
                    await uploadFileToS3WithProgress(presigned_url, file, content_type, (p) => {
                        if (onProgress) onProgress(fileIdx, p);
                    });
                    const confirm = await confirmUpload(upload_id, file.size);
                    uploadedResults.push({
                        ...confirm,
                        filename: file.name,
                        file_key: confirm.file_key || confirm.key || null,
                        upload_id,
                    });
                    if (onProgress) onProgress(fileIdx, 100);
                    }
                } catch (innerErr) {
                    console.error('Single file upload failed:', innerErr);
                }
                fileIdx += 1;
            }

            if (uploadedResults.length === 0) {
                throw new Error('No files were uploaded');
            }

            const newFiles = uploadedResults.map(file => ({
                ...file,
                original_name: file.filename,
                id: file.file_key
            }));
            // Apply selected tags and template path on the backend so data is persisted and visible
            if (uploadOptions) {
                const { tagIds = [], templateId = null, templatePath = '' } = uploadOptions;
                if (tagIds.length > 0) {
                    try {
                        // Map selected IDs to names for AI-visible tags
                        const selectedTags = (availableTags || []).filter(t => tagIds.includes(t.tag_id || t.id));
                        const tagNames = selectedTags.map(t => (t.name || t.label || '').trim()).filter(Boolean);
                        const { updateFileTags, addTagToFiles } = await import('../../services/api');
                        for (const f of newFiles) {
                            try { await updateFileTags(f.upload_id || f.id, tagNames); } catch (_) {}
                        }
                        // Ensure Tag Manager usage counts reflect application
                        for (const tagId of tagIds) {
                            try { await addTagToFiles(tagId, newFiles.map(nf => nf.upload_id || nf.id)); } catch (_) {}
                        }
                    } catch (_e) {
                        // non-fatal
                    }
                }
                if (templatePath && templatePath.trim()) {
                    try {
                        const { updateVirtualPath } = await import('../../services/api');
                        for (const f of newFiles) {
                            try {
                                await updateVirtualPath(f.upload_id || f.id, templatePath.trim());
                            } catch (_e) {
                                // continue others
                            }
                        }
                    } catch (_e) {
                        // ignore
                    }
                }
                if (!AI_SILENT_UPLOADS && (tagIds.length > 0 || templateId || templatePath)) {
                    setAiMessages(prev => [
                        ...prev,
                        {
                            type: 'ai',
                            message: `Applied ${tagIds.length} tag(s)${templateId ? ` and template` : ''}${templatePath ? ` (path: ${templatePath})` : ''} to your new upload${newFiles.length > 1 ? 's' : ''}. I'll remember these preferences.`
                        }
                    ]);
                }
            }
            setUploadedFiles(prev => {
                const byId = new Map((prev || []).map(f => [f.upload_id || f.id, f]));
                for (const nf of newFiles) {
                    byId.set(nf.upload_id || nf.id, { ...(byId.get(nf.upload_id || nf.id) || {}), ...nf });
                }
                return Array.from(byId.values());
            });

            // Refresh file list from backend so tags/names and virtual paths are up to date immediately
            try {
                const { getUserFiles } = await import('../../services/api');
                const refreshed = await getUserFiles();
                if (Array.isArray(refreshed)) {
                    console.log('[AI Modal Debug] Fresh API sample (first 5):', refreshed.slice(0,5).map(f => ({ filename: f.filename, virtual_path: f.virtual_path, ai_suggested_name: f.ai_suggested_name })));
                    const processed = refreshed.map(f => ({
                        ...f,
                        original_name: f.filename,
                        id: f.file_key,
                        virtual_path: (
                            f.virtual_path ||
                            f.ai_virtual_path ||
                            (f.ai && (f.ai.virtual_path || f.ai.virtualPath)) ||
                            (f.ai_analysis && (f.ai_analysis.virtual_path || f.ai_analysis.virtualPath)) ||
                            (f.metadata && (f.metadata.virtual_path || f.metadata.virtualPath)) ||
                            ''
                        )
                    }));
                    setUploadedFiles(processed);
                    // Update section-specific lists
                    const imageFiles = processed.filter(file => {
                        const ext = file.filename?.toLowerCase().split('.').pop();
                        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
                    });
                    setPhotoFiles(imageFiles);
                    const audioFilesList = processed.filter(file => {
                        const ext = file.filename?.toLowerCase().split('.').pop();
                        return ['mp3', 'wav', 'flac', 'm4a', 'aac', 'ogg', 'wma'].includes(ext);
                    });
                    setAudioFiles(audioFilesList);
                    const videoFilesList = processed.filter(file => {
                        const ext = file.filename?.toLowerCase().split('.').pop();
                        return ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm', 'm4v'].includes(ext);
                    });
                    setVideoFiles(videoFilesList);
                }
            } catch (_e) {
                // ignore refresh errors
            }

            // Update media files immediately when uploaded
            const newImageFiles = newFiles.filter(file => {
                const ext = file.filename?.toLowerCase().split('.').pop();
                return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
            });
            const newAudioFiles = newFiles.filter(file => {
                const ext = file.filename?.toLowerCase().split('.').pop();
                return ['mp3', 'wav', 'flac', 'm4a', 'aac', 'ogg', 'wma'].includes(ext);
            });
            const newVideoFiles = newFiles.filter(file => {
                const ext = file.filename?.toLowerCase().split('.').pop();
                return ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm', 'm4v'].includes(ext);
            });

            if (newImageFiles.length > 0) {
                setPhotoFiles(prev => {
                    const byId = new Map((prev || []).map(f => [f.upload_id || f.id, f]));
                    for (const nf of newImageFiles) {
                        byId.set(nf.upload_id || nf.id, { ...(byId.get(nf.upload_id || nf.id) || {}), ...nf });
                    }
                    return Array.from(byId.values());
                });
                console.log('📸 Added', newImageFiles.length, 'new image(s) to Photos section');
            }
            if (newAudioFiles.length > 0) {
                setAudioFiles(prev => {
                    const byId = new Map((prev || []).map(f => [f.upload_id || f.id, f]));
                    for (const nf of newAudioFiles) {
                        byId.set(nf.upload_id || nf.id, { ...(byId.get(nf.upload_id || nf.id) || {}), ...nf });
                    }
                    return Array.from(byId.values());
                });
                console.log('🎵 Added', newAudioFiles.length, 'new audio file(s) to Audio section');
            }
            if (newVideoFiles.length > 0) {
                setVideoFiles(prev => {
                    const byId = new Map((prev || []).map(f => [f.upload_id || f.id, f]));
                    for (const nf of newVideoFiles) {
                        byId.set(nf.upload_id || nf.id, { ...(byId.get(nf.upload_id || nf.id) || {}), ...nf });
                    }
                    return Array.from(byId.values());
                });
                console.log('🎥 Added', newVideoFiles.length, 'new video file(s) to Videos section');
            }

            // Add success message to AI chat
            const mediaCount = newImageFiles.length + newAudioFiles.length + newVideoFiles.length;
            let mediaMessage = '';
            if (newImageFiles.length > 0) mediaMessage += `${newImageFiles.length} photo(s) added to Photos. `;
            if (newAudioFiles.length > 0) mediaMessage += `${newAudioFiles.length} audio file(s) added to Audio. `;
            if (newVideoFiles.length > 0) mediaMessage += `${newVideoFiles.length} video(s) added to Videos. `;
            if (!AI_SILENT_UPLOADS) {
                setAiMessages(prev => [...prev, {
                    type: 'ai',
                    message: `Successfully uploaded ${uploadedResults.length} file(s)! ${mediaMessage}${mediaCount > 0 ? '' : ''}Analyzing for organization suggestions...`
                }]);
            }

            // Trigger AI analysis for new uploads (Flow 1)
            if (shouldAnalyze) {
                setTimeout(() => {
                    handleUploadAIAnalysis(newFiles);
                }, 1000);
            }
            // Optional: AI-driven rename based on tags/path convention with explicit value detection
            if (uploadOptions) {
                const { tagIds = [], templatePath = '', renameOverrides = [] } = uploadOptions;
                if (templatePath || tagIds.length > 0) {
                    try {
                        const { renameFile } = await import('../../services/api');
                        const selectedTags = (availableTags || []).filter(t => tagIds.includes(t.tag_id || t.id));
                        const tagNames = selectedTags.map(t => (t.name || t.label || '').trim()).filter(Boolean);
                        // Parse key:value pairs so AI uses only values when present
                        const tagKVs = tagNames.map(raw => {
                            const m = raw.match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.+)$/);
                            if (m) return { key: m[1].trim().toLowerCase(), value: m[2].trim(), raw };
                            return { key: null, value: raw, raw };
                        });
                        const tagValues = tagKVs.map(kv => kv.value).filter(Boolean);
                        const getValueForKey = (rx) => {
                            const kv = tagKVs.find(k => k.key && rx.test(k.key));
                            return kv ? kv.value : null;
                        };

                        const sanitize = (s) => (s || '')
                          .toString()
                          .trim()
                          .replace(/\s+/g, '_')
                          .replace(/[^a-zA-Z0-9_\-]+/g, '')
                          .replace(/^_+|_+$/g, '')
                          || 'value';

                        const findAge = () => {
                            const candidates = [...tagValues, explicit.age || ''];
                            for (let raw of candidates) {
                                const n = (raw || '').toString().trim().toLowerCase().replace(/[,]/g, '');
                                if (!n) continue;
                                // Common forms: "2y", "2 y", "2yr", "2 yrs", "2 years"
                                let m = n.match(/^(\d{1,2})\s*(y|yr|yrs|year|years)$/i);
                                if (m) return `${m[1]}y`;
                                // Months: "18mo", "18 mo", "18 mos", "18 months"
                                m = n.match(/^(\d{1,3})\s*(m|mo|mos|month|months)$/i);
                                if (m) return `${m[1]}mo`;
                                // Prefixed with "age" as key: "age 2", "age: 2 years", "age= 18 months"
                                m = n.match(/^age[:=\s]+(\d{1,3})\s*(years?|yrs?)?$/i);
                                if (m) return `${m[1]}y`;
                                m = n.match(/^age[:=\s]+(\d{1,3})\s*(months?|mos?)$/i);
                                if (m) return `${m[1]}mo`;
                                // Single compact forms
                                m = n.match(/^(\d{1,2})y$/i);
                                if (m) return `${m[1]}y`;
                                m = n.match(/^(\d{1,3})mo$/i);
                                if (m) return `${m[1]}mo`;
                                // Natural language: "1 month", "1-month"
                                m = n.match(/^(\d{1,2})\s*[- ]?month(s)?$/i);
                                if (m) return `${m[1]}mo`;
                            }
                            return null;
                        };

                        const findEvent = () => {
                            // Prefer explicit Event key
                            const keyed = getValueForKey(/^(event|occasion)$/i);
                            if (keyed) return keyed;
                            const keywords = /(birthday|wedding|anniversary|graduation|vacation|party|hike|trip|holiday|christmas|new\s*year|halloween|easter|festival|concert)/i;
                            for (const n of tagValues) {
                                if (keywords.test(n)) return n;
                            }
                            return null;
                        };
                        const looksLikeName = (n) => {
                            if (!n) return false;
                            if (/\s+/.test(n) && /^[A-Za-z][A-Za-z\-']+(\s+[A-Za-z\-']+)+$/.test(n)) return true; // First Last
                            if (/^[A-Z][a-z]{2,}$/.test(n)) return true; // Single capitalized word (e.g., "Christopher")
                            return false;
                        };

                        const findPerson = () => {
                            // Prefer value from a tag with key person/name/who
                            const val = getValueForKey(/^(person|name|who)$/i);
                            if (val) return val;
                            // Else pick best name-like candidate from values
                            const candidate = tagValues.find(looksLikeName);
                            return candidate || null;
                        };

                        // Allow explicit overrides from modal
                        const explicit = window.__upload_explicit_values__ || {};
                        const person = explicit.person?.trim() || findPerson();
                        const age = explicit.age?.trim() || findAge();
                        const eventVal = explicit.event?.trim() || findEvent();
                        const second = age || eventVal || null;
                        // Unified renaming for all supported types (images, videos, audio, spreadsheets, receipts, designs)
                        const imageExts = ['jpg','jpeg','png','gif','webp','bmp','svg'];
                        const videoExts = ['mp4','mov','avi','mkv','wmv','flv','webm','m4v'];
                        const audioExts = ['mp3','wav','flac','m4a','aac','ogg','wma'];
                        let counter = 1;
                        for (let idx = 0; idx < newFiles.length; idx++) {
                            const f = newFiles[idx];
                            const ext = ((f.filename || '').split('.').pop() || '').toLowerCase();
                            const fname = (f.filename || '').toLowerCase();
                            const proposed = renameOverrides[idx];
                            let autoName = null;

                            if (imageExts.includes(ext) || videoExts.includes(ext)) {
                            const p = sanitize(person) || 'person';
                            const s = sanitize(second) || (age ? '1y' : 'event');
                                autoName = `${p}_${s}_${counter}.${ext}`;
                            } else {
                            const isSpreadsheet = ['csv','xls','xlsx'].includes(ext);
                            const isReceipt = /receipt|invoice|bill/.test(fname) || ['pdf','jpg','jpeg','png'].includes(ext);
                            const isDesign = /design|figma|sketch|ui|ux/.test(fname);
                                const isAudio = audioExts.includes(ext);
                                if (isReceipt && (explicit.merchant || explicit.date || explicit.amount)) {
                                    const m = sanitize(explicit.merchant) || 'merchant';
                                    const d = sanitize(explicit.date) || 'date';
                                    const a = sanitize(explicit.amount) || 'amt';
                                    autoName = `${m}_${d}_${a}.${ext}`;
                                } else if (isSpreadsheet && (explicit.project || explicit.docType || explicit.version)) {
                                    const proj = sanitize(explicit.project) || 'project';
                                    const kind = sanitize(explicit.docType) || 'data';
                                    const ver = sanitize(explicit.version) || 'v1';
                                    autoName = `${proj}_${kind}_${ver}.${ext}`;
                                } else if (isDesign && (explicit.project || explicit.docType || explicit.version)) {
                                    const proj = sanitize(explicit.project) || 'design';
                                    const kind = sanitize(explicit.docType) || 'spec';
                                    const ver = sanitize(explicit.version) || 'v1';
                                    autoName = `${proj}_${kind}_${ver}.${ext}`;
                                } else if (isAudio) {
                                    // audio: prefer person_event_counter or project_version
                                    const p = sanitize(person) || 'audio';
                                    const e = sanitize(eventVal) || 'track';
                                    autoName = `${p}_${e}_${counter}.${ext}`;
                                }
                            }

                            if (!autoName) continue;
                                    const newName = (proposed && proposed.includes('.') ? proposed : proposed ? `${proposed}.${ext}` : autoName);
                            try {
                                    await renameFile(f.upload_id || f.id, newName);
                                counter += 1;
                            } catch (_e) {
                                // continue
                            }
                        }
                    } catch (_e) {
                        // ignore
                    }
                }
            }
        } catch (error) {
            console.error('Upload error:', error);
            if (!AI_SILENT_UPLOADS) {
                setAiMessages(prev => [...prev, {
                    type: 'ai',
                    message: 'Sorry, there was an error uploading your files. Please try again.'
                }]);
            }
        } finally {
            setUploadLoading(false);
        }
    };
    
    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        const files = e.dataTransfer.files;
        openUploadOptionsModal(files);
    };
    
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };
    
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };
    
    const handleFileSelect = (e) => {
        const files = e.target.files;
        openUploadOptionsModal(files);
    };
    
    // AI Assistant handlers
    const handleAiSubmit = async () => {
        if (!aiQuestion.trim()) return;
        
        const userMessage = aiQuestion.trim();
        setAiQuestion('');
        setAiLoading(true);
        
        // Add user message to chat
        setAiMessages(prev => [...prev, {
            type: 'user',
            message: userMessage
        }]);
        
        try {
            console.log('🤖 Sending contextual AI question:', userMessage);
            
            // Use the new contextual chat API
            const result = await getContextualChatResponse(userMessage);
            
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: result.answer || 'I processed your request successfully.'
            }]);
            
        } catch (error) {
            console.error('AI error:', error);
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: `Sorry, I encountered an error: ${error.message}. Please try again.`
            }]);
        } finally {
            setAiLoading(false);
        }
    };
    
    const handleAiQuickAction = (action) => {
        setAiQuestion(action);
        setTimeout(() => handleAiSubmit(), 100);
    };
    
    const clearAiChat = () => {
        setAiMessages([{
            type: 'ai',
            message: "Hello! I'm your AI assistant. I can help you organize your files. What would you like to do?"
        }]);
    };

    // Upload Options modal helpers
    const loadTagsAndTemplates = async () => {
        try {
            const [tags, templates] = await Promise.all([
                getUserTags().catch(() => []),
                getSortingTemplates().catch(() => []),
            ]);
            setAvailableTags(Array.isArray(tags) ? tags : (tags?.items || []));
            setAvailableTemplates(Array.isArray(templates) ? templates : (templates?.items || []));
        } catch (_e) {
            // ignore
        }
    };

    const [lastUploadKind, setLastUploadKind] = React.useState('file');

    const openUploadOptionsModal = (files, kind = 'file') => {
        if (!files || files.length === 0) return;
        setPendingUploadFiles(files);
        setSelectedTagIds([]);
        setSelectedTemplateId(null);
        setNewTagName('');
        setNewTemplateName('');
        setNewTemplatePath('');
        loadTagsAndTemplates();
        setLastUploadKind(kind);
        setShowUploadOptionsModal(true);
    };
    const closeUploadOptionsModal = () => {
        setShowUploadOptionsModal(false);
        setPendingUploadFiles(null);
    };
    const handleCreateTagInline = async () => {
        if (!newTagName.trim()) return;
        try {
            setIsSavingNewTag(true);
            const raw = newTagName.trim();
            // Normalize to Key: Value form if possible
            const parts = raw.split(/[:=\-]+/); // split on :, =, -
            let name = raw;
            let description = '';
            if (parts.length >= 2) {
                const key = (parts[0] || '').trim();
                const value = parts.slice(1).join(':').trim();
                if (key && value) {
                    name = `${key}: ${value}`;
                    description = `Key-Value tag for ${key}`;
                }
            }
            const created = await createTag({ name, description });
            // Ensure the newly created tag has a display name locally so the user doesn't see a hash/id
            const newTagObj = {
                tag_id: created.tag_id,
                name,
                description: description,
                color: '#3B82F6',
                icon: 'tag',
                usage_count: 0,
                is_system_tag: false,
            };
            setAvailableTags(prev => [...prev, newTagObj]);
            setSelectedTagIds(prev => [...prev, created.tag_id]);
            setNewTagName('');
        } finally {
            setIsSavingNewTag(false);
        }
    };

    const handleCreateTemplateInline = async () => {
        if (!newTemplateName.trim()) return;
        try {
            setIsSavingNewTemplate(true);
            // Build a backend-compliant template payload (name, criteria, actions required)
            const name = newTemplateName.trim();
            let path = newTemplatePath.trim();
            // Map current selected tag ids to tag names for apply-tags action
            const selectedTagsByName = (availableTags || [])
              .filter(t => selectedTagIds.includes(t.tag_id || t.id))
              .map(t => (t.name || t.label || '').trim())
              .filter(Boolean);

            // Helper: parse "Key: Value" into { key, value }
            // No auto-normalization; leave the path as the user typed.

            const actions = {};
            if (path) actions.move_to_folder = path;
            if (selectedTagIds && selectedTagIds.length > 0) actions.add_tags = selectedTagIds;

            const payload = {
                name,
                description: '',
                criteria: {},
                actions,
                is_active: true,
                auto_apply: false
            };

            const created = await createSortingTemplate(payload);
            // Refresh templates to get full actions/criteria for the new template
            try {
                const refreshed = await getSortingTemplates();
                setAvailableTemplates(Array.isArray(refreshed) ? refreshed : (refreshed?.items || []));
            } catch (_e) {
                // fallback to append minimal object
            setAvailableTemplates(prev => [...prev, created]);
            }
            setSelectedTemplateId(created.id || created.template_id);
            setNewTemplateName('');
            setNewTemplatePath('');

            // Ensure local folders exist so users see them immediately
            if (path) {
                ensureLocalFoldersForPath(path, lastUploadKind);
            }
        } finally {
            setIsSavingNewTemplate(false);
        }
    };
    const [isModalUploading, setIsModalUploading] = React.useState(false);
    const [modalUploadProgress, setModalUploadProgress] = React.useState([]);

    const handleConfirmUploadOptions = async (renameOverrides = []) => {
        // Resolve target path from inline field or selected template actions
        const inlinePath = (newTemplatePath || '').trim();
        const selectedTemplate = (availableTemplates || []).find(t => (t.id || t.template_id) === selectedTemplateId) || {};
        const templateMovePath = selectedTemplate?.actions?.move_to_folder || selectedTemplate?.actions?.moveToFolder || '';
        const resolvedPath = inlinePath || templateMovePath || '';

        const options = {
            tagIds: selectedTagIds,
            templateId: selectedTemplateId,
            templatePath: resolvedPath,
            renameOverrides,
        };
        const files = pendingUploadFiles;
        // initialize per-file progress and keep modal open during upload
        const total = Array.from(files || []).length;
        setModalUploadProgress(Array.from({ length: total }, () => 0));
        setIsModalUploading(true);
        if (resolvedPath) {
            // Make sure folders are visible immediately in UI
            ensureLocalFoldersForPath(resolvedPath, lastUploadKind);
        }
        await handleFileUpload(files, options, (idx, p) => {
            setModalUploadProgress(prev => {
                const next = prev.slice();
                next[idx] = p;
                return next;
            });
        }, false);
        setIsModalUploading(false);
        closeUploadOptionsModal();
        // Return user to the section they started from
        const kind = (lastUploadKind || 'file').toLowerCase();
        const tabMap = { file: 'files', photo: 'photos', audio: 'audio', video: 'videos' };
        const target = tabMap[kind] || 'files';
        setActiveTab(target);
    };
    // Search functionality
    const handleSearch = async (query = searchQuery) => {
        if (!query.trim()) {
            setSearchResults([]);
            setShowSearchResults(false);
            return;
        }

        setIsSearching(true);
        setShowSearchResults(true);

        try {
            const searchTermLower = query.toLowerCase().trim();
            
            console.log('Searching with query:', searchTermLower);
            console.log('Available files for search:', uploadedFiles.length);
            
            // Search through uploaded files
            const fileResults = uploadedFiles.filter(file => {
                const nameMatch = file.original_name?.toLowerCase().includes(searchTermLower);
                const descMatch = file.description?.toLowerCase().includes(searchTermLower);
                const tagMatch = file.tags?.some(tag => tag.toLowerCase().includes(searchTermLower));
                const filenameMatch = file.filename?.toLowerCase().includes(searchTermLower); // Also check filename field
                
                return nameMatch || descMatch || tagMatch || filenameMatch;
            }).map(file => ({
                type: 'file',
                id: file.id || file.file_key || file.original_name,
                name: file.original_name || file.filename,
                description: file.description || 'Uploaded file',
                icon: getFileIconClass(file.original_name || file.filename),
                data: file
            }));
            
            console.log('File search results:', fileResults.length);

            // Search through navigation items/folders
            const folderResults = [
                { name: 'Documents', type: 'folder', icon: 'fas fa-folder text-blue-500' },
                { name: 'Images', type: 'folder', icon: 'fas fa-images text-green-500' },
                { name: 'Receipts', type: 'folder', icon: 'fas fa-receipt text-purple-500' },
                { name: 'Templates', type: 'folder', icon: 'fas fa-file-alt text-orange-500' },
                { name: 'Tags', type: 'folder', icon: 'fas fa-tags text-red-500' }
            ].filter(folder => 
                folder.name.toLowerCase().includes(searchTermLower)
            ).map(folder => ({
                type: 'folder',
                id: folder.name,
                name: folder.name,
                description: `Navigate to ${folder.name}`,
                icon: folder.icon,
                data: folder
            }));

            // Search through features/sections
            const featureResults = [
                { name: 'AI Assistant', tab: 'assistant', description: 'Chat with AI about your files' },
                { name: 'File Upload', tab: 'upload', description: 'Upload new files' },
                { name: 'AI Insights', tab: 'insights', description: 'View organization insights' },
                { name: 'File Manager', tab: 'files', description: 'Manage your files' }
            ].filter(feature => 
                feature.name.toLowerCase().includes(searchTermLower) ||
                feature.description.toLowerCase().includes(searchTermLower)
            ).map(feature => ({
                type: 'feature',
                id: feature.tab,
                name: feature.name,
                description: feature.description,
                icon: 'fas fa-cog text-gray-500',
                data: feature
            }));
            const allResults = [...fileResults, ...folderResults, ...featureResults];
            setSearchResults(allResults);

        } catch (error) {
            console.error('Search error:', error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const getFileIconClass = (filename) => {
        if (!filename) return 'fas fa-file text-gray-500';
        const ext = filename.toLowerCase().split('.').pop();
        switch (ext) {
            case 'pdf': return 'fas fa-file-pdf text-red-500';
            case 'jpg': case 'jpeg': case 'png': case 'gif': return 'fas fa-file-image text-blue-500';
            case 'doc': case 'docx': return 'fas fa-file-word text-blue-600';
            case 'xls': case 'xlsx': return 'fas fa-file-excel text-green-600';
            case 'csv': return 'fas fa-file-csv text-green-500';
            default: return 'fas fa-file text-gray-500';
        }
    };

    const handleSearchResultClick = (result) => {
        if (result.type === 'feature') {
            setActiveTab(result.data.tab);
        } else if (result.type === 'folder') {
            // Navigate to appropriate tab based on folder type
            switch (result.name) {
                case 'Documents':
                case 'Images':
                case 'Receipts':
                    setActiveTab('files');
                    break;
                case 'Templates':
                    setActiveTab('templates');
                    break;
                case 'Tags':
                    setActiveTab('tags');
                    break;
                default:
                    setActiveTab('files');
            }
        }
        // For files, we could implement a preview or navigate to files tab
        if (result.type === 'file') {
            setActiveTab('files');
        }
        
        setShowSearchResults(false);
        setSearchQuery('');
    };

    const clearSearch = () => {
        setSearchQuery('');
        setSearchResults([]);
        setShowSearchResults(false);
    };

    // Files folder management functions
    const handleCreateFileFolder = () => {
        setShowCreateFileFolder(true);
    };

    const handleFileFolderCreate = () => {
        if (newFileFolderName.trim()) {
            const newFolder = {
                id: Date.now().toString(),
                name: newFileFolderName.trim(),
                createdAt: new Date().toISOString(),
                fileCount: 0
            };
            setFileFolders(prev => [...prev, newFolder]);
            setNewFileFolderName('');
            setShowCreateFileFolder(false);
        }
    };

    const handleFileFolderCancel = () => {
        setNewFileFolderName('');
        setShowCreateFileFolder(false);
    };

    // Photo folder management functions
    const handleCreatePhotoFolder = () => {
        setShowCreatePhotoFolder(true);
    };

    const handlePhotoFolderCreate = () => {
        if (newPhotoFolderName.trim()) {
            const newFolder = {
                id: Date.now().toString(),
                name: newPhotoFolderName.trim(),
                created_at: new Date().toISOString(),
                photo_count: 0
            };
            setPhotoFolders(prev => [...prev, newFolder]);
            setNewPhotoFolderName('');
            setShowCreatePhotoFolder(false);
        }
    };

    const handlePhotoFolderCancel = () => {
        setNewPhotoFolderName('');
        setShowCreatePhotoFolder(false);
    };

    // Audio folder management functions
    const handleCreateAudioFolder = () => {
        setShowCreateAudioFolder(true);
    };

    const handleAudioFolderCreate = () => {
        if (newAudioFolderName.trim()) {
            const newFolder = {
                id: Date.now().toString(),
                name: newAudioFolderName.trim(),
                created_at: new Date().toISOString(),
                audio_count: 0
            };
            setAudioFolders(prev => [...prev, newFolder]);
            setNewAudioFolderName('');
            setShowCreateAudioFolder(false);
        }
    };

    const handleAudioFolderCancel = () => {
        setNewAudioFolderName('');
        setShowCreateAudioFolder(false);
    };

    // Video folder management functions
    const handleCreateVideoFolder = () => {
        setShowCreateVideoFolder(true);
    };

    const handleVideoFolderCreate = () => {
        if (newVideoFolderName.trim()) {
            const newFolder = {
                id: Date.now().toString(),
                name: newVideoFolderName.trim(),
                created_at: new Date().toISOString(),
                video_count: 0
            };
            setVideoFolders(prev => [...prev, newFolder]);
            setNewVideoFolderName('');
            setShowCreateVideoFolder(false);
        }
    };

    const handleVideoFolderCancel = () => {
        setNewVideoFolderName('');
        setShowCreateVideoFolder(false);
    };

    // Derive subfolders for a given path from file virtual paths
    const getSubfoldersFromFiles = (files, basePath = '', categoryPrefix = '') => {
        const normalizedBase = (basePath || '').replace(/^\/+|\/+$/g, '');
        const prefix = normalizedBase ? normalizedBase + '/' : '';
        const names = new Set();
        (files || []).forEach(f => {
            let vp = (f.virtual_path || '').replace(/^\/+|\/+$/g, '');
            if (categoryPrefix && vp.toLowerCase().startsWith(categoryPrefix.toLowerCase() + '/')) {
                vp = vp.slice(categoryPrefix.length + 1);
            }
            if (!vp.startsWith(prefix)) return;
            const rest = vp.slice(prefix.length);
            const seg = rest.split('/')[0];
            if (seg) names.add(seg);
        });
        return Array.from(names).map(name => ({ id: prefix + name, name, path: prefix + name }));
    };

    const getFilesForPath = (files, basePath = '', categoryPrefix = '') => {
        const base = (basePath || '').replace(/^\/+|\/+$/g, '');
        return (files || []).filter(f => {
            let vp = (f.virtual_path || '').replace(/^\/+|\/+$/g, '');
            if (categoryPrefix && vp.toLowerCase().startsWith(categoryPrefix.toLowerCase() + '/')) {
                vp = vp.slice(categoryPrefix.length + 1);
            }
            if (!base) return !vp; // in root, only items without folder
            return vp.startsWith(base + '/');
        });
    };

    // Count files contained in a folder path for a given list and optional category prefix
    const countFilesInFolder = (files, folderPath = '', categoryPrefix = '') => {
        const base = (folderPath || '').replace(/^\/+|\/+$/g, '');
        return (files || []).reduce((count, f) => {
            let vp = (f.virtual_path || '').replace(/^\/+|\/+$/g, '');
            if (categoryPrefix && vp.toLowerCase().startsWith(categoryPrefix.toLowerCase() + '/')) {
                vp = vp.slice(categoryPrefix.length + 1);
            }
            if (base ? vp.startsWith(base + '/') : !vp) {
                return count + 1;
            }
            return count;
        }, 0);
    };
    const renderPhotoGrid = (files, basePath = '', categoryPrefix = '') => {
        const filesToShow = getFilesForPath(files, basePath, categoryPrefix);
        return (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
                {filesToShow.map((photo, index) => (
                    <LazyPhotoThumb key={photo.file_key || index} photo={photo} onClick={() => handlePhotoClick(photo)} />
                ))}
            </div>
        );
    };
    // Ensure local folder lists include a given virtual path
    const ensureLocalFoldersForPath = (virtualPath, kind = 'file') => {
        const segments = (virtualPath || '').split('/').map(s => s.trim()).filter(Boolean);
        const addIfMissing = (setter, current, nameKey = 'name') => {
            let created = false;
            const existingNames = new Set((current || []).map(f => (f[nameKey] || '').toLowerCase()));
            segments.forEach(seg => {
                const key = seg.toLowerCase();
                if (!existingNames.has(key)) {
                    const folder = { id: `${Date.now()}-${key}`, [nameKey]: seg };
                    setter(prev => [...prev, folder]);
                    existingNames.add(key);
                    created = true;
                }
            });
            return created;
        };

        switch ((kind || 'file').toLowerCase()) {
            case 'photo':
                addIfMissing(setPhotoFolders, photoFolders, 'name');
                break;
            case 'audio':
                addIfMissing(setAudioFolders, audioFolders, 'name');
                break;
            case 'video':
                addIfMissing(setVideoFolders, videoFolders, 'name');
                break;
            default:
                addIfMissing(setFileFolders, fileFolders, 'name');
        }
    };

    // Fetch presigned URL for a photo
    const fetchPhotoUrl = async (file) => {
        try {
            const token = localStorage.getItem('access_token');
            const apiBase = buildUrl();
            
            const response = await fetch(`${apiBase}/files/${encodeURIComponent(file.file_key)}/download-url`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                return data.presigned_url;
            } else {
                console.error('Failed to fetch presigned URL:', response.status);
                return null;
            }
        } catch (error) {
            console.error('Error fetching presigned URL:', error);
            return null;
        }
    };

    const getPhotoThumbnailUrl = (file) => {
        // Prefer cached URL; fallback to an inline SVG placeholder so <img> always renders
        const placeholder = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
          '<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">\n' +
          '<rect width="200" height="200" fill="#e5e7eb"/>\n' +
          '<g fill="none" stroke="#9ca3af" stroke-width="4">\n' +
          '<rect x="30" y="40" width="140" height="120" rx="8"/>\n' +
          '<circle cx="80" cy="90" r="14"/>\n' +
          '<path d="M50 140 L95 100 L130 130 L160 110"/>\n' +
          '</g></svg>'
        )}`;
        return photoUrls[file.file_key] || placeholder;
    };

    // Lazy-loaded photo thumbnail with blur-up placeholder
    const LazyPhotoThumb = ({ photo, onClick }) => {
        const imgRef = React.useRef(null);
        const [src, setSrc] = React.useState(() => getPhotoThumbnailUrl(photo));
        const [loaded, setLoaded] = React.useState(false);

        React.useEffect(() => {
            let observer;
            const el = imgRef.current;
            if (!el) return;
            const load = async () => {
                try {
                    let url = photoUrls[photo.file_key];
                    if (!url) {
                        url = await fetchPhotoUrl(photo);
                        if (url) {
                            setPhotoUrls(prev => ({ ...prev, [photo.file_key]: url }));
                        }
                    }
                    if (url) setSrc(url);
                } catch (_) {
                    // ignore
                }
            };
            if ('IntersectionObserver' in window) {
                observer = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            observer.disconnect();
                            load();
                        }
                    });
                }, { rootMargin: '200px' });
                observer.observe(el);
            } else {
                // Fallback: immediately load
                load();
            }
            return () => observer && observer.disconnect();
        }, [photo.file_key]);

        return (
            <div
                className="group relative aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden hover:opacity-75 transition-opacity cursor-pointer"
                onClick={onClick}
            >
                <img
                    ref={imgRef}
                    src={src}
                    alt={photo.filename}
                    loading="lazy"
                    className={`w-full h-full object-cover transition will-change-transform ${loaded ? '' : 'blur-sm scale-105'}`}
                    onLoad={() => setLoaded(true)}
                    onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
                    }}
                />
                {/* Fallback icon */}
                <div className="w-full h-full hidden items-center justify-center bg-gray-200 dark:bg-gray-600">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                </div>

                {/* Photo overlay with info */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-end">
                    <div className="w-full p-2 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-xs font-medium truncate">{photo.filename}</p>
                        <p className="text-xs opacity-75">{new Date(photo.last_modified).toLocaleDateString()}</p>
                    </div>
                </div>

                {/* Action buttons */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex space-x-1">
                        <button
                            className="p-1 bg-black bg-opacity-50 text-white rounded hover:bg-opacity-75 transition-colors"
                            title="Download"
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePhotoDownload(photo);
                            }}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Fetch presigned URL for a video
    const fetchVideoUrl = async (file) => {
        try {
            const token = localStorage.getItem('access_token');
            const apiBase = buildUrl();
            const response = await fetch(`${apiBase}/files/${encodeURIComponent(file.file_key)}/download-url`, {
                method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                return data.presigned_url;
            }
        } catch (_) {}
        return null;
    };
    // Lazy video thumbnail generator using canvas capture of first frame
    const LazyVideoThumb = ({ video, onClick }) => {
        const containerRef = React.useRef(null);
        const [thumb, setThumb] = React.useState('');
        const [loaded, setLoaded] = React.useState(false);

        React.useEffect(() => {
            let observer;
            const el = containerRef.current;
            if (!el) return;
            const load = async () => {
                try {
                    const url = await fetchVideoUrl(video);
                    if (!url) return;
                    const vid = document.createElement('video');
                    vid.crossOrigin = 'anonymous';
                    vid.preload = 'auto';
                    vid.src = url;
                    vid.muted = true;
                    const onLoaded = async () => {
                        try {
                            const seekTo = Math.min(1, (vid.duration || 1) / 3);
                            const onSeeked = () => {
                                try {
                                    const w = vid.videoWidth || 320;
                                    const h = vid.videoHeight || 180;
                                    const canvas = document.createElement('canvas');
                                    canvas.width = w; canvas.height = h;
                                    const ctx = canvas.getContext('2d');
                                    ctx.drawImage(vid, 0, 0, w, h);
                                    const data = canvas.toDataURL('image/jpeg', 0.75);
                                    setThumb(data);
                                    setLoaded(true);
                                    vid.removeEventListener('seeked', onSeeked);
                                    vid.src = '';
                                } catch (_) {}
                            };
                            vid.addEventListener('seeked', onSeeked);
                            try { vid.currentTime = seekTo; } catch (_) { onSeeked(); }
                        } catch (_) {}
                    };
                    vid.addEventListener('loadedmetadata', onLoaded);
                } catch (_) {}
            };
            if ('IntersectionObserver' in window) {
                observer = new IntersectionObserver((entries) => {
                    entries.forEach(entry => { if (entry.isIntersecting) { observer.disconnect(); load(); } });
                }, { rootMargin: '200px' });
                observer.observe(el);
            } else {
                load();
            }
            return () => observer && observer.disconnect();
        }, [video.file_key]);

        return (
            <div
                ref={containerRef}
                className="group relative aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden hover:opacity-75 transition-opacity cursor-pointer"
                onClick={onClick}
            >
                {thumb ? (
                    <img src={thumb} alt={video.filename} className={`w-full h-full object-cover transition ${loaded ? '' : 'blur-sm'}`} />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-100 to-indigo-200 dark:from-indigo-900 dark:to-indigo-800">
                        <svg className="w-12 h-12 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                    </div>
                )}

                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all">
                    <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg className="w-6 h-6 text-indigo-600 ml-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                    </div>
                </div>
            </div>
        );
    };

    // Preload photo URLs when photoFiles change
    useEffect(() => {
        const loadPhotoUrls = async () => {
            const newUrls = {};
            
            // Load URLs for up to 20 photos at once to avoid overwhelming the server
            const photosToLoad = photoFiles.slice(0, 20);
            
            for (const photo of photosToLoad) {
                if (!photoUrls[photo.file_key]) {
                    const url = await fetchPhotoUrl(photo);
                    if (url) {
                        newUrls[photo.file_key] = url;
                    }
                }
            }
            
            if (Object.keys(newUrls).length > 0) {
                setPhotoUrls(prev => ({ ...prev, ...newUrls }));
            }
        };

        if (photoFiles.length > 0) {
            loadPhotoUrls();
        }
    }, [photoFiles]);

    // Photo modal handlers
    const handlePhotoClick = (photo) => {
        setSelectedPhoto(photo);
        setShowPhotoModal(true);
    };

    // File modal handlers
    const handleFileClick = (file) => {
        setSelectedFile(file);
        setShowFileModal(true);
    };

    // Audio modal handlers
    const handleAudioClick = (audio) => {
        setSelectedAudio(audio);
        setShowAudioModal(true);
    };

    // Video modal handlers
    const handleVideoClick = (video) => {
        setSelectedVideo(video);
        setShowVideoModal(true);
    };
    const handleFileDownload = async (file) => {
        try {
            const token = localStorage.getItem('access_token');
            const apiBase = buildUrl();
            
            const response = await fetch(`${apiBase}/files/${encodeURIComponent(file.file_key)}/download-url`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const downloadUrl = data.presigned_url;
                
                // Use the presigned URL for direct download
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = file.filename || 'file';
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                setAiMessages(prev => [...prev, {
                    type: 'ai',
                    message: `✅ Downloaded ${file.filename}`
                }]);
            } else {
                throw new Error('Unable to get download URL');
            }
        } catch (error) {
            console.error('Download error:', error);
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: `❌ Failed to download ${file.filename}: ${error.message}`
            }]);
        }
    };
    const handlePhotoDownload = async (photo) => {
        try {
            let downloadUrl = photoUrls[photo.file_key];
            
            // If we don't have a cached URL, fetch it
            if (!downloadUrl) {
                downloadUrl = await fetchPhotoUrl(photo);
                if (downloadUrl) {
                    setPhotoUrls(prev => ({ ...prev, [photo.file_key]: downloadUrl }));
                }
            }
            
            if (downloadUrl) {
                // Use the presigned URL for direct download
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = photo.filename || 'photo';
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                setAiMessages(prev => [...prev, {
                    type: 'ai',
                    message: `✅ Downloaded ${photo.filename}`
                }]);
            } else {
                throw new Error('Unable to get download URL');
            }
        } catch (error) {
            console.error('Download error:', error);
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: `❌ Failed to download ${photo.filename}: ${error.message}`
            }]);
        }
    };

    // Photo-specific upload handler
    const handlePhotoUpload = async (files) => {
        if (!files || files.length === 0) return;
        
        // Filter for image files only
        const imageFiles = Array.from(files).filter(file => {
            const ext = file.name.toLowerCase().split('.').pop();
            return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
        });
        
        if (imageFiles.length === 0) {
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: '⚠️ Please select image files only (JPG, PNG, GIF, WebP, BMP, SVG).'
            }]);
            return;
        }
        
        // Use the existing upload handler for photos
        await handleFileUpload(imageFiles);
        
        // Show specific message for photo uploads
        if (imageFiles.length !== files.length) {
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: `📸 Uploaded ${imageFiles.length} photo(s). ${files.length - imageFiles.length} non-image file(s) were skipped.`
            }]);
        }
    };

    // Photo drag and drop handlers
    const handlePhotoDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setPhotoDragActive(false);
        
        const files = e.dataTransfer.files;
        openUploadOptionsModal(files, 'photo');
    };
    
    const handlePhotoDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setPhotoDragActive(true);
    };
    
    const handlePhotoDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setPhotoDragActive(false);
    };

    // Audio-specific upload handler
    const handleAudioUpload = async (files) => {
        if (!files || files.length === 0) return;
        
        // Filter for audio files only
        const audioFilesList = Array.from(files).filter(file => {
            const ext = file.name.toLowerCase().split('.').pop();
            return ['mp3', 'wav', 'flac', 'm4a', 'aac', 'ogg', 'wma'].includes(ext);
        });
        
        if (audioFilesList.length === 0) {
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: '⚠️ Please select audio files only (MP3, WAV, FLAC, M4A, AAC, OGG, WMA).'
            }]);
            return;
        }
        
        // Use the existing upload handler for audio
        await handleFileUpload(audioFilesList);
        
        // Show specific message for audio uploads
        if (audioFilesList.length !== files.length) {
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: `🎵 Uploaded ${audioFilesList.length} audio file(s). ${files.length - audioFilesList.length} non-audio file(s) were skipped.`
            }]);
        }
    };

    // Audio drag and drop handlers
    const handleAudioDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setAudioDragActive(false);
        
        const files = e.dataTransfer.files;
        openUploadOptionsModal(files, 'audio');
    };
    
    const handleAudioDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setAudioDragActive(true);
    };
    
    const handleAudioDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setAudioDragActive(false);
    };

    // Video-specific upload handler
    const handleVideoUpload = async (files) => {
        if (!files || files.length === 0) return;
        
        // Filter for video files only
        const videoFilesList = Array.from(files).filter(file => {
            const ext = file.name.toLowerCase().split('.').pop();
            return ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm', 'm4v'].includes(ext);
        });
        
        if (videoFilesList.length === 0) {
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: '⚠️ Please select video files only (MP4, MOV, AVI, MKV, WMV, FLV, WebM, M4V).'
            }]);
            return;
        }
        
        // Use the existing upload handler for video
        await handleFileUpload(videoFilesList);
        
        // Show specific message for video uploads
        if (videoFilesList.length !== files.length) {
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: `🎥 Uploaded ${videoFilesList.length} video file(s). ${files.length - videoFilesList.length} non-video file(s) were skipped.`
            }]);
        }
    };

    // Video drag and drop handlers
    const handleVideoDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setVideoDragActive(false);
        
        const files = e.dataTransfer.files;
        openUploadOptionsModal(files, 'video');
    };
    
    const handleVideoDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setVideoDragActive(true);
    };
    
    const handleVideoDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setVideoDragActive(false);
    };
    // Template creation functions
    const handleCreateTemplate = () => {
        setShowCreateTemplate(true);
        const suggestions = generateTemplatePathSuggestions();
        setNewTemplate({
            name: '',
            directoryPath: '',
            description: '',
            fileTypes: ['all'],
            variables: [],
            enabled: true,
            styleHints: {
                nestingDepth: 0,
                usesYearSegment: false,
                usesCategorySegment: false,
                variablesCount: 0
            },
            suggestions
        });
    };

    const parseTemplateVariables = (directoryPath) => {
        const variableRegex = /<([^>]+)>/g;
        const variables = [];
        let match;
        
        while ((match = variableRegex.exec(directoryPath)) !== null) {
            const variableName = match[1];
            if (!variables.find(v => v.name === variableName)) {
                variables.push({
                    name: variableName,
                    source: 'tag', // default to tag, can be changed to 'ai' or 'manual'
                    tagName: variableName.toLowerCase().replace(/\s+/g, '_'),
                    required: true,
                    aiPrompt: `Extract ${variableName} from the file content or metadata`
                });
            }
        }
        
        return variables;
    };

    const handleTemplatePathChange = (path) => {
        setNewTemplate(prev => {
            const variables = parseTemplateVariables(path);
            const segments = path.split('/').filter(Boolean);
            const styleHints = {
                nestingDepth: segments.length,
                usesYearSegment: segments.some(s => /\b20\d{2}\b|\[year\]|<year>/i.test(s)),
                usesCategorySegment: segments.some(s => /(receipts|invoices|contracts|photos|videos|audio|documents|\[category\]|<category>)/i.test(s)),
                variablesCount: variables.length
            };
            return {
                ...prev,
                directoryPath: path,
                variables: variables,
                styleHints
            };
        });
    };

    const handleSaveTemplate = async () => {
        if (!newTemplate.name.trim() || !newTemplate.directoryPath.trim()) {
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: '⚠️ Please provide both a template name and directory path.'
            }]);
            return;
        }

        const template = {
            ...newTemplate,
            id: Date.now().toString(),
            createdAt: new Date().toISOString(),
            appliedCount: 0
        };

        setCustomTemplates(prev => [...prev, template]);
        setShowCreateTemplate(false);
        
        // Record template style to AI learning
        try {
            await sendSuggestionFeedback('template_style', 'create', {
                name: template.name,
                directoryPath: template.directoryPath,
                variables: template.variables,
                fileTypes: template.fileTypes,
                description: template.description || ''
            });
        } catch (e) {
            // non-fatal
            console.warn('Failed to store template style feedback', e);
        }

        setAiMessages(prev => [...prev, {
            type: 'ai',
            message: `✅ Created template "${template.name}" with path "${template.directoryPath}". Variables found: ${template.variables.map(v => v.name).join(', ') || 'none'}.`
        }]);
    };
    const handleCancelTemplate = () => {
        setShowCreateTemplate(false);
        setNewTemplate({
            name: '',
            directoryPath: '',
            description: '',
            fileTypes: ['all'],
            variables: [],
            enabled: true,
            styleHints: {
                nestingDepth: 0,
                usesYearSegment: false,
                usesCategorySegment: false,
                variablesCount: 0
            }
        });
    };

    const applyTemplateToFile = async (file, template) => {
        try {
            let resolvedPath = template.directoryPath;
            const missingTags = [];
            
            // Check each variable and try to resolve it
            for (const variable of template.variables) {
                const placeholder = `<${variable.name}>`;
                let value = null;
                
                if (variable.source === 'tag') {
                    // Look for the tag in the file's tags
                    value = file.tags?.find(tag => 
                        tag.toLowerCase().includes(variable.tagName.toLowerCase())
                    );
                    
                    if (!value) {
                        missingTags.push(variable);
                        continue;
                    }
                } else if (variable.source === 'ai') {
                    // Use AI to extract the value (mock implementation)
                    value = await mockAIExtractVariable(file, variable);
                } else if (variable.source === 'manual') {
                    // Manual entry would be handled in a separate modal
                    missingTags.push(variable);
                    continue;
                }
                
                if (value) {
                    resolvedPath = resolvedPath.replace(placeholder, value);
                }
            }
            
            if (missingTags.length > 0) {
                // Show modal to handle missing variables
                setPendingTemplateApplication({
                    file,
                    template,
                    resolvedPath,
                    missingVariables: missingTags
                });
                setShowTemplateVariableModal(true);
                return null;
            }
            
            return resolvedPath;
        } catch (error) {
            console.error('Error applying template:', error);
            return null;
        }
    };
    // Generate suggested folder structures for templates
    const generateTemplatePathSuggestions = () => {
        const suggestions = new Set();

        // Heuristics based on current files
        const total = uploadedFiles.length;
        const lower = (s) => (s || '').toLowerCase();
        const hasReceipts = uploadedFiles.some(f => lower(f.filename).includes('receipt'));
        const hasInvoices = uploadedFiles.some(f => lower(f.filename).includes('invoice'));
        const imageCount = uploadedFiles.filter(f => {
            const ext = lower(f.filename).split('.').pop();
            return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
        }).length;

        // Derive from existing templates
        (customTemplates || []).forEach(t => {
            if (t.directoryPath) {
                // Normalize to include trailing slash
                const path = t.directoryPath.endsWith('/') ? t.directoryPath : `${t.directoryPath}/`;
                suggestions.add(path);
            }
        });

        // Common helpful patterns
        suggestions.add('Documents/<Year>/<Category>/');
        suggestions.add('Documents/<Year>/receipts/');
        suggestions.add('Documents/<Year>/invoices/');
        suggestions.add('Projects/<Project>/<Year>/');
        suggestions.add('Clients/<Client>/<Year>/');

        if (hasReceipts || hasInvoices || total > 0) {
            suggestions.add('Documents/<Year>/<Vendor>/');
            suggestions.add('Documents/<Year>/<Vendor>/<Month>/');
        }
        if (imageCount > 3) {
            suggestions.add('Photos/<Person>/<Age>/');
            suggestions.add('Photos/<Year>/<Event>/');
            suggestions.add('Media/Photos/<Year>/<Month>/');
        }

        // Return a limited, stable list
        return Array.from(suggestions).slice(0, 8);
    };

    const mockAIExtractVariable = async (file, variable) => {
        // Mock AI extraction - in real implementation, this would call the AI service
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Mock extraction based on file name and variable type
        const filename = file.filename?.toLowerCase() || '';
        
        if (variable.name.toLowerCase().includes('age')) {
            // Extract age from filename patterns like "5_years", "baby", "toddler"
            if (filename.includes('baby')) return 'Baby';
            if (filename.includes('toddler')) return 'Toddler';
            if (filename.includes('child')) return 'Child';
            const ageMatch = filename.match(/(\d+)[-_\s]*(year|yr|month|mo)/i);
            if (ageMatch) return `${ageMatch[1]} ${ageMatch[2]}s`;
            return 'Unknown Age';
        }
        
        if (variable.name.toLowerCase().includes('child') || variable.name.toLowerCase().includes('person')) {
            // Extract names from common patterns
            const namePatterns = ['john', 'jane', 'alex', 'emma', 'noah', 'olivia'];
            for (const name of namePatterns) {
                if (filename.includes(name)) {
                    return name.charAt(0).toUpperCase() + name.slice(1);
                }
            }
            return 'Unknown';
        }
        
        return `AI_${variable.name}`;
    };
    const handleResolveTemplateVariables = async (resolvedVariables) => {
        if (!pendingTemplateApplication) return;
        
        let finalPath = pendingTemplateApplication.resolvedPath;
        const createdTags = [];
        const createdFolders = [];
        
        // Apply the resolved variables
        for (const [variableName, value] of Object.entries(resolvedVariables)) {
            finalPath = finalPath.replace(`<${variableName}>`, value);
            
            // Check if this variable was supposed to come from a tag
            const variable = pendingTemplateApplication.template.variables.find(v => v.name === variableName);
            if (variable && variable.source === 'tag') {
                // AI auto-create the tag if it doesn't exist
                const tagExists = await checkIfTagExists(variable.tagName);
                if (!tagExists && value.trim()) {
                    await createTagFromAI(variable.tagName, value, variableName);
                    createdTags.push({ name: variable.tagName, value: value });
                }
            }
        }
        
        // AI auto-create folders in the path if they don't exist
        const pathSegments = finalPath.split('/').filter(segment => segment.trim());
        let currentPath = '';
        for (const segment of pathSegments) {
            if (segment && !segment.includes('.')) { // Skip file names
                currentPath += (currentPath ? '/' : '') + segment;
                const folderExists = await checkIfFolderExists(currentPath);
                if (!folderExists) {
                    await createFolderFromAI(currentPath, segment);
                    createdFolders.push(segment);
                }
            }
        }
        
        // Here you would actually move/organize the file to the resolved path
        console.log(`Organizing file ${pendingTemplateApplication.file.filename} to: ${finalPath}`);
        
        // Build success message with created resources
        let message = `📁 Organized "${pendingTemplateApplication.file.filename}" to "${finalPath}"`;
        if (createdTags.length > 0) {
            message += `\n🏷️ AI created ${createdTags.length} new tag(s): ${createdTags.map(t => t.name).join(', ')}`;
        }
        if (createdFolders.length > 0) {
            message += `\n📂 AI created ${createdFolders.length} new folder(s): ${createdFolders.join(', ')}`;
        }
        
        setAiMessages(prev => [...prev, {
            type: 'ai',
            message: message
        }]);
        
        // Update template usage count
        setCustomTemplates(prev => prev.map(template => 
            template.id === pendingTemplateApplication.template.id 
                ? { ...template, appliedCount: (template.appliedCount || 0) + 1 }
                : template
        ));
        
        setShowTemplateVariableModal(false);
        setPendingTemplateApplication(null);
    };

    // AI helper functions for auto-creation
    const checkIfTagExists = async (tagName) => {
        // In a real implementation, this would check the backend/database
        // For now, simulate checking against existing tags (would come from TagManager state)
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Mock: assume tag doesn't exist if it's not in common tags
        const commonTags = ['expense', 'receipt', 'invoice', 'contract', 'legal', 'personal', 'work'];
        return commonTags.includes(tagName.toLowerCase());
    };

    const checkIfFolderExists = async (folderPath) => {
        // In a real implementation, this would check the S3/file system
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Mock: assume folders don't exist for demo purposes
        // In reality, this would query the folder structure
        return false;
    };

    const createTagFromAI = async (tagName, value, context) => {
        try {
            // Simulate AI-powered tag creation with intelligent defaults
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // AI determines appropriate color and icon based on context
            const aiTagConfig = getAITagConfiguration(tagName, value, context);
            
            // In a real implementation, this would call the createTag API
            console.log(`AI creating tag: ${tagName}`, aiTagConfig);
            
            // For demo, we'll just log the creation
            // await createTag({ 
            //     name: tagName, 
            //     description: aiTagConfig.description,
            //     color: aiTagConfig.color,
            //     icon: aiTagConfig.icon 
            // });
            
            return true;
        } catch (error) {
            console.error('Failed to create tag via AI:', error);
            return false;
        }
    };
    const createFolderFromAI = async (folderPath, folderName) => {
        try {
            // Simulate AI-powered folder creation
            await new Promise(resolve => setTimeout(resolve, 200));
            
            // In a real implementation, this would create the folder structure
            console.log(`AI creating folder: ${folderPath}`);
            
            // Add to appropriate folder state based on context
            const folderType = determineFolderType(folderPath);
            const newFolder = {
                id: Date.now().toString() + Math.random(),
                name: folderName,
                path: folderPath,
                createdAt: new Date().toISOString(),
                fileCount: 0,
                createdByAI: true
            };
            
            // Add to the appropriate folder list
            switch (folderType) {
                case 'photos':
                    setPhotoFolders(prev => [...prev, newFolder]);
                    break;
                case 'audio':
                    setAudioFolders(prev => [...prev, newFolder]);
                    break;
                case 'videos':
                    setVideoFolders(prev => [...prev, newFolder]);
                    break;
                default:
                    setFileFolders(prev => [...prev, newFolder]);
                    break;
            }
            
            return true;
        } catch (error) {
            console.error('Failed to create folder via AI:', error);
            return false;
        }
    };

    const getAITagConfiguration = (tagName, value, context) => {
        // AI-powered intelligent tag configuration
        const name = tagName.toLowerCase();
        
        // Smart color assignment based on tag name/context
        let color = '#3B82F6'; // default blue
        let icon = 'tag'; // default icon
        let description = `Auto-created tag for ${context}: ${value}`;
        
        if (name.includes('expense') || name.includes('cost') || name.includes('price')) {
            color = '#EF4444'; // red for expenses
            icon = 'receipt';
            description = `Financial expense tag for tracking ${value}`;
        } else if (name.includes('age') || name.includes('year') || name.includes('time')) {
            color = '#10B981'; // green for time-based
            icon = 'calendar-alt';
            description = `Time-based organization tag: ${value}`;
        } else if (name.includes('person') || name.includes('child') || name.includes('name')) {
            color = '#8B5CF6'; // purple for people
            icon = 'user';
            description = `Person identification tag: ${value}`;
        } else if (name.includes('location') || name.includes('place') || name.includes('site')) {
            color = '#F59E0B'; // amber for locations
            icon = 'map-marker-alt';
            description = `Location-based tag: ${value}`;
        } else if (name.includes('project') || name.includes('work') || name.includes('job')) {
            color = '#06B6D4'; // cyan for work/projects
            icon = 'briefcase';
            description = `Work/project organization tag: ${value}`;
        } else if (name.includes('category') || name.includes('type') || name.includes('kind')) {
            color = '#84CC16'; // lime for categories
            icon = 'folder-open';
            description = `Category classification tag: ${value}`;
        }
        
        return { color, icon, description };
    };

    const determineFolderType = (folderPath) => {
        const pathLower = folderPath.toLowerCase();
        
        if (pathLower.includes('photo') || pathLower.includes('image') || pathLower.includes('picture')) {
            return 'photos';
        } else if (pathLower.includes('audio') || pathLower.includes('music') || pathLower.includes('sound')) {
            return 'audio';
        } else if (pathLower.includes('video') || pathLower.includes('movie') || pathLower.includes('clip')) {
            return 'videos';
        }
        
        return 'files'; // default to files
    };
    // Simulate advanced OCR and AI content extraction
    const simulateContentExtraction = async (file) => {
        // Simulate processing time for OCR + AI analysis
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const filename = file.filename?.toLowerCase() || '';
        const analysis = {
            originalFilename: file.filename,
            documentType: 'unknown',
            confidence: 0.0,
            extractedDate: null,
            extractedData: {},
            ocrText: '', // Simulated OCR output
        };

        // Mock OCR and content analysis based on filename patterns (in real app, this would be actual OCR + AI)
        if (filename.includes('receipt') || filename.includes('walmart') || filename.includes('target') || filename.includes('grocery')) {
            analysis.documentType = 'receipt';
            analysis.confidence = 0.85 + Math.random() * 0.15; // 85-100% confidence
            
            // Mock extracted receipt data
            const mockMerchants = ['Walmart', 'Target', 'Starbucks', 'Home Depot', 'Costco', 'Amazon', 'CVS Pharmacy', 'Whole Foods'];
            const mockAmounts = ['12.45', '67.89', '156.23', '45.67', '89.12', '234.56', '78.90', '123.45'];
            const mockDates = [
                new Date('2024-12-15'),
                new Date('2024-11-28'),
                new Date('2024-10-10'),
                new Date('2023-12-20'),
                new Date('2023-09-15'),
                new Date('2024-01-05')
            ];
            
            analysis.extractedData = {
                merchant: mockMerchants[Math.floor(Math.random() * mockMerchants.length)],
                amount: mockAmounts[Math.floor(Math.random() * mockAmounts.length)],
                tax: (Math.random() * 10 + 1).toFixed(2),
                paymentMethod: Math.random() > 0.5 ? 'Credit Card' : 'Cash'
            };
            analysis.extractedDate = mockDates[Math.floor(Math.random() * mockDates.length)];
            analysis.ocrText = `${analysis.extractedData.merchant}\nSubtotal: $${analysis.extractedData.amount}\nTax: $${analysis.extractedData.tax}\nTotal: $${(parseFloat(analysis.extractedData.amount) + parseFloat(analysis.extractedData.tax)).toFixed(2)}\nDate: ${analysis.extractedDate.toLocaleDateString()}`;
            
        } else if (filename.includes('invoice') || filename.includes('bill') || filename.includes('statement')) {
            analysis.documentType = 'invoice';
            analysis.confidence = 0.88 + Math.random() * 0.12;
            
            // Mock extracted invoice data
            const mockClients = ['Acme Corp', 'Tech Solutions', 'Marketing Plus', 'Design Studio', 'Consulting LLC', 'Business Partners'];
            const mockInvoiceNumbers = ['INV-2024-001', 'INV-2024-067', 'INV-2023-158', 'BILL-789', 'ST-2024-045'];
            const mockAmounts = ['1250.00', '2400.50', '875.25', '3200.00', '567.89', '1899.99'];
            
            analysis.extractedData = {
                client: mockClients[Math.floor(Math.random() * mockClients.length)],
                invoiceNumber: mockInvoiceNumbers[Math.floor(Math.random() * mockInvoiceNumbers.length)],
                amount: mockAmounts[Math.floor(Math.random() * mockAmounts.length)],
                dueDate: new Date(Date.now() + (Math.random() * 30 + 15) * 24 * 60 * 60 * 1000) // 15-45 days from now
            };
            analysis.extractedDate = new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000); // Within last year
            analysis.ocrText = `INVOICE\n${analysis.extractedData.invoiceNumber}\nBill To: ${analysis.extractedData.client}\nAmount Due: $${analysis.extractedData.amount}\nDate: ${analysis.extractedDate.toLocaleDateString()}`;
            
        } else if (filename.includes('contract') || filename.includes('agreement') || filename.includes('legal')) {
            analysis.documentType = 'contract';
            analysis.confidence = 0.80 + Math.random() * 0.15;
            analysis.extractedDate = new Date(Date.now() - Math.random() * 180 * 24 * 60 * 60 * 1000); // Within last 6 months
            analysis.extractedData = {
                documentType: 'Legal Contract',
                parties: ['Your Company', 'Partner LLC'],
                effectiveDate: analysis.extractedDate.toLocaleDateString()
            };
            analysis.ocrText = `CONTRACT AGREEMENT\nEffective: ${analysis.extractedDate.toLocaleDateString()}\nParties: ${analysis.extractedData.parties.join(', ')}`;
            
        } else if (filename.includes('tax') || filename.includes('w2') || filename.includes('1099')) {
            analysis.documentType = 'tax_document';
            analysis.confidence = 0.92 + Math.random() * 0.08;
            analysis.extractedDate = new Date('2024-01-31'); // Tax documents typically from January
            analysis.extractedData = {
                documentType: filename.includes('w2') ? 'W-2' : '1099',
                taxYear: '2024',
                employer: 'Your Employer Inc'
            };
            analysis.ocrText = `${analysis.extractedData.documentType} Tax Document\nTax Year: ${analysis.extractedData.taxYear}\nEmployer: ${analysis.extractedData.employer}`;
        } else {
            // For unrecognized files, try to determine type by extension
            const ext = filename.split('.').pop();
            if (['jpg', 'jpeg', 'png'].includes(ext)) {
                analysis.documentType = 'image';
                analysis.confidence = 0.60;
                analysis.extractedData = { fileType: 'Image', possibleContent: 'Photo or scanned document' };
            } else if (ext === 'pdf') {
                analysis.documentType = 'document';
                analysis.confidence = 0.65;
                analysis.extractedData = { fileType: 'PDF Document', requiresOCR: true };
            }
            analysis.extractedDate = new Date(); // Default to current date
        }

        return analysis;
    };
    // AI Organization functions - Enhanced Content-Aware Analysis
    const mockAIAnalysis = async (files, analysisType) => {
        // Simulate AI analysis delay (OCR + content parsing)
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Mock AI suggestions based on CONTENT analysis (simulating OCR + AI parsing)
        const suggestions = {
            folders: [],
            renames: [],
            tags: [],
            insights: [],
            contentAnalysis: [] // New: show what AI "extracted" from content
        };

        if (files && files.length > 0) {
            // Simulate content extraction for each file
            const analyzedFiles = await Promise.all(files.map(async (file) => {
                return await simulateContentExtraction(file);
            }));

            // Group files by detected content type and year
            const receiptsByYear = {};
            const invoicesByYear = {};
            const generalDocsByYear = {};

            analyzedFiles.forEach(analysis => {
                const year = analysis.extractedDate?.getFullYear() || new Date().getFullYear();
                
                if (analysis.documentType === 'receipt') {
                    if (!receiptsByYear[year]) receiptsByYear[year] = [];
                    receiptsByYear[year].push(analysis);
                } else if (analysis.documentType === 'invoice') {
                    if (!invoicesByYear[year]) invoicesByYear[year] = [];
                    invoicesByYear[year].push(analysis);
                } else {
                    if (!generalDocsByYear[year]) generalDocsByYear[year] = [];
                    generalDocsByYear[year].push(analysis);
                }
            });

            // Generate folder suggestions based on content analysis
            Object.keys(receiptsByYear).forEach(year => {
                const receipts = receiptsByYear[year];
                suggestions.folders.push({
                    name: `Documents/${year}/receipts`,
                    reason: `AI detected ${receipts.length} receipt(s) from ${year} with extractable transaction data`,
                    files: receipts.map(r => r.originalFilename),
                    contentBased: true,
                    extractedData: receipts.length
                });
            });

            Object.keys(invoicesByYear).forEach(year => {
                const invoices = invoicesByYear[year];
                suggestions.folders.push({
                    name: `Documents/${year}/invoices`,
                    reason: `AI detected ${invoices.length} invoice(s) from ${year} with billing information`,
                    files: invoices.map(i => i.originalFilename),
                    contentBased: true,
                    extractedData: invoices.length
                });
            });

            // Generate intelligent rename suggestions based on extracted content
            analyzedFiles.forEach(analysis => {
                if (analysis.documentType === 'receipt' && analysis.extractedData.merchant && analysis.extractedData.amount) {
                    const year = analysis.extractedDate?.getFullYear() || new Date().getFullYear();
                    const merchant = analysis.extractedData.merchant.replace(/[^a-zA-Z0-9]/g, '');
                    const dateStr = analysis.extractedDate ? 
                        analysis.extractedDate.toISOString().split('T')[0] : // YYYY-MM-DD format
                        new Date().toISOString().split('T')[0];
                    const ext = analysis.originalFilename.split('.').pop();
                    
                    suggestions.renames.push({
                        original: analysis.originalFilename,
                        suggested: `${dateStr}_${merchant}_$${analysis.extractedData.amount}.${ext}`,
                        reason: `AI extracted: Date "${analysis.extractedDate?.toLocaleDateString()}", Merchant "${analysis.extractedData.merchant}", Amount "${analysis.extractedData.amount}"`,
                        targetFolder: `Documents/${year}/receipts`,
                        contentExtracted: true,
                        extractedData: analysis.extractedData
                    });
                } else if (analysis.documentType === 'invoice' && analysis.extractedData.client && analysis.extractedData.amount) {
                    const year = analysis.extractedDate?.getFullYear() || new Date().getFullYear();
                    const client = analysis.extractedData.client.replace(/[^a-zA-Z0-9]/g, '');
                    const invoiceNum = analysis.extractedData.invoiceNumber || 'INV';
                    const dateStr = analysis.extractedDate ? 
                        analysis.extractedDate.toISOString().split('T')[0] : // YYYY-MM-DD format
                        new Date().toISOString().split('T')[0];
                    const ext = analysis.originalFilename.split('.').pop();
                    
                    suggestions.renames.push({
                        original: analysis.originalFilename,
                        suggested: `${dateStr}_${client}_${invoiceNum}_$${analysis.extractedData.amount}.${ext}`,
                        reason: `AI extracted: Date "${analysis.extractedDate?.toLocaleDateString()}", Client "${analysis.extractedData.client}", Invoice #"${analysis.extractedData.invoiceNumber}", Amount "${analysis.extractedData.amount}"`,
                        targetFolder: `Documents/${year}/invoices`,
                        contentExtracted: true,
                        extractedData: analysis.extractedData
                    });
                }
            });

            // Add content analysis insights
            suggestions.contentAnalysis = analyzedFiles.filter(a => a.confidence > 0.7);
            
            // Enhanced insights based on content analysis
            const totalContentExtracted = analyzedFiles.filter(a => a.documentType !== 'unknown').length;
            if (totalContentExtracted > 0) {
                suggestions.insights.push(`AI successfully analyzed ${totalContentExtracted}/${files.length} files using OCR and content parsing`);
                suggestions.insights.push(`Extracted transaction data enables automatic organization by merchant, amount, and date`);
                suggestions.insights.push(`Year-based folder structure will save 80% of manual organization time during tax season`);
            }

            // Suggest tags
            files.forEach(file => {
                const filename = file.filename?.toLowerCase() || '';
                const suggestedTags = [];
                
                if (filename.includes('receipt') || filename.includes('invoice')) {
                    suggestedTags.push('Receipt', 'Business');
                }
                if (filename.includes('tax') || filename.includes('w2') || filename.includes('1099')) {
                    suggestedTags.push('Tax', 'Important');
                }
                if (filename.includes('contract') || filename.includes('agreement')) {
                    suggestedTags.push('Legal', 'Important');
                }
                if (['jpg', 'png', 'jpeg'].includes(filename.split('.').pop())) {
                    suggestedTags.push('Image');
                }

                if (suggestedTags.length > 0) {
                    suggestions.tags.push({
                        file: file.filename,
                        suggestedTags,
                        reason: "Based on file content and naming patterns"
                    });
                }
            });
        }

        // Add type-specific suggestions
        if (analysisType === 'batch') {
            suggestions.insights.push("Consider organizing files by year and category for better long-term management");
            suggestions.insights.push("Files with similar naming patterns could be grouped together");
        }

        return suggestions;
    };

    const handleUploadAIAnalysis = async (uploadedFiles) => {
        if (!uploadedFiles || uploadedFiles.length === 0) return;
        
        setIsAnalyzingFiles(true);
        try {
            // Pull latest files (includes AI rename previews and virtual_path) before building suggestions
            const { getUserFiles } = await import('../../services/api');
            let fresh = [];
            try {
                const list = await getUserFiles();
                fresh = Array.isArray(list) ? list : [];
            } catch (_) {}
            const mapByKey = new Map(fresh.map(f => [f.file_key || f.filename, f]));
            const enriched = uploadedFiles.map(f => {
                const key = f.file_key || f.filename;
                const latest = mapByKey.get(key);
                return {
                    ...f,
                    filename: latest?.ai_suggested_name || latest?.filename || f.filename,
                    virtual_path: latest?.virtual_path || f.virtual_path || '',
                };
            });

            const suggestions = await mockAIAnalysis(enriched, 'upload');
            
            if (suggestions.folders.length > 0 || suggestions.renames.length > 0 || suggestions.tags.length > 0) {
                setAISuggestions({
                    ...suggestions,
                    uploadedFiles: enriched,
                    type: 'upload'
                });
                setAISuggestionType('upload');
                setShowAISuggestionsModal(true);
            }
        } catch (error) {
            console.error('AI analysis error:', error);
        } finally {
            setIsAnalyzingFiles(false);
        }
    };
    const handleBatchAIOrganization = async () => {
        if (uploadedFiles.length === 0) return;
        
        setIsAnalyzingFiles(true);
        try {
            const suggestions = await mockAIAnalysis(uploadedFiles, 'batch');
            
            setAISuggestions({
                ...suggestions,
                allFiles: uploadedFiles,
                type: 'batch'
            });
            setAISuggestionType('batch');
            setShowAISuggestionsModal(true);
        } catch (error) {
            console.error('AI batch analysis error:', error);
        } finally {
            setIsAnalyzingFiles(false);
        }
    };

    const handleTagsAIOrganization = async () => {
        setIsAnalyzingFiles(true);
        setAiSuggestionType('tags');
        
        try {
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Enhanced AI analysis for smart tag suggestions with auto-creation
            const mockTagSuggestions = await generateSmartTagSuggestions(uploadedFiles);
            
            const suggestions = {
                tags: mockTagSuggestions,
                applicableFiles: uploadedFiles.slice(0, 8).map(f => f.filename),
                insights: [
                    `AI analyzed ${uploadedFiles.length} files and found ${mockTagSuggestions.length} tag opportunities`,
                    'Suggested tags will be auto-created and applied to relevant files',
                    'Smart tagging can organize your entire library with intelligent categorization'
                ],
                autoCreateTags: true // Flag to indicate AI will auto-create missing tags
            };
            
            setAISuggestions({ ...suggestions, uploadedFiles });
            setShowAISuggestions(true);
        } catch (error) {
            console.error('AI tags analysis error:', error);
        } finally {
            setIsAnalyzingFiles(false);
        }
    };
    const generateSmartTagSuggestions = async (files) => {
        // Simulate advanced AI analysis of files to suggest intelligent tags
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const suggestions = [];
        const fileTypes = {};
        const contentAnalysis = {};
        
        // Analyze file patterns and content
        files.forEach(file => {
            const filename = file.filename?.toLowerCase() || '';
            const ext = filename.split('.').pop();
            
            // File type analysis
            if (['pdf', 'doc', 'docx'].includes(ext)) {
                fileTypes.documents = (fileTypes.documents || 0) + 1;
            }
            if (['jpg', 'png', 'gif'].includes(ext)) {
                fileTypes.images = (fileTypes.images || 0) + 1;
            }
            
            // Content pattern analysis
            if (filename.includes('receipt') || filename.includes('invoice')) {
                contentAnalysis.financial = (contentAnalysis.financial || 0) + 1;
            }
            if (filename.includes('contract') || filename.includes('agreement')) {
                contentAnalysis.legal = (contentAnalysis.legal || 0) + 1;
            }
            if (filename.includes('2024') || filename.includes('2023')) {
                contentAnalysis.recent = (contentAnalysis.recent || 0) + 1;
            }
        });
        
        // Generate smart tag suggestions based on analysis
        if (contentAnalysis.financial >= 2) {
            suggestions.push({
                name: 'expense',
                description: 'Financial expenses and receipts',
                reason: `AI detected ${contentAnalysis.financial} financial documents`,
                color: '#EF4444',
                icon: 'receipt',
                autoCreate: true,
                confidence: 'high'
            });
        }
        
        if (contentAnalysis.legal >= 1) {
            suggestions.push({
                name: 'legal',
                description: 'Legal documents and contracts',
                reason: `AI found ${contentAnalysis.legal} legal document(s)`,
                color: '#8B5CF6',
                icon: 'legal',
                autoCreate: true,
                confidence: 'high'
            });
        }
        
        if (contentAnalysis.recent >= 3) {
            suggestions.push({
                name: '2024',
                description: 'Current year documents',
                reason: `AI identified ${contentAnalysis.recent} recent documents`,
                color: '#10B981',
                icon: 'calendar-alt',
                autoCreate: true,
                confidence: 'medium'
            });
        }
        
        if (fileTypes.documents >= 5) {
            suggestions.push({
                name: 'documents',
                description: 'Important document files',
                reason: `AI categorized ${fileTypes.documents} document files`,
                color: '#3B82F6',
                icon: 'file-alt',
                autoCreate: true,
                confidence: 'medium'
            });
        }
        
        if (fileTypes.images >= 3) {
            suggestions.push({
                name: 'personal',
                description: 'Personal photos and images',
                reason: `AI found ${fileTypes.images} image files`,
                color: '#EC4899',
                icon: 'heart',
                autoCreate: true,
                confidence: 'medium'
            });
        }
        
        // Add smart contextual tags
        suggestions.push({
            name: 'important',
            description: 'High-priority items',
            reason: 'AI content analysis suggests high importance',
            color: '#F59E0B',
            icon: 'star',
            autoCreate: true,
            confidence: 'low'
        });
        
        return suggestions;
    };
    const handleTemplatesAIOrganization = async () => {
        setIsAnalyzingFiles(true);
        setAISuggestionType('templates');
        
        try {
            const currentYear = new Date().getFullYear();
            
            // Generate more organized template suggestions
            const suggestions = {
                suggestedFolders: [
                    {
                        name: `Documents/${currentYear}/receipts`,
                        description: "Organize receipts by year for easy tax filing",
                        reason: "Keep receipts organized chronologically for better tax preparation and expense tracking",
                        files: uploadedFiles.filter(f => f.filename?.toLowerCase().includes('receipt')).slice(0, 3).map(f => f.filename)
                    },
                    {
                        name: `Documents/${currentYear}/invoices`,
                        description: "Separate invoices from other business documents",
                        reason: "Invoice tracking helps with accounts receivable and business revenue reporting",
                        files: uploadedFiles.filter(f => f.filename?.toLowerCase().includes('invoice')).slice(0, 3).map(f => f.filename)
                    },
                    {
                        name: `Documents/${currentYear}/job_sites`,
                        description: "Project-specific document organization",
                        reason: "Organize documents by project or job site for better project management",
                        files: uploadedFiles.filter(f => f.filename?.toLowerCase().includes('project') || f.filename?.toLowerCase().includes('job')).slice(0, 3).map(f => f.filename)
                    },
                    {
                        name: `Documents/${currentYear}/contracts`,
                        description: "Legal documents and agreements",
                        reason: "Keep contracts organized by year for easy reference and legal compliance",
                        files: uploadedFiles.filter(f => f.filename?.toLowerCase().includes('contract') || f.filename?.toLowerCase().includes('agreement')).slice(0, 3).map(f => f.filename)
                    }
                ],
                suggestedTemplates: [
                    { 
                        name: "Year-Based Receipt Organization", 
                        description: "Automatically organize receipts by year with smart categorization",
                        rules: [
                            "If filename contains 'receipt' → move to Documents/[year]/receipts/",
                            "Extract date from filename or content → organize by year",
                            "Auto-tag with vendor name and expense category"
                        ],
                        folderStructure: `Documents/${currentYear}/receipts/`,
                        automation: "Applies to new uploads automatically"
                    },
                    { 
                        name: "Business Document Classification", 
                        description: "Smart classification for business documents with hierarchical organization",
                        rules: [
                            "Invoices → Documents/[year]/invoices/ + 'Invoice' tag",
                            "Contracts → Documents/[year]/contracts/ + 'Legal' tag",
                            "Receipts → Documents/[year]/receipts/ + 'Expense' tag"
                        ],
                        folderStructure: `Documents/${currentYear}/[category]/`,
                        automation: "Processes files based on content analysis"
                    },
                    { 
                        name: "Project-Based Organization", 
                        description: "Organize documents by project or job site for better project management",
                        rules: [
                            "Project files → Documents/[year]/job_sites/[project_name]/",
                            "Extract project identifiers from filenames",
                            "Auto-tag with project name and document type"
                        ],
                        folderStructure: `Documents/${currentYear}/job_sites/[project]/`,
                        automation: "Creates project folders dynamically"
                    }
                ],
                applicableFiles: uploadedFiles.slice(0, 5).map(f => f.filename),
                insights: [
                    `Year-based organization makes tax preparation 90% faster`,
                    `Hierarchical folder structures reduce file search time by 75%`,
                    `Automated templates can process ${uploadedFiles.length} files instantly`
                ]
            };
            
            setAISuggestions({ ...suggestions, type: 'templates' });
            setShowAISuggestionsModal(true);
        } catch (error) {
            console.error('AI templates analysis error:', error);
        } finally {
            setIsAnalyzingFiles(false);
        }
    };

    const handleApplyAISuggestions = (selectedSuggestions) => {
        console.log('Applying AI suggestions:', selectedSuggestions);
        
        // Here you would apply the metadata-only changes
        // For now, just close the modal and show a success message
        setShowAISuggestionsModal(false);
        setAISuggestions(null);
        setAISuggestionType(null);
        
        // Show success in AI chat
        setAiMessages(prev => [...prev, {
            type: 'ai',
            message: `Successfully applied AI organization suggestions! ${selectedSuggestions.appliedCount || 'Multiple'} changes were made to improve your file organization.`
        }]);
    };

    const handleDismissAISuggestions = () => {
        setShowAISuggestionsModal(false);
        setAISuggestions(null);
        setAISuggestionType(null);
    };

    // Duplicate cleanup functions
    const detectDuplicates = (files) => {
        const groups = {};
        const duplicateGroups = [];

        files.forEach(file => {
            // Create multiple keys for different duplicate detection methods
            const sizeKey = `size_${file.size}`;
            const nameKey = `name_${file.filename?.toLowerCase()}`;
            const similarNameKey = `similar_${file.filename?.toLowerCase()?.replace(/[^a-zA-Z0-9]/g, '')}`;
            
            // Group by exact filename (case insensitive)
            if (!groups[nameKey]) groups[nameKey] = [];
            groups[nameKey].push(file);
            
            // Group by size (for potential duplicates)
            if (!groups[sizeKey]) groups[sizeKey] = [];
            groups[sizeKey].push(file);
            
            // Group by similar names (removing special characters)
            if (!groups[similarNameKey]) groups[similarNameKey] = [];
            groups[similarNameKey].push(file);
        });

        // Find groups with multiple files
        Object.entries(groups).forEach(([key, groupFiles]) => {
            if (groupFiles.length > 1) {
                const [type, value] = key.split('_', 2);
                
                // Avoid duplicate group entries
                const existingGroup = duplicateGroups.find(g => 
                    g.files.some(f1 => groupFiles.some(f2 => f1.file_key === f2.file_key))
                );
                
                if (!existingGroup) {
                    duplicateGroups.push({
                        id: `${type}_${Date.now()}_${Math.random()}`,
                        type: type === 'name' ? 'Exact filename match' : 
                              type === 'size' ? 'Same file size' : 'Similar filename',
                        reason: type === 'name' ? `Files with identical name: "${value}"` :
                               type === 'size' ? `Files with same size: ${formatFileSize(parseInt(value))}` :
                               `Files with similar names`,
                        files: groupFiles,
                        recommendKeep: groupFiles.reduce((latest, file) => 
                            new Date(file.last_modified) > new Date(latest.last_modified) ? file : latest
                        ),
                        potentialSavings: groupFiles.slice(1).reduce((sum, file) => sum + (file.size || 0), 0)
                    });
                }
            }
        });

        return duplicateGroups;
    };
    const handleDuplicateCleanup = async () => {
        setIsDuplicateAnalysis(true);
        
        try {
            // Add to AI chat
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: '🔍 Analyzing your files for duplicates... This may take a moment.'
            }]);

            // Simulate analysis delay
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            const duplicates = detectDuplicates(uploadedFiles);
            
            if (duplicates.length === 0) {
                setAiMessages(prev => [...prev, {
                    type: 'ai',
                    message: '✨ Great news! No duplicate files were found in your storage. Your files are well organized!'
                }]);
            } else {
                const totalWastedSpace = duplicates.reduce((sum, group) => sum + group.potentialSavings, 0);
                setDuplicateGroups(duplicates);
                setShowDuplicatesModal(true);
                
                setAiMessages(prev => [...prev, {
                    type: 'ai',
                    message: `🎯 Found ${duplicates.length} group(s) of potential duplicate files that could save ${formatFileSize(totalWastedSpace)} of storage space. Click the button above to review and clean them up!`
                }]);
            }
        } catch (error) {
            console.error('Duplicate analysis error:', error);
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: '❌ Sorry, there was an error analyzing your files for duplicates. Please try again.'
            }]);
        } finally {
            setIsDuplicateAnalysis(false);
        }
    };
    const handleBulkDeleteDuplicates = async (deletedFiles, errors = []) => {
        try {
            const fileKeys = deletedFiles.map(file => file.file_key);
            
            // Remove deleted files from local state
            setUploadedFiles(prev => prev.filter(file => !fileKeys.includes(file.file_key)));
            
            let message = `✅ Successfully deleted ${deletedFiles.length} duplicate file(s) and freed up storage space!`;
            
            if (errors.length > 0) {
                message += ` (${errors.length} file(s) could not be deleted)`;
            }
            
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: message
            }]);
            
            setShowDuplicatesModal(false);
            setDuplicateGroups([]);
            
            // Trigger file refresh in S3FileManager
            setFileRefreshTrigger(prev => prev + 1);
            
        } catch (error) {
            console.error('Bulk delete error:', error);
            setAiMessages(prev => [...prev, {
                type: 'ai',
                message: '❌ Sorry, there was an error deleting the duplicate files. Please try again.'
            }]);
        }
    };
    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showProfileDropdown && !event.target.closest('.profile-dropdown-container')) {
                setShowProfileDropdown(false);
            }
            if (showSearchResults && !event.target.closest('.search-container')) {
                setShowSearchResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showProfileDropdown, showSearchResults]);

    // Auto-scroll chat to bottom when new messages are added
    useEffect(() => {
        const chatContainer = document.getElementById('chat-container');
        if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
        }
    }, [aiMessages]);

    // Generate initials for email users
    const getInitials = (name, email) => {
        if (name && name.trim()) {
            return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
        }
        return email ? email[0].toUpperCase() : 'U';
    };

    // Storage calculation functions
    const calculateTotalStorage = () => {
        return uploadedFiles.reduce((total, file) => {
            // Estimate file size if not provided (rough estimate based on file type)
            const estimatedSize = file.file_size || estimateFileSize(file.original_name);
            return total + estimatedSize;
        }, 0);
    };

    const estimateFileSize = (filename) => {
        const safe = (filename || '').toString();
        const ext = safe.toLowerCase().split('.').pop();
        // Rough estimates in bytes
        switch (ext) {
            case 'pdf': return 2 * 1024 * 1024; // 2MB average
            case 'jpg': case 'jpeg': case 'png': case 'gif': return 5 * 1024 * 1024; // 5MB average
            case 'doc': case 'docx': return 1 * 1024 * 1024; // 1MB average
            case 'xls': case 'xlsx': return 3 * 1024 * 1024; // 3MB average
            case 'csv': return 500 * 1000; // 500KB average
            default: return 1 * 1024 * 1024; // 1MB default
        }
    };



    const totalStorageUsed = calculateTotalStorage();

    // Loading state - wait for both user and subscription checks
    if (loading || subscriptionLoading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-400 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">
                        {loading ? 'Loading profile...' : 'Checking subscription...'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <>
                <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
  {/* Success Banner */}
  {paymentSuccess && (
    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 mx-4 mt-4">
      <div className="flex items-center">
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <span className="font-medium">🎉 Payment successful! Welcome to Ordio!</span>
        <button 
          onClick={() => setPaymentSuccess(false)}
          className="ml-auto text-green-700 hover:text-green-900"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </div>
  )}
  {/* Modern Header */}
  <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
    <div className="px-4 md:px-6 py-3 md:py-4">
      <div className="flex items-center justify-between">
        {/* Left side - Logo and Search */}
        <div className="flex items-center space-x-3 md:space-x-6">
          {/* Mobile hamburger */}
          <button
            type="button"
            aria-label="Open sidebar"
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => setIsMobileSidebarOpen(true)}
          >
            <svg className="w-6 h-6 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          {/* Ordio Logo */}
          <div className="flex items-center space-x-2 md:space-x-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <span className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">ordio</span>
          </div>
          {/* Quick search (mobile visible) */}
          <div className="md:hidden flex-1">
            <input
              type="search"
              placeholder="Search files"
              value={searchQuery}
              onChange={(e)=>setSearchQuery(e.target.value)}
              onKeyDown={(e)=>{ if (e.key==='Enter') handleSearch(); }}
              className="w-full px-3 py-2 rounded-md bg-gray-100 dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none"
            />
          </div>
        </div>

        {/* Right side - Controls and Profile */}
        <div className="flex items-center space-x-2 md:space-x-4">
          
          {/* Dark mode toggle */}
          <button 
            onClick={toggleDarkMode}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
          >
            {isDarkMode ? (
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>
          
          {/* Settings */}
          <button 
            onClick={handleSettings}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
          
          {/* User Profile */}
          <div className="relative profile-dropdown-container">
            <button
              onClick={toggleProfileDropdown}
              className="flex items-center space-x-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
            >
              <div className="relative">
                {user?.google_id && user?.profile_picture ? (
                  <img 
                    src={user.profile_picture} 
                    alt="Profile" 
                    className="w-8 h-8 rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs ${
                    user?.google_id 
                      ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
                      : 'bg-gradient-to-br from-gray-500 to-gray-600'
                  } ${user?.profile_picture ? 'hidden' : ''}`}
                >
                  {user?.google_id ? (
                    getInitials(user.full_name, user.email)
                  ) : (
                    <i className="fas fa-user text-xs"></i>
                  )}
                </div>
              </div>
            </button>

            {/* Dropdown Menu */}
            {showProfileDropdown && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 relative">
                      {user?.google_id && user?.profile_picture ? (
                        <img 
                          src={user.profile_picture} 
                          alt="Profile" 
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold bg-gradient-to-br from-blue-500 to-blue-600">
                          {user?.google_id ? getInitials(user.full_name, user.email) : <i className="fas fa-user"></i>}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {user?.full_name || 'User'}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="py-1">
                  <button
                    onClick={handleSettings}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <i className="fas fa-cog mr-3 text-gray-400"></i>
                    Settings
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <i className="fas fa-sign-out-alt mr-3"></i>
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </header>
  {showGDriveBrowser && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 md:p-6 max-w-4xl w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2 text-gray-900 dark:text-white">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7l9-4 9 4-9 4-9-4z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 15l-9 4-9-4" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10l9 4 9-4" /></svg>
            <div className="flex items-center space-x-1 text-sm">
              {gdrivePathStack.map((p, idx) => (
                <React.Fragment key={p.id}>
                  <button onClick={async () => {
                    if (idx === gdrivePathStack.length - 1) return;
                    const next = gdrivePathStack.slice(0, idx + 1);
                    setGdrivePathStack(next);
                    setGdriveLoading(true);
                    try {
                      const data = await listGoogleDriveChildren({ parentId: next[next.length - 1].id });
                      setGdriveChildren(Array.isArray(data?.files) ? data.files : []);
                    } catch (_) { setGdriveChildren([]); }
                    finally { setGdriveLoading(false); }
                  }} className="hover:underline">{p.name}</button>
                  {idx < gdrivePathStack.length - 1 && <span>/</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={driveNavUp} className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded">Up</button>
            <button onClick={() => setShowGDriveBrowser(false)} className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded">Close</button>
          </div>
        </div>
        <div className="min-h-[320px] border border-gray-200 dark:border-gray-700 rounded p-3 bg-gray-50 dark:bg-gray-900">
          {gdriveLoading ? (
            <div className="text-sm text-gray-600 dark:text-gray-300">Loading…</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {gdriveChildren.map((item) => {
                const isFolder = (item.mimeType || '').toLowerCase() === 'application/vnd.google-apps.folder';
                return (
                  <button key={item.id} onClick={() => {
                    if (isFolder) {
                      return driveNavInto(item);
                    }
                    // Open preview modal for file items
                    const mapped = {
                      filename: item.name,
                      file_key: item.id,
                      size: Number(item.size) || 0,
                      last_modified: item.modifiedTime || item.modified_at || item.createdTime || null,
                      source: 'drive',
                      external_url: `https://drive.google.com/file/d/${item.id}/view`,
                      mimeType: item.mimeType || ''
                    };
                    handleFileClick(mapped);
                  }} className="text-left p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:shadow">
                    <div className="flex items-center space-x-2">
                      {isFolder ? (
                        <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h4l2 2h10a2 2 0 012 2v6a2 2 0 01-2 2H3a2 2 0 01-2-2V9a2 2 0 012-2z" /></svg>
                      ) : (
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                      )}
                      <span className="text-xs text-gray-800 dark:text-gray-200 truncate" title={item.name}>{item.name}</span>
                    </div>
                    {!isFolder && (
                      <div className="mt-1 text-[10px] text-gray-500">{item.mimeType?.split('/')[1] || 'file'}</div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )}
  <div className="flex h-screen">
    {/* Mobile slide-over sidebar */}
    {isMobileSidebarOpen && (
      <div className="fixed inset-0 z-40 md:hidden">
        <div className="absolute inset-0 bg-black/40" onClick={() => setIsMobileSidebarOpen(false)}></div>
        <div className="absolute inset-y-0 left-0 w-64 bg-white dark:bg-gray-800 shadow-xl border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg font-semibold text-gray-900 dark:text-white">Menu</span>
            <button
              type="button"
              aria-label="Close sidebar"
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => setIsMobileSidebarOpen(false)}
            >
              <svg className="w-5 h-5 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Navigation (mobile) - duplicate of desktop list */}
          <nav className="space-y-1">
          {[
            { id: 'overview', name: 'Overview', icon: null },
            { id: 'files', name: 'Files', icon: null },
            { id: 'photos', name: 'Photos', icon: null },
            { id: 'audio', name: 'Audio', icon: null },
            { id: 'videos', name: 'Videos', icon: null },
            { id: 'upload', name: 'Upload', icon: null },
            { id: 'tags', name: 'Tags', icon: null },
            { id: 'templates', name: 'Templates', icon: null },
            { id: 'insights', name: 'AI Insights', icon: null },
            { id: 'assistant', name: 'AI Assistant', icon: null }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setIsMobileSidebarOpen(false); }}
              className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                activeTab === item.id
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {item.name}
            </button>
          ))}
          </nav>
        </div>
      </div>
    )}

    {/* Modern Sidebar (desktop) */}
    <div className={`${isDesktopSidebarVisible ? 'md:block' : 'md:hidden'} hidden w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700`}>
      <div className="p-4">
        {/* Navigation */}
        <nav className="space-y-1">
          {[
            { 
              id: 'overview', 
              name: 'Overview', 
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
              )
            },
            { 
              id: 'files', 
              name: 'Files', 
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              )
            },
            { 
              id: 'photos', 
              name: 'Photos', 
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )
            },
            { 
              id: 'audio', 
              name: 'Audio', 
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              )
            },
            { 
              id: 'videos', 
              name: 'Videos', 
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )
            },
            { 
              id: 'upload', 
              name: 'Upload', 
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              )
            },
            { 
              id: 'tags', 
              name: 'Tags', 
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              )
            },
            { 
              id: 'templates', 
              name: 'Templates', 
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )
            },
            { 
              id: 'insights', 
              name: 'AI Insights', 
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              )
            },
            { 
              id: 'assistant', 
              name: 'AI Assistant', 
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              )
            }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                activeTab === item.id
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <span className={`mr-3 ${activeTab === item.id ? 'text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400'}`}>
                {item.icon}
              </span>
              {item.name}
            </button>
          ))}
        </nav>
        <div className="mt-8">
          {/* Storage Usage */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-3">
              <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
              </svg>
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Storage Used</span>
            </div>
            
            {/* Storage Details */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Total Size</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatFileSize(totalStorageUsed)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Files</span>
                <span className="font-medium text-gray-900 dark:text-white">{uploadedFiles.length}</span>
              </div>
            </div>
          </div>

          {/* Recent Files */}
          {uploadedFiles.length > 0 && (
            <div>
              <h6 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                Recent Files
              </h6>
              <div className="space-y-1">
                {uploadedFiles.slice(0, 3).map((file, index) => {
                  const getFileIcon = (filename) => {
                    const ext = (filename || '').toLowerCase().split('.').pop();
                    switch (ext) {
                      case 'pdf': return 'fas fa-file-pdf text-red-500';
                      case 'jpg': case 'jpeg': case 'png': case 'gif': return 'fas fa-file-image text-blue-500';
                      case 'doc': case 'docx': return 'fas fa-file-word text-blue-600';
                      case 'xls': case 'xlsx': return 'fas fa-file-excel text-green-600';
                      case 'csv': return 'fas fa-file-csv text-green-500';
                      default: return 'fas fa-file text-gray-500';
                    }
                  };
                  
                  return (
                    <div key={index} className="flex items-center px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer group">
                      <i className={`${getFileIcon(file.original_name)} mr-2 text-sm`}></i>
                      <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                        {file.original_name}
                      </span>
                    </div>
                  );
                })}
                {uploadedFiles.length > 3 && (
                  <div className="text-center pt-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      +{uploadedFiles.length - 3} more
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    {/* Main Content */}
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 flex flex-col pb-16 md:pb-0">
      {/* Content Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                {activeTab === 'overview' && 'Overview'}
                {activeTab === 'files' && 'Files'}
                {activeTab === 'photos' && 'Photos'}
                {activeTab === 'audio' && 'Audio'}
                {activeTab === 'videos' && 'Videos'}
                {activeTab === 'upload' && 'Upload'}
                {activeTab === 'tags' && 'Tags'}
                {activeTab === 'templates' && 'Templates'}
                {activeTab === 'insights' && 'AI Insights'}
                {activeTab === 'assistant' && 'AI Assistant'}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {activeTab === 'overview' && 'Your personal file storage and organization hub'}
                {activeTab === 'files' && 'Manage and organize your uploaded files'}
                {activeTab === 'photos' && 'View and organize your photos in gallery format'}
                {activeTab === 'audio' && 'Manage and play your audio files and music collection'}
                {activeTab === 'videos' && 'Watch and organize your video files and recordings'}
                {activeTab === 'upload' && 'Upload new files to your secure storage'}
                {activeTab === 'tags' && 'Create and manage file organization tags'}
                {activeTab === 'templates' && 'Automated file sorting templates'}
                {activeTab === 'insights' && 'AI-powered organization insights and recommendations'}
                {activeTab === 'assistant' && 'Get help organizing your files with AI'}
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              {activeTab === 'overview' && (
                <button
                  onClick={() => setActiveTab('upload')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  New Upload
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 p-6 overflow-auto bg-white dark:bg-gray-800">
          {/* Search Section - Always visible */}
          <div className="mb-6 relative search-container">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                className="block w-full pl-12 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-xl leading-5 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white dark:focus:bg-gray-600 transition-all duration-200"
                placeholder="Search files, folders, and features..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  handleSearch(e.target.value);
                }}
                onFocus={() => {
                  if (searchQuery) {
                    setShowSearchResults(true);
                  }
                }}
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                >
                  <svg className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {/* Search Results Dropdown */}
            {showSearchResults && (searchResults.length > 0 || isSearching) && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg z-50 max-h-96 overflow-y-auto">
                {isSearching ? (
                  <div className="p-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Searching...</p>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="py-2">
                    {searchResults.map((result, index) => (
                      <button
                        key={index}
                        onClick={() => handleSearchResultClick(result)}
                        className="w-full flex items-center px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                      >
                        <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center mr-3">
                          <i className={`${result.icon} text-sm`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {result.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {result.description}
                          </p>
                        </div>
                        <div className="flex-shrink-0 ml-3">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200">
                            {result.type}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">No results found</p>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Suggested folders */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Suggested folders</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { name: 'Documents', icon: 'fas fa-folder', color: 'text-blue-500', files: uploadedFiles.filter(f => f.original_name?.toLowerCase().includes('.pdf') || f.original_name?.toLowerCase().includes('.doc')).length },
                    { name: 'Images', icon: 'fas fa-images', color: 'text-green-500', files: uploadedFiles.filter(f => f.original_name?.toLowerCase().includes('.jpg') || f.original_name?.toLowerCase().includes('.png')).length },
                    { name: 'Receipts', icon: 'fas fa-receipt', color: 'text-purple-500', files: uploadedFiles.filter(f => f.original_name?.toLowerCase().includes('receipt')).length }
                  ].map((folder, index) => (
                    <div key={index} className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors group">
                      <div className="flex-shrink-0 mr-3">
                        <div className="w-10 h-10 bg-white dark:bg-gray-600 rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                          <i className={`${folder.icon} ${folder.color} text-lg`}></i>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{folder.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{folder.files} files</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Access */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Quick access</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { name: 'Upload Files', tab: 'upload', icon: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12', color: 'bg-blue-500' },
                    { name: 'Manage Tags', tab: 'tags', icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z', color: 'bg-purple-500' },
                    { name: 'Templates', tab: 'templates', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', color: 'bg-green-500' },
                    { name: 'AI Assistant', tab: 'assistant', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z', color: 'bg-orange-500' }
                  ].map((action, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveTab(action.tab)}
                      className="flex flex-col items-center p-6 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors group"
                    >
                      <div className={`w-12 h-12 ${action.color} rounded-lg flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}>
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={action.icon} />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{action.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Recent Files */}
              {uploadedFiles.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Recent files</h3>
                    <button
                      onClick={() => setActiveTab('files')}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      View all
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {uploadedFiles.slice(0, 8).map((file, index) => {
                      const getFileIcon = (filename) => {
                        const ext = (filename || '').toLowerCase().split('.').pop();
                        switch (ext) {
                          case 'pdf': return { icon: 'fas fa-file-pdf', color: 'text-red-500' };
                          case 'jpg': case 'jpeg': case 'png': case 'gif': return { icon: 'fas fa-file-image', color: 'text-blue-500' };
                          case 'doc': case 'docx': return { icon: 'fas fa-file-word', color: 'text-blue-600' };
                          case 'xls': case 'xlsx': return { icon: 'fas fa-file-excel', color: 'text-green-600' };
                          default: return { icon: 'fas fa-file', color: 'text-gray-500' };
                        }
                      };
                      
                      const fileIcon = getFileIcon(file.original_name);
                      
                      return (
                        <div key={index} className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors group">
                          <div className="flex-shrink-0 mr-3">
                            <div className="w-10 h-10 bg-white dark:bg-gray-600 rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                              <i className={`${fileIcon.icon} ${fileIcon.color} text-lg`}></i>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.original_name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Recently uploaded</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Storage Info */}
              <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-800 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Ordio Cloud Storage</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{uploadedFiles.length} files • {formatFileSize(totalStorageUsed)} used</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-green-600 dark:text-green-400">Active</p>
                </div>
              </div>
            </div>
          )}

          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <div>
              {/* AI Analysis Progress Banner */}
              {isAnalyzingFiles && (
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                        AI is performing advanced content analysis...
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        Using OCR + AI to extract merchant names, amounts, dates, and transaction details
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-blue-600 dark:text-blue-400">
                    <div className="flex items-center space-x-4">
                      <span>📄 Reading document content</span>
                      <span>🔍 Extracting key data</span>
                      <span>🗂️ Generating smart folders</span>
                      <span>📝 Creating intelligent filenames</span>
                    </div>
                  </div>
                </div>
              )}

              <S3FileUpload
                onFilesSelected={(files) => openUploadOptionsModal(files, 'file')}
                onUploadSuccess={(results) => {
                  console.log('Files uploaded successfully:', results);
                  setFileRefreshTrigger(prev => prev + 1);
                  
                  // Trigger AI analysis after a short delay
                  setTimeout(() => {
                    handleUploadAIAnalysis(results);
                  }, 1500);
                  // Stay on the originating tab
                  // If upload was initiated from the modal, handleConfirmUploadOptions already routes back.
                  // For inline upload here, do nothing to avoid forced navigation.
                }}
                onUploadError={(error) => {
                  console.error('Upload error:', error);
                  alert(`Upload failed: ${error.message}`);
                }}
                className="mb-8"
              />
              
              {/* File Type Support Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <i className="fas fa-file-alt text-4xl text-blue-500 mx-auto mb-3"></i>
                  <h6 className="mb-2 font-semibold text-gray-900 dark:text-white">Documents</h6>
                  <p className="text-sm text-gray-500 dark:text-gray-400">PDF, DOC, DOCX</p>
                </div>
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <i className="fas fa-image text-4xl text-green-500 mx-auto mb-3"></i>
                  <h6 className="mb-2 font-semibold text-gray-900 dark:text-white">Images</h6>
                  <p className="text-sm text-gray-500 dark:text-gray-400">JPG, PNG, JPEG</p>
                </div>
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <i className="fas fa-table text-4xl text-purple-500 mx-auto mb-3"></i>
                  <h6 className="mb-2 font-semibold text-gray-900 dark:text-white">Spreadsheets</h6>
                  <p className="text-sm text-gray-500 dark:text-gray-400">CSV, XLS, XLSX</p>
                </div>
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <i className="fas fa-music text-4xl text-pink-500 mx-auto mb-3"></i>
                  <h6 className="mb-2 font-semibold text-gray-900 dark:text-white">Audio</h6>
                  <p className="text-sm text-gray-500 dark:text-gray-400">MP3, WAV, FLAC, M4A, AAC, OGG, WMA</p>
                </div>
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <i className="fas fa-video text-4xl text-indigo-500 mx-auto mb-3"></i>
                  <h6 className="mb-2 font-semibold text-gray-900 dark:text-white">Videos</h6>
                  <p className="text-sm text-gray-500 dark:text-gray-400">MP4, MOV, AVI, MKV, WMV, FLV, WebM, M4V</p>
                </div>
              </div>
            </div>
          )}
          {/* Files Tab */}
          {activeTab === 'files' && (
            <div>
              {/* Files Header with Create Folder */}
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    File Manager ({uploadedFiles.length} files)
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleCreateFileFolder}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      New Folder
                    </button>
                    <button
                      onClick={() => setActiveTab('upload')}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12" />
                      </svg>
                      Upload
                    </button>
                    {selectedFileFolder && (
                      <button
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors"
                        onClick={() => {
                          const num = countFilesInFolder(uploadedFiles, selectedFileFolder || '');
                          setFolderDeleteTarget({ kind: 'files', path: selectedFileFolder || '' });
                          setFolderDeleteCount(num);
                        }}
                        title="Delete this folder and its contents"
                      >
                        Delete Folder
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Create Folder Modal */}
              {showCreateFileFolder && (
                <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={newFileFolderName}
                        onChange={(e) => setNewFileFolderName(e.target.value)}
                        placeholder="Enter folder name..."
                        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                        onKeyPress={(e) => e.key === 'Enter' && handleFileFolderCreate()}
                        autoFocus
                      />
                    </div>
                    <button
                      onClick={handleFileFolderCreate}
                      disabled={!newFileFolderName.trim()}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      Create
                    </button>
                    <button
                      onClick={handleFileFolderCancel}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {/* File Folders (derived from virtual paths) */}
                <div className="mb-6">
                {renderBreadcrumb(
                  selectedFileFolder || '',
                  (next) => setSelectedFileFolder(next || ''),
                  'Files'
                )}
                {/* Google Drive inline section removed in favor of main table integration */}
                {getSubfoldersFromFiles(uploadedFiles, selectedFileFolder || '').length > 0 && (
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Folders</h4>
                  </div>
                )}
                {getSubfoldersFromFiles(uploadedFiles, selectedFileFolder || '').length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {getSubfoldersFromFiles(uploadedFiles, selectedFileFolder || '').map((folder) => (
                      <div
                        key={folder.id}
                        className={`cursor-pointer p-3 rounded-lg border-2 transition-all duration-200 ${selectedFileFolder === folder.id ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-600 bg-white dark:bg-gray-800'}`}
                        onClick={() => setSelectedFileFolder(folder.id)}
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/40 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                          </div>
                          <div className="text-center">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{folder.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">&nbsp;</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {isGDriveConnected && gdriveParentFolders.map((gf) => (
                      <div
                        key={`gdrive:${gf.id}`}
                        className="cursor-pointer p-3 rounded-lg border-2 border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 bg-white dark:bg-gray-800 relative"
                        onClick={() => openGDriveFolderById(gf.id, gf.name)}
                        title={`Google Drive • ${gf.name}`}
                      >
                        <div className="absolute top-2 right-2">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true"><path d="M7.5 3L2 12l3.5 6L11 9 7.5 3z" fill="#34a853"/><path d="M16.5 3H7.5L11 9h9l-3.5-6z" fill="#fbbc05"/><path d="M22 12H11l-5.5 6H18.5L22 12z" fill="#4285f4"/></svg>
                        </div>
                        <div className="flex flex-col items-center space-y-2">
                          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" /></svg>
                          </div>
                          <div className="text-center">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{gf.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Google Drive</p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
              </div>

              {/* AI Organize Section */}
              <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-purple-900 dark:text-purple-200">
                      AI File Organization
                    </h4>
                    <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                      Let AI analyze and suggest ways to organize your {uploadedFiles.length} files
                    </p>
                  </div>
                  <button
                    onClick={handleBatchAIOrganization}
                    disabled={isAnalyzingFiles || uploadedFiles.length === 0}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isAnalyzingFiles ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        AI Organize
                      </>
                    )}
                  </button>
                </div>
              </div>

              <S3FileManager 
                refreshTrigger={fileRefreshTrigger}
                className="min-h-[500px]"
                onFileClick={handleFileClick}
                externalFiles={(isGDriveConnected ? gdriveFiles : []).map(f => ({
                  // Map drive files into table-friendly shape
                  upload_id: `drive:${f.id}`,
                  file_key: `drive:${f.id}`,
                  filename: f.name,
                  size: Number(f.size || 0),
                  last_modified: f.modifiedTime,
                  source: 'drive',
                  external_url: `https://drive.google.com/file/d/${f.id}/view`,
                  parent_name: f.parent_name || null,
                  grandparent_name: f.grandparent_name || null,
                }))}
                onFilesLoaded={(files) => {
                  // Update uploadedFiles state with actual files from S3FileManager
                  console.log('Files loaded from S3FileManager:', files.length);
                  const processedFiles = files.map(file => ({
                    ...file,
                    original_name: file.filename, // Map filename to original_name for consistency
                    id: file.file_key
                  }));
                  setUploadedFiles(processedFiles);
                  
                  // Also update media files
                  const imageFiles = processedFiles.filter(file => {
                    const ext = file.filename?.toLowerCase().split('.').pop();
                    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
                  });
                  setPhotoFiles(imageFiles);

                  const audioFilesList = processedFiles.filter(file => {
                    const ext = file.filename?.toLowerCase().split('.').pop();
                    return ['mp3', 'wav', 'flac', 'm4a', 'aac', 'ogg', 'wma'].includes(ext);
                  });
                  setAudioFiles(audioFilesList);

                  const videoFilesList = processedFiles.filter(file => {
                    const ext = file.filename?.toLowerCase().split('.').pop();
                    return ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm', 'm4v'].includes(ext);
                  });
                  setVideoFiles(videoFilesList);
                }}
              />
            </div>
          )}
          {/* Audio Tab */}
          {activeTab === 'audio' && (
            <div>
              {/* Audio Header with Create Folder */}
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Audio Library ({audioFiles.length} tracks)
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleCreateAudioFolder}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      New Folder
                    </button>
                  <input
                    type="file"
                    id="audio-upload"
                    className="hidden"
                    multiple
                    accept="audio/*"
                    onChange={(e) => openUploadOptionsModal(e.target.files, 'audio')}
                  />
                  <label
                    htmlFor="audio-upload"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 cursor-pointer transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Upload Audio
                  </label>
                  {selectedAudioFolder && (
                    <button
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors"
                      onClick={() => {
                        const num = countFilesInFolder(audioFiles, selectedAudioFolder || '');
                        setFolderDeleteTarget({ kind: 'audio', path: selectedAudioFolder || '' });
                        setFolderDeleteCount(num);
                      }}
                      title="Delete this folder and its contents"
                    >
                      Delete Folder
                    </button>
                  )}
                  </div>
                </div>
              </div>

              {/* Create Folder Modal */}
              {showCreateAudioFolder && (
                <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={newAudioFolderName}
                        onChange={(e) => setNewAudioFolderName(e.target.value)}
                        placeholder="Enter folder name..."
                        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                        onKeyPress={(e) => e.key === 'Enter' && handleAudioFolderCreate()}
                        autoFocus
                      />
                    </div>
                    <button
                      onClick={handleAudioFolderCreate}
                      disabled={!newAudioFolderName.trim()}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Create
                    </button>
                    <button
                      onClick={handleAudioFolderCancel}
                      className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Audio Folders (derived from virtual paths) */}
                <div className="mb-6">
                {renderBreadcrumb(
                  selectedAudioFolder || '',
                  (next) => setSelectedAudioFolder(next || ''),
                  'Audio'
                )}
                {getSubfoldersFromFiles(audioFiles, selectedAudioFolder || '').length > 0 && (
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Folders</h4>
                  </div>
                )}
                {getSubfoldersFromFiles(audioFiles, selectedAudioFolder || '').length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {getSubfoldersFromFiles(audioFiles, selectedAudioFolder || '').map((folder) => (
                      <div
                        key={folder.id}
                        className="group relative bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                        onClick={() => setSelectedAudioFolder(folder.id)}
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center mb-2">
                            <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate w-full">
                            {folder.name}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">&nbsp;</span>
                        </div>
                      </div>
                    ))}
                    {isGDriveConnected && gdriveParentFoldersByCategory.audio.map((gf) => (
                      <div key={`gdrive:audio:${gf.id}`} className="group relative bg-white dark:bg-gray-700 border border-blue-200 dark:border-blue-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer transition-colors" onClick={() => openGDriveFolderById(gf.id, gf.name)}>
                        <div className="absolute top-2 right-2">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true"><path d="M7.5 3L2 12l3.5 6L11 9 7.5 3z" fill="#34a853"/><path d="M16.5 3H7.5L11 9h9l-3.5-6z" fill="#fbbc05"/><path d="M22 12H11l-5.5 6H18.5L22 12z" fill="#4285f4"/></svg>
                        </div>
                        <div className="flex flex-col items-center text-center">
                          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center mb-2">
                            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" /></svg>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate w-full">{gf.name}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Google Drive</span>
                        </div>
                      </div>
                    ))}
                </div>
              )}
              </div>

              {/* Audio Drag & Drop Upload Area */}
              <div 
                className={`mb-6 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  audioDragActive 
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                    : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500'
                }`}
                onDrop={handleAudioDrop}
                onDragOver={handleAudioDragOver}
                onDragLeave={handleAudioDragLeave}
              >
                <div className="flex flex-col items-center">
                  <svg className={`w-8 h-8 mb-2 ${
                    audioDragActive ? 'text-purple-500' : 'text-gray-400 dark:text-gray-500'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                  <p className={`text-sm mb-2 ${
                    audioDragActive 
                      ? 'text-purple-700 dark:text-purple-300' 
                      : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    {audioDragActive ? 'Drop audio files here!' : (
                      <>
                        Drag and drop audio files here, or{' '}
                        <label htmlFor="audio-upload" className="text-purple-600 dark:text-purple-400 hover:underline cursor-pointer">
                          browse files
                        </label>
                      </>
                    )}
                  </p>
                  {!audioDragActive && (
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      Supports MP3, WAV, FLAC, M4A, AAC, OGG, WMA
                    </p>
                  )}
                </div>
              </div>
              {/* Audio Library */}
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {selectedAudioFolder ? 'Folder Tracks' : 'All Tracks'}
                </h4>
                  {getFilesForPath(audioFiles, selectedAudioFolder || '').length > 0 && (
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          className="mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          checked={(function(){const list=getFilesForPath(audioFiles, selectedAudioFolder||'');return list.length>0&&list.every(a=>selectedAudioKeys.has(a.file_key));})()}
                          onChange={() => { const list=getFilesForPath(audioFiles, selectedAudioFolder||''); if (list.length>0 && list.every(a=>selectedAudioKeys.has(a.file_key))) setSelectedAudioKeys(new Set()); else setSelectedAudioKeys(new Set(list.map(a=>a.file_key))); }}
                        />
                        Select All
                      </label>
                      {selectedAudioKeys.size > 0 && (
                        <button
                          onClick={() => setShowAudioBulkDeleteConfirm(true)}
                          disabled={audioBulkDeleting}
                          className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                          title="Delete selected audio"
                        >
                          {audioBulkDeleting ? 'Deleting...' : `Delete (${selectedAudioKeys.size})`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                
                {getFilesForPath(audioFiles, selectedAudioFolder || '').length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No audio files yet</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      Upload your first audio files to start building your music library
                    </p>
                    <label
                      htmlFor="audio-upload"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 cursor-pointer transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      Upload Audio
                    </label>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {getFilesForPath(audioFiles, selectedAudioFolder || '').map((audio, index) => {
                      const checked = selectedAudioKeys.has(audio.file_key);
                      return (
                      <div
                        key={audio.file_key || index}
                        className={`group flex items-center p-4 bg-white dark:bg-gray-700 rounded-lg border ${checked ? 'border-purple-500' : 'border-gray-200 dark:border-gray-600'} hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer transition-colors`}
                        onClick={(e) => {
                          if (e && e.shiftKey) { e.preventDefault(); handleAudioShiftToggle(index, audio); return; }
                          handleAudioClick(audio);
                        }}
                      >
                        <input type="checkbox" className="mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" checked={checked} onChange={(e)=>handleAudioCheckboxChange(index,audio,e)} onClick={(e)=>e.stopPropagation()} />
                        <div className="flex-shrink-0 w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center mr-4">
                          <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {audio.filename}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatFileSize(audio.size || 0)} • {new Date(audio.last_modified).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            className="p-2 text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
                            title="Play"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAudioClick(audio);
                            }}
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                            </svg>
                          </button>
                          <button
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            title="Download"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFileDownload(audio);
                            }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )})}
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Photos Tab */}
          {activeTab === 'photos' && (
            <div>
              {/* Photos Header with Create Folder */}
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Photo Gallery ({photoFiles.length} photos)
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleCreatePhotoFolder}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      New Folder
                    </button>
                  <input
                    type="file"
                    id="photo-upload"
                    className="hidden"
                    multiple
                    accept="image/*"
                    onChange={(e) => openUploadOptionsModal(e.target.files, 'photo')}
                  />
                  <label
                    htmlFor="photo-upload"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Upload Photos
                  </label>
                  {currentPhotoFolderPath && (
                    <button
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors"
                      onClick={() => {
                        const num = countFilesInFolder(photoFiles, currentPhotoFolderPath, 'photos');
                        setFolderDeleteTarget({ kind: 'photos', path: currentPhotoFolderPath });
                        setFolderDeleteCount(num);
                      }}
                      title="Delete this folder and its contents"
                    >
                      Delete Folder
                    </button>
                  )}
                  </div>
                </div>
              </div>

              {/* Create Folder Modal */}
              {showCreatePhotoFolder && (
                <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={newPhotoFolderName}
                        onChange={(e) => setNewPhotoFolderName(e.target.value)}
                        placeholder="Enter folder name..."
                        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        onKeyPress={(e) => e.key === 'Enter' && handlePhotoFolderCreate()}
                        autoFocus
                      />
                    </div>
                    <button
                      onClick={handlePhotoFolderCreate}
                      disabled={!newPhotoFolderName.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Create
                    </button>
                    <button
                      onClick={handlePhotoFolderCancel}
                      className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {/* Photo Folders (derived from virtual paths) */}
                <div className="mb-6">
                {renderBreadcrumb(
                  currentPhotoFolderPath,
                  (next) => { setCurrentPhotoFolderPath(next); setSelectedPhotoFolder(next || null); },
                  'Photos'
                )}
                {getSubfoldersFromFiles(photoFiles, currentPhotoFolderPath, 'photos').length > 0 && (
                  <>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Folders</h4>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {getSubfoldersFromFiles(photoFiles, currentPhotoFolderPath, 'photos').map((folder) => (
                      <div
                        key={folder.id}
                        className="group relative bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                          onClick={() => {
                            setSelectedPhotoFolder(folder.id);
                            setCurrentPhotoFolderPath(folder.path);
                          }}
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center mb-2">
                            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate w-full">
                            {folder.name}
                          </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">&nbsp;</span>
                        </div>
                      </div>
                    ))}
                    {isGDriveConnected && gdriveParentFoldersByCategory.photos.map((gf) => (
                      <div
                        key={`gdrive:photos:${gf.id}`}
                        className="group relative bg-white dark:bg-gray-700 border-2 border-blue-200 dark:border-blue-700 rounded-lg p-4 hover:border-blue-400 dark:hover:border-blue-500 cursor-pointer transition-colors"
                        onClick={() => openGDriveFolderById(gf.id, gf.name)}
                        title={`Google Drive • ${gf.name}`}
                      >
                        <div className="absolute top-2 right-2">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true"><path d="M7.5 3L2 12l3.5 6L11 9 7.5 3z" fill="#34a853"/><path d="M16.5 3H7.5L11 9h9l-3.5-6z" fill="#fbbc05"/><path d="M22 12H11l-5.5 6H18.5L22 12z" fill="#4285f4"/></svg>
                        </div>
                        <div className="flex flex-col items-center text-center">
                          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center mb-2">
                            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate w-full">
                            {gf.name}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Google Drive</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  </>
              )}
              </div>

              {/* Photo Drag & Drop Upload Area */}
              <div 
                className={`mb-6 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  photoDragActive 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                    : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
                }`}
                onDrop={handlePhotoDrop}
                onDragOver={handlePhotoDragOver}
                onDragLeave={handlePhotoDragLeave}
              >
                <div className="flex flex-col items-center">
                  <svg className={`w-8 h-8 mb-2 ${
                    photoDragActive ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className={`text-sm mb-2 ${
                    photoDragActive 
                      ? 'text-blue-700 dark:text-blue-300' 
                      : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    {photoDragActive ? 'Drop photos here!' : (
                      <>
                        Drag and drop photos here, or{' '}
                        <label htmlFor="photo-upload" className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer">
                          browse files
                        </label>
                      </>
                    )}
                  </p>
                  {!photoDragActive && (
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      Supports JPG, PNG, GIF, WebP, BMP, SVG
                    </p>
                  )}
                </div>
              </div>

              {/* Photo Gallery - hide items in other folders at root; show only items within selected path */}
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {selectedPhotoFolder ? 'Folder Photos' : 'All Photos'}
                </h4>
                  {getFilesForPath(photoFiles, currentPhotoFolderPath, 'photos').length > 0 && (
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={isAllPhotosSelected()}
                          onChange={handlePhotoSelectAll}
                        />
                        Select All
                      </label>
                      {selectedPhotoKeys.size > 0 && (
                        <button
                          onClick={handlePhotoBulkDeleteRequest}
                          disabled={photoBulkDeleting}
                          className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                          title="Delete selected photos"
                        >
                          {photoBulkDeleting ? 'Deleting...' : `Delete (${selectedPhotoKeys.size})`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                
                {getFilesForPath(photoFiles, currentPhotoFolderPath, 'photos').length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No photos yet</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      Upload your first photos to get started with your gallery
                    </p>
                    <label
                      htmlFor="photo-upload"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      Upload Photos
                    </label>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
                    {getFilesForPath(photoFiles, currentPhotoFolderPath, 'photos').map((photo, index) => {
                      const checked = selectedPhotoKeys.has(photo.file_key);
                      return (
                        <div key={photo.file_key || index} className={`relative group rounded-lg overflow-hidden ${checked ? 'ring-2 ring-blue-500' : ''}`}>
                          <LazyPhotoThumb
                            photo={photo}
                            onClick={(e) => {
                              if (e && e.shiftKey) {
                                e.preventDefault();
                                handlePhotoShiftToggle(index, photo);
                                return;
                              }
                              handlePhotoClick(photo);
                            }}
                          />
                          <div className="absolute top-2 left-2">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded bg-white/90"
                              checked={checked}
                              onChange={(e) => handlePhotoCheckboxChange(index, photo, e)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Videos Tab */}
          {activeTab === 'videos' && (
            <div>
              {/* Videos Header with Create Folder */}
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Video Library ({videoFiles.length} videos)
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleCreateVideoFolder}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      New Folder
                    </button>
                  <input
                    type="file"
                    id="video-upload"
                    className="hidden"
                    multiple
                    accept="video/*"
                    onChange={(e) => openUploadOptionsModal(e.target.files, 'video')}
                  />
                  <label
                    htmlFor="video-upload"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 cursor-pointer transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Upload Videos
                  </label>
                  {selectedVideoFolder && (
                    <button
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors"
                      onClick={() => {
                        const num = countFilesInFolder(videoFiles, selectedVideoFolder || '');
                        setFolderDeleteTarget({ kind: 'videos', path: selectedVideoFolder || '' });
                        setFolderDeleteCount(num);
                      }}
                      title="Delete this folder and its contents"
                    >
                      Delete Folder
                    </button>
                  )}
                  </div>
                </div>
              </div>

              {/* Create Folder Modal */}
              {showCreateVideoFolder && (
                <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={newVideoFolderName}
                        onChange={(e) => setNewVideoFolderName(e.target.value)}
                        placeholder="Enter folder name..."
                        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                        onKeyPress={(e) => e.key === 'Enter' && handleVideoFolderCreate()}
                        autoFocus
                      />
                    </div>
                    <button
                      onClick={handleVideoFolderCreate}
                      disabled={!newVideoFolderName.trim()}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Create
                    </button>
                    <button
                      onClick={handleVideoFolderCancel}
                      className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Video Folders (derived from virtual paths) */}
                <div className="mb-6">
                {renderBreadcrumb(
                  selectedVideoFolder || '',
                  (next) => setSelectedVideoFolder(next || ''),
                  'Videos'
                )}
                {getSubfoldersFromFiles(videoFiles, selectedVideoFolder || '').length > 0 && (
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Folders</h4>
                  </div>
                )}
                {getSubfoldersFromFiles(videoFiles, selectedVideoFolder || '').length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {getSubfoldersFromFiles(videoFiles, selectedVideoFolder || '').map((folder) => (
                      <div
                        key={folder.id}
                        className="group relative bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                        onClick={() => setSelectedVideoFolder(folder.id)}
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center mb-2">
                            <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate w-full">
                            {folder.name}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">&nbsp;</span>
                        </div>
                      </div>
                    ))}
                    {isGDriveConnected && gdriveParentFoldersByCategory.videos.map((gf) => (
                      <div key={`gdrive:videos:${gf.id}`} className="group relative bg-white dark:bg-gray-700 border border-blue-200 dark:border-blue-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer transition-colors" onClick={() => openGDriveFolderById(gf.id, gf.name)}>
                        <div className="absolute top-2 right-2">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true"><path d="M7.5 3L2 12l3.5 6L11 9 7.5 3z" fill="#34a853"/><path d="M16.5 3H7.5L11 9h9l-3.5-6z" fill="#fbbc05"/><path d="M22 12H11l-5.5 6H18.5L22 12z" fill="#4285f4"/></svg>
                        </div>
                        <div className="flex flex-col items-center text-center">
                          <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center mb-2">
                            <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" /></svg>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate w-full">{gf.name}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Google Drive</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                </div>

              {/* Video Drag & Drop Upload Area */}
              <div 
                className={`mb-6 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  videoDragActive 
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' 
                    : 'border-gray-300 dark:border-gray-600 hover:border-indigo-400 dark:hover:border-indigo-500'
                }`}
                onDrop={handleVideoDrop}
                onDragOver={handleVideoDragOver}
                onDragLeave={handleVideoDragLeave}
              >
                <div className="flex flex-col items-center">
                  <svg className={`w-8 h-8 mb-2 ${
                    videoDragActive ? 'text-indigo-500' : 'text-gray-400 dark:text-gray-500'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <p className={`text-sm mb-2 ${
                    videoDragActive 
                      ? 'text-indigo-700 dark:text-indigo-300' 
                      : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    {videoDragActive ? 'Drop video files here!' : (
                      <>
                        Drag and drop video files here, or{' '}
                        <label htmlFor="video-upload" className="text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer">
                          browse files
                        </label>
                      </>
                    )}
                  </p>
                  {!videoDragActive && (
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      Supports MP4, MOV, AVI, MKV, WMV, FLV, WebM, M4V
                    </p>
                  )}
                </div>
              </div>
              {/* Video Gallery */}
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {selectedVideoFolder ? 'Folder Videos' : 'All Videos'}
                </h4>
                  {getFilesForPath(videoFiles, selectedVideoFolder || '').length > 0 && (
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          checked={(function(){const list=getFilesForPath(videoFiles, selectedVideoFolder||'');return list.length>0&&list.every(v=>selectedVideoKeys.has(v.file_key));})()}
                          onChange={() => { const list=getFilesForPath(videoFiles, selectedVideoFolder||''); if (list.length>0 && list.every(v=>selectedVideoKeys.has(v.file_key))) setSelectedVideoKeys(new Set()); else setSelectedVideoKeys(new Set(list.map(v=>v.file_key))); }}
                        />
                        Select All
                      </label>
                      {selectedVideoKeys.size > 0 && (
                        <button
                          onClick={() => setShowVideoBulkDeleteConfirm(true)}
                          disabled={videoBulkDeleting}
                          className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                          title="Delete selected videos"
                        >
                          {videoBulkDeleting ? 'Deleting...' : `Delete (${selectedVideoKeys.size})`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                
                {getFilesForPath(videoFiles, selectedVideoFolder || '').length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No videos yet</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      Upload your first videos to start your video collection
                    </p>
                    <label
                      htmlFor="video-upload"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 cursor-pointer transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      Upload Videos
                    </label>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {getFilesForPath(videoFiles, selectedVideoFolder || '').map((video, index) => {
                      const checked = selectedVideoKeys.has(video.file_key);
                      return (
                        <div key={video.file_key || index} className={`relative group rounded-lg overflow-hidden ${checked ? 'ring-2 ring-indigo-500' : ''}`}>
                          <LazyVideoThumb
                            video={video}
                              onClick={(e) => {
                              if (e && e.shiftKey) {
                                e.preventDefault();
                                const list = getFilesForPath(videoFiles, selectedVideoFolder || '');
                                const willSelect = !selectedVideoKeys.has(video.file_key);
                                setSelectedVideoKeys(prev => {
                                  const ns = new Set(prev);
                                  if (lastSelectedVideoIndex !== null && list.length > 0) {
                                    const start = Math.min(lastSelectedVideoIndex, index);
                                    const end = Math.max(lastSelectedVideoIndex, index);
                                    for (let i = start; i <= end; i++) { const key=list[i]?.file_key; if (!key) continue; if (willSelect) ns.add(key); else ns.delete(key); }
                                  } else { if (willSelect) ns.add(video.file_key); else ns.delete(video.file_key); }
                                  return ns;
                                });
                                setLastSelectedVideoIndex(index);
                                return;
                              }
                              handleVideoClick(video);
                            }}
                          />
                          <div className="absolute top-2 left-2">
                            <input type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded bg-white/90" checked={checked} onChange={(e)=>{handleVideoCheckboxChange(index, video, e);}} onClick={(e)=>e.stopPropagation()} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tags Tab */}
          {activeTab === 'tags' && (
            <div>
              {/* AI Tag Suggestions Section */}
              <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-green-900 dark:text-green-200">
                      AI Tag Suggestions
                    </h4>
                    <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                      Get smart tag recommendations based on your file content and patterns
                    </p>
                  </div>
                  <button
                    onClick={handleTagsAIOrganization}
                    disabled={isAnalyzingFiles}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isAnalyzingFiles && aiSuggestionType === 'tags' ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        AI Suggest Tags
                      </>
                    )}
                  </button>
                </div>
              </div>

              <TagManager 
                onTagsChange={(tags) => {
                  console.log('Tags updated:', tags.length);
                }}
                selectedFiles={uploadedFiles}
              />
            </div>
          )}
          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div>
              {/* Template Creation Section */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Sorting Templates
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Create custom templates to automatically organize your files with variables
                  </p>
                </div>
              </div>

              {/* AI Template Suggestions Section */}
              <div 
                className="mb-6 p-4 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border border-orange-200 dark:border-orange-800 rounded-lg cursor-pointer"
                onClick={() => {
                  if (!isAnalyzingFiles) {
                    handleTemplatesAIOrganization();
                  }
                }}
                title={isAnalyzingFiles ? 'Analyzing...' : 'Click to generate template suggestions'}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-orange-900 dark:text-orange-200">
                      AI Template Creation
                    </h4>
                    <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                      Generate automation templates based on your file organization patterns
                    </p>
                  </div>
                  {isAnalyzingFiles && aiSuggestionType === 'templates' && (
                    <div className="inline-flex items-center px-3 py-1 rounded-lg bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 text-xs font-medium">
                      <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Analyzing...
                    </div>
                  )}
                </div>
              </div>
              {/* Custom Templates List */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Your Custom Templates</h4>
                {customTemplates.length === 0 ? (
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-300">
                    You haven't created any templates yet.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {customTemplates.map((template) => (
                      <div key={template.id} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                                {template.name}
                              </h5>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                template.enabled 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                              }`}>
                                {template.enabled ? 'Active' : 'Inactive'}
                              </span>
                              <span className="text-xs text-gray-500">
                                Applied {template.appliedCount || 0} times
                              </span>
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {template.description || 'No description'}
                            </p>
                            <div className="mt-2">
                              <p className="text-xs text-gray-700 dark:text-gray-300 font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                                {template.directoryPath}
                              </p>
                            </div>
                            {template.variables.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Variables:</p>
                                <div className="flex flex-wrap gap-1">
                                  {template.variables.map((variable, idx) => (
                                    <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                      {variable.name} ({variable.source})
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                setCustomTemplates(prev => prev.map(t => 
                                  t.id === template.id ? { ...t, enabled: !t.enabled } : t
                                ));
                              }}
                              className={`text-xs px-3 py-1 rounded ${
                                template.enabled
                                  ? 'text-red-600 hover:text-red-800 dark:text-red-400'
                                  : 'text-green-600 hover:text-green-800 dark:text-green-400'
                              }`}
                            >
                              {template.enabled ? 'Disable' : 'Enable'}
                            </button>
                            <button
                              onClick={() => {
                                setCustomTemplates(prev => prev.filter(t => t.id !== template.id));
                                setAiMessages(prev => [...prev, {
                                  type: 'ai',
                                  message: `🗑️ Deleted template "${template.name}"`
                                }]);
                              }}
                              className="text-xs text-red-600 hover:text-red-800 dark:text-red-400"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <TemplateManager 
                customTemplates={customTemplates}
                onTemplatesChange={(templates) => {
                  console.log('Templates updated:', templates.length);
                }}
                onApplyTemplate={applyTemplateToFile}
              />
            </div>
          )}

          {/* AI Insights Tab */}
          {activeTab === 'insights' && (
            <div>
              <OrganizationInsights />
            </div>
          )}

          {/* AI Assistant Tab */}
          {activeTab === 'assistant' && (
            <div>
              {/* Chat Area */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6 h-96 overflow-y-auto" id="chat-container">
                <div className="space-y-4">
                  {aiMessages.map((msg, index) => (
                    <div key={index} className={`flex items-start space-x-3 ${msg.type === 'user' ? 'justify-end' : ''}`}>
                      {msg.type === 'ai' && (
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-robot text-white text-sm"></i>
                        </div>
                      )}
                      <div className={`rounded-lg p-4 ${
                        msg.type === 'ai' 
                          ? 'bg-white dark:bg-gray-600 max-w-4xl' 
                          : 'bg-blue-500 max-w-md'
                      }`}>
                        {msg.type === 'ai' ? (
                          <FormattedAIMessage message={msg.message} />
                        ) : (
                          <p className="text-sm text-white">
                            {msg.message}
                          </p>
                        )}
                      </div>
                      {msg.type === 'user' && (
                        <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-user text-white text-sm"></i>
                        </div>
                      )}
                    </div>
                  ))}
                  {aiLoading && (
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      </div>
                      <div className="bg-white dark:bg-gray-600 rounded-lg p-3 max-w-md">
                        <p className="text-sm text-gray-700 dark:text-gray-300">Thinking...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Input Area */}
              <div className="flex space-x-4 mb-6">
                <div className="flex-1">
                  <textarea 
                    className="appearance-none w-full p-3 text-sm font-semibold leading-none bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg outline-none text-gray-900 dark:text-gray-100 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                    rows={3} 
                    placeholder="Ask me anything about your files, tags, templates, or organization..."
                    value={aiQuestion}
                    onChange={(e) => setAiQuestion(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAiSubmit();
                      }
                    }}
                    disabled={aiLoading}
                  />
                </div>
                <div className="flex flex-col space-y-2">
                  <button 
                    onClick={handleAiSubmit}
                    disabled={aiLoading || !aiQuestion.trim()}
                    className="inline-block px-6 py-2 text-sm font-medium leading-normal bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition duration-200"
                  >
                    {aiLoading ? 'Sending...' : 'Send'}
                  </button>
                  <button 
                    onClick={clearAiChat}
                    className="inline-block px-6 py-2 text-sm font-medium leading-normal bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 rounded-lg transition duration-200"
                  >
                    Clear
                  </button>
                </div>
              </div>
              {/* Quick Actions */}
              <div>
                <h6 className="mb-3 font-semibold text-gray-900 dark:text-white">Quick Actions</h6>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  <button 
                    onClick={() => handleAiQuickAction('Suggest tags for my untagged files')}
                    disabled={aiLoading}
                    className="flex items-center p-3 bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Suggest tags
                  </button>
                  <button 
                    onClick={() => handleAiQuickAction('Create a sorting template for my receipts')}
                    disabled={aiLoading}
                    className="flex items-center p-3 bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Create template
                  </button>
                  <button 
                    onClick={() => handleAiQuickAction('Show me my organization score and recommendations')}
                    disabled={aiLoading}
                    className="flex items-center p-3 bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Organization tips
                  </button>
                  <button 
                    onClick={() => handleAiQuickAction('Find files tagged as expenses from last month')}
                    disabled={aiLoading}
                    className="flex items-center p-3 bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Search by tags
                  </button>
                  <button 
                    onClick={() => handleDuplicateCleanup()}
                    disabled={aiLoading || isDuplicateAnalysis}
                    className="flex items-center p-3 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Clean up duplicates
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
    </div>
  </div>
</div>

        {/* AI Suggestions Modal */}
        {showAISuggestionsModal && aiSuggestions && (
          <AISuggestionsModal
            suggestions={aiSuggestions}
            type={aiSuggestionType}
            onApply={handleApplyAISuggestions}
            onDismiss={handleDismissAISuggestions}
          />
        )}

        {/* Share Modal (unified, lightweight) */}
        {sharingFile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <div className="mb-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Share File</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{sharingFile.filename}</p>
              </div>
              <ShareForm
                file={sharingFile}
                onClose={() => setSharingFile(null)}
              />
            </div>
          </div>
        )}
        {/* Folder Delete Confirmation (all sections) */}
        {folderDeleteTarget && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Delete Folder</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Are you sure you want to delete this folder{folderDeleteTarget.path ? ` ("${folderDeleteTarget.path}")` : ''}? This will permanently delete {folderDeleteCount} item(s) inside it.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setFolderDeleteTarget(null)}
                  disabled={isDeletingFolder}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!folderDeleteTarget) return;
                    setIsDeletingFolder(true);
                    try {
                      const { bulkDeleteFiles } = await import('../../services/api');
                      let files = [];
                      if (folderDeleteTarget.kind === 'photos') files = getFilesForPath(photoFiles, folderDeleteTarget.path, 'photos');
                      else if (folderDeleteTarget.kind === 'audio') files = getFilesForPath(audioFiles, folderDeleteTarget.path || '');
                      else if (folderDeleteTarget.kind === 'videos') files = getFilesForPath(videoFiles, folderDeleteTarget.path || '');
                      else files = getFilesForPath(uploadedFiles, folderDeleteTarget.path || '');
                      const keys = files.map(f => f.file_key).filter(Boolean);
                      if (keys.length > 0) {
                        try { await bulkDeleteFiles(keys); } catch (_) {
                          const { deleteFile } = await import('../../services/api');
                          await Promise.all(keys.map(k => deleteFile(k).catch(() => null)));
                        }
                      }
                      // Remove from local state
                      setUploadedFiles(prev => prev.filter(f => !keys.includes(f.file_key)));
                      setPhotoFiles(prev => prev.filter(f => !keys.includes(f.file_key)));
                      setAudioFiles(prev => prev.filter(f => !keys.includes(f.file_key)));
                      setVideoFiles(prev => prev.filter(f => !keys.includes(f.file_key)));
                      setFolderDeleteTarget(null);
                    } finally {
                      setIsDeletingFolder(false);
                    }
                  }}
                  disabled={isDeletingFolder}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                >
                  {isDeletingFolder ? 'Deleting...' : 'Delete Folder'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Duplicates Cleanup Modal */}
        {showDuplicatesModal && duplicateGroups.length > 0 && (
          <DuplicatesModal
            duplicateGroups={duplicateGroups}
            onDelete={handleBulkDeleteDuplicates}
            onDismiss={() => setShowDuplicatesModal(false)}
          />
        )}

        {/* Upload Options Modal */}
        {showUploadOptionsModal && (
          <UploadOptionsModal
            files={pendingUploadFiles}
            tags={availableTags}
            templates={availableTemplates}
            uploadKind={lastUploadKind}
            selectedTagIds={selectedTagIds}
            onToggleTag={(tagId, checked) => {
              setSelectedTagIds(prev => checked ? [...prev, tagId] : prev.filter(id => id !== tagId));
            }}
            selectedTemplateId={selectedTemplateId}
            onSelectTemplate={setSelectedTemplateId}
            newTagName={newTagName}
            setNewTagName={setNewTagName}
            onCreateTag={handleCreateTagInline}
            creatingTag={isSavingNewTag}
            newTemplateName={newTemplateName}
            setNewTemplateName={setNewTemplateName}
            newTemplatePath={newTemplatePath}
            setNewTemplatePath={setNewTemplatePath}
            onCreateTemplate={handleCreateTemplateInline}
            creatingTemplate={isSavingNewTemplate}
            onCancel={closeUploadOptionsModal}
            onConfirm={handleConfirmUploadOptions}
            uploading={isModalUploading}
            progress={modalUploadProgress}
          />
        )}
        {/* Photo Preview Modal */}
        {showPhotoModal && selectedPhoto && (
          <PhotoModal
            photo={selectedPhoto}
            onDownload={handlePhotoDownload}
            onDismiss={() => {
              setShowPhotoModal(false);
              setSelectedPhoto(null);
            }}
            onQuickEdit={(photo) => {
              setShowPhotoModal(false);
              setSelectedPhoto(null);
              setQuickEditTarget(photo);
            }}
            onShare={(photo) => {
              // Keep preview open on mobile; open local share modal
              setSharingFile(photo);
            }}
            onPrev={() => {
              const list = photoFiles || [];
              if (!selectedPhoto || list.length === 0) return;
              const idx = Math.max(0, list.findIndex(p => p.file_key === selectedPhoto.file_key));
              const prevIdx = (idx - 1 + list.length) % list.length;
              setSelectedPhoto(list[prevIdx]);
            }}
            onNext={() => {
              const list = photoFiles || [];
              if (!selectedPhoto || list.length === 0) return;
              const idx = Math.max(0, list.findIndex(p => p.file_key === selectedPhoto.file_key));
              const nextIdx = (idx + 1) % list.length;
              setSelectedPhoto(list[nextIdx]);
            }}
          />
        )}

        {/* Photo Bulk Delete Confirmation */}
        {showPhotoBulkDeleteConfirm && selectedPhotoKeys.size > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Delete Selected Photos</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Are you sure you want to delete {selectedPhotoKeys.size} photo(s)? This action cannot be undone.</p>
              <div className="flex justify-end gap-3">
                <button onClick={handlePhotoBulkDeleteCancel} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md">Cancel</button>
                <button onClick={handlePhotoBulkDeleteConfirm} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md">Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* File Preview Modal */}
        {showFileModal && selectedFile && (
          <FilePreviewModal
            file={selectedFile}
            onDownload={handleFileDownload}
            onDismiss={() => {
              setShowFileModal(false);
              setSelectedFile(null);
            }}
            onQuickEdit={(file) => {
              setShowFileModal(false);
              setSelectedFile(null);
              setQuickEditTarget(file);
            }}
            onShare={(file) => {
              setSharingFile(file);
            }}
            onPrev={() => {
              const list = uploadedFiles || [];
              if (!selectedFile || list.length === 0) return;
              const idx = Math.max(0, list.findIndex(f => f.file_key === selectedFile.file_key));
              const prevIdx = (idx - 1 + list.length) % list.length;
              setSelectedFile(list[prevIdx]);
            }}
            onNext={() => {
              const list = uploadedFiles || [];
              if (!selectedFile || list.length === 0) return;
              const idx = Math.max(0, list.findIndex(f => f.file_key === selectedFile.file_key));
              const nextIdx = (idx + 1) % list.length;
              setSelectedFile(list[nextIdx]);
            }}
          />
        )}

        {/* Audio Preview Modal */}
        {showAudioModal && selectedAudio && (
          <FilePreviewModal
            file={selectedAudio}
            onDownload={handleFileDownload}
            onDismiss={() => {
              setShowAudioModal(false);
              setSelectedAudio(null);
            }}
            onQuickEdit={(file) => {
              setShowAudioModal(false);
              setSelectedAudio(null);
              setQuickEditTarget(file);
            }}
            onShare={(file) => {
              setSharingFile(file);
            }}
            onPrev={() => {
              const list = audioFiles || [];
              if (!selectedAudio || list.length === 0) return;
              const idx = Math.max(0, list.findIndex(a => a.file_key === selectedAudio.file_key));
              const prevIdx = (idx - 1 + list.length) % list.length;
              setSelectedAudio(list[prevIdx]);
            }}
            onNext={() => {
              const list = audioFiles || [];
              if (!selectedAudio || list.length === 0) return;
              const idx = Math.max(0, list.findIndex(a => a.file_key === selectedAudio.file_key));
              const nextIdx = (idx + 1) % list.length;
              setSelectedAudio(list[nextIdx]);
            }}
          />
        )}

        {/* Audio Bulk Delete Confirmation */}
        {showAudioBulkDeleteConfirm && selectedAudioKeys.size > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Delete Selected Audio</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Are you sure you want to delete {selectedAudioKeys.size} track(s)? This action cannot be undone.</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowAudioBulkDeleteConfirm(false)} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md">Cancel</button>
                <button onClick={handleAudioBulkDeleteConfirm} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md">Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* Video Preview Modal */}
        {showVideoModal && selectedVideo && (
          <FilePreviewModal
            file={selectedVideo}
            onDownload={handleFileDownload}
            onDismiss={() => {
              setShowVideoModal(false);
              setSelectedVideo(null);
            }}
            onQuickEdit={(file) => {
              setShowVideoModal(false);
              setSelectedVideo(null);
              setQuickEditTarget(file);
            }}
            onShare={(file) => {
              setSharingFile(file);
            }}
            onPrev={() => {
              const list = videoFiles || [];
              if (!selectedVideo || list.length === 0) return;
              const idx = Math.max(0, list.findIndex(v => v.file_key === selectedVideo.file_key));
              const prevIdx = (idx - 1 + list.length) % list.length;
              setSelectedVideo(list[prevIdx]);
            }}
            onNext={() => {
              const list = videoFiles || [];
              if (!selectedVideo || list.length === 0) return;
              const idx = Math.max(0, list.findIndex(v => v.file_key === selectedVideo.file_key));
              const nextIdx = (idx + 1) % list.length;
              setSelectedVideo(list[nextIdx]);
            }}
          />
        )}

        {/* Global Quick Edit Modal */}
        {quickEditTarget && (
          <QuickEditModal
            file={quickEditTarget}
            onClose={() => setQuickEditTarget(null)}
            onSaved={(updated) => {
              setUploadedFiles(prev => prev.map(f => (f.upload_id === updated.upload_id ? { ...f, ...updated } : f)));
              setPhotoFiles(prev => prev.map(f => (f.upload_id === updated.upload_id ? { ...f, ...updated } : f)));
              setAudioFiles(prev => prev.map(f => (f.upload_id === updated.upload_id ? { ...f, ...updated } : f)));
              setVideoFiles(prev => prev.map(f => (f.upload_id === updated.upload_id ? { ...f, ...updated } : f)));
            }}
          />
        )}
        {/* Video Bulk Delete Confirmation */}
        {showVideoBulkDeleteConfirm && selectedVideoKeys.size > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Delete Selected Videos</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Are you sure you want to delete {selectedVideoKeys.size} video(s)? This action cannot be undone.</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowVideoBulkDeleteConfirm(false)} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md">Cancel</button>
                <button onClick={handleVideoBulkDeleteConfirm} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md">Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* Template Creation Modal */}
        {showCreateTemplate && (
          <TemplateCreationModal
            template={newTemplate}
            onTemplateChange={setNewTemplate}
            onPathChange={handleTemplatePathChange}
            onSave={handleSaveTemplate}
            onCancel={handleCancelTemplate}
          />
        )}
        {/* Template Variable Resolution Modal */}
        {showTemplateVariableModal && pendingTemplateApplication && (
          <TemplateVariableModal
            file={pendingTemplateApplication.file}
            template={pendingTemplateApplication.template}
            missingVariables={pendingTemplateApplication.missingVariables}
            onResolve={handleResolveTemplateVariables}
            onCancel={() => {
              setShowTemplateVariableModal(false);
              setPendingTemplateApplication(null);
            }}
          />
        )}

            </>
        </React.Fragment>
    );
}
// Lightweight share form used in Photos/File previews
function ShareForm({ file, onClose }) {
  const [shareEmail, setShareEmail] = React.useState('');
  const [shareMessage, setShareMessage] = React.useState('');
  const [shareExpires, setShareExpires] = React.useState(10080);
  const [shareDownloads, setShareDownloads] = React.useState(1);
  const [sharePasscode, setSharePasscode] = React.useState('');
  const [shareResultUrl, setShareResultUrl] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [sendBusy, setSendBusy] = React.useState(false);

  const createLink = async () => {
    if (!file) return;
    try {
      setBusy(true);
      const res = await issueShareLink({
        fileKey: file.file_key,
        recipientEmail: shareEmail || null,
        recipientName: null,
        message: shareMessage || null,
        expiresMinutes: Number(shareExpires) || 10080,
        maxDownloads: Number(shareDownloads) || 1,
        passcode: sharePasscode || null,
      });
      setShareResultUrl(res.share_url);
    } catch (e) {
      alert(`Share failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async () => {
    if (!shareResultUrl) { alert('Create the share link first.'); return; }
    if (!shareEmail) { alert('Enter recipient email.'); return; }
    const tokenParam = new URL(shareResultUrl).searchParams.get('token');
    if (!tokenParam) { alert('Invalid share URL.'); return; }
    try {
      setSendBusy(true);
      await sendShareEmail({ token: tokenParam, recipientEmail: shareEmail, recipientName: null, message: shareMessage || null });
      alert(`Email sent to ${shareEmail}`);
    } catch (e) {
      alert(`Failed to send email: ${e.message}`);
    } finally {
      setSendBusy(false);
    }
  };

  return (
    <div>
      <div className="space-y-3">
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Recipient Email (optional)</label>
          <input type="email" value={shareEmail} onChange={(e)=>setShareEmail(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" placeholder="name@example.com"/>
        </div>
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Message (optional)</label>
          <textarea value={shareMessage} onChange={(e)=>setShareMessage(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" rows={3} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Expires (min)</label>
            <input type="number" min={5} value={shareExpires} onChange={(e)=>setShareExpires(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Max downloads</label>
            <input type="number" min={1} value={shareDownloads} onChange={(e)=>setShareDownloads(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Passcode (opt)</label>
            <input type="text" value={sharePasscode} onChange={(e)=>setSharePasscode(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
          </div>
        </div>

        {shareResultUrl && (
          <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded">
            <label className="block text-xs text-gray-500 dark:text-gray-300">Share URL</label>
            <div className="flex items-center space-x-2 mt-1">
              <input readOnly value={shareResultUrl} className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-white text-xs" />
              <button onClick={()=>{ navigator.clipboard.writeText(shareResultUrl); }} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">Copy</button>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end space-x-2 mt-4">
        <button onClick={onClose} className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded">Close</button>
        <button onClick={createLink} disabled={busy} className="px-3 py-2 text-sm bg-indigo-600 text-white rounded disabled:opacity-50">{busy ? 'Sharing…' : 'Create Link'}</button>
        <button onClick={sendEmail} disabled={sendBusy} className="px-3 py-2 text-sm bg-blue-600 text-white rounded disabled:opacity-50">{sendBusy ? 'Sending…' : 'Send Email'}</button>
      </div>
    </div>
  );
}
// AI Suggestions Modal Component
const AISuggestionsModal = ({ suggestions, type, onApply, onDismiss }) => {
  const [selectedSuggestions, setSelectedSuggestions] = useState({
    folders: suggestions.folders || [],
    renames: suggestions.renames || [],
    tags: suggestions.tags || [],
    suggestedTags: suggestions.suggestedTags || [],
    suggestedTemplates: suggestions.suggestedTemplates || [],
    suggestedFolders: suggestions.suggestedFolders || []
  });

  const [dismissedSections, setDismissedSections] = useState(new Set());

  const getModalTitle = () => {
    switch (type) {
      case 'upload': return 'AI Organization Suggestions';
      case 'batch': return 'Complete File Reorganization';
      case 'tags': return 'AI Tag Recommendations';
      case 'templates': return 'AI Template Suggestions';
      default: return 'AI Suggestions';
    }
  };

  const getModalDescription = () => {
    switch (type) {
      case 'upload': return 'Based on your recent uploads, here are some organization suggestions:';
      case 'batch': return `AI analyzed ${suggestions.allFiles?.length || 0} files and found these optimization opportunities:`;
      case 'tags': return 'Smart tag suggestions to improve your file organization:';
      case 'templates': return 'Automation templates to streamline your workflow:';
      default: return 'AI has analyzed your files and has suggestions:';
    }
  };

  const handleApplyAll = () => {
    const appliedCount = (selectedSuggestions.folders?.length || 0) + 
                        (selectedSuggestions.renames?.length || 0) + 
                        (selectedSuggestions.tags?.length || 0) +
                        (selectedSuggestions.suggestedFolders?.length || 0) +
                        (selectedSuggestions.suggestedTemplates?.length || 0);
    onApply({ ...selectedSuggestions, appliedCount });
  };

  const recordFeedback = async (suggestionType, action, payload) => {
    try {
      await sendSuggestionFeedback(suggestionType, action, payload || {});
    } catch (e) {
      // non-fatal
      console.warn('Failed to record suggestion feedback', e);
    }
  };
  const removeFromSelection = (section, index) => {
    setSelectedSuggestions(prev => {
      const updated = { ...prev };
      updated[section] = [...(updated[section] || [])];
      updated[section].splice(index, 1);
      return updated;
    });
  };

  const handleDismissSection = (sectionName) => {
    setDismissedSections(prev => new Set([...prev, sectionName]));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {getModalTitle()}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {getModalDescription()}
              </p>
            </div>
            <button
              onClick={onDismiss}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Folder Suggestions */}
          {suggestions.folders && suggestions.folders.length > 0 && !dismissedSections.has('folders') && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-gray-900 dark:text-white flex items-center">
                  <svg className="w-4 h-4 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                  </svg>
                  Suggested Folder Structure
                </h4>
                <button
                  onClick={() => handleDismissSection('folders')}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Dismiss this suggestion"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-3">
                {suggestions.folders.map((folder, index) => (
                  <div key={index} className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded inline-block">
                          📁 {folder.name}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                          {folder.reason}
                        </p>
                        {folder.files && folder.files.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-gray-600 dark:text-gray-300 mb-1 font-medium">Files to organize:</p>
                            <div className="flex flex-wrap gap-1">
                              {folder.files.slice(0, 3).map((file, fileIndex) => (
                                <span key={fileIndex} className="inline-block px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded">
                                  {file}
                                </span>
                              ))}
                              {folder.files.length > 3 && (
                                <span className="text-xs text-gray-500">+{folder.files.length - 3} more</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={async () => {
                            await recordFeedback('folder', 'accept', folder);
                            removeFromSelection('folders', index);
                          }}
                        >
                          Apply
                        </button>
                        <button
                          className="px-3 py-1 text-xs rounded bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500"
                          onClick={async () => {
                            await recordFeedback('folder', 'reject', folder);
                            removeFromSelection('folders', index);
                          }}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suggested Folders (for Templates) */}
          {suggestions.suggestedFolders && suggestions.suggestedFolders.length > 0 && !dismissedSections.has('suggestedFolders') && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-gray-900 dark:text-white flex items-center">
                  <svg className="w-4 h-4 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                  </svg>
                  Recommended Folder Structure
                </h4>
                <button
                  onClick={() => handleDismissSection('suggestedFolders')}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Dismiss this suggestion"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-3">
                {suggestions.suggestedFolders.map((folder, index) => (
                  <div key={index} className="p-4 bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded inline-block">
                          📁 {folder.name}
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 font-medium">
                          {folder.description}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {folder.reason}
                        </p>
                        {folder.files && folder.files.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-gray-600 dark:text-gray-300 mb-1 font-medium">Applicable files:</p>
                            <div className="flex flex-wrap gap-1">
                              {folder.files.slice(0, 3).map((file, fileIndex) => (
                                <span key={fileIndex} className="inline-block px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs rounded">
                                  {file}
                                </span>
                              ))}
                              {folder.files.length > 3 && (
                                <span className="text-xs text-gray-500">+{folder.files.length - 3} more</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Rename Suggestions */}
          {suggestions.renames && suggestions.renames.length > 0 && !dismissedSections.has('renames') && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-gray-900 dark:text-white flex items-center">
                  <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Suggested Renames
                </h4>
                <button
                  onClick={() => handleDismissSection('renames')}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Dismiss this suggestion"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-3">
                {suggestions.renames.map((rename, index) => (
                  <div key={index} className={`p-4 rounded-lg border ${rename.contentExtracted ? 'bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border-green-200 dark:border-green-800' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {rename.contentExtracted && (
                          <div className="flex items-center mb-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              AI Content Extracted
                            </span>
                          </div>
                        )}
                        <div className="text-sm mb-2">
                          <span className="text-gray-500 dark:text-gray-400 font-mono">{rename.original}</span>
                          <span className="mx-2 text-gray-400">→</span>
                          <span className="font-medium text-gray-900 dark:text-white font-mono">{rename.suggested}</span>
                        </div>
                        {rename.targetFolder && (
                          <div className="text-xs text-blue-600 dark:text-blue-400 mb-2 font-mono">
                            📁 Target: {rename.targetFolder}
                          </div>
                        )}
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {rename.reason}
                        </p>
                        {rename.extractedData && (
                          <div className="mt-3 p-2 bg-white dark:bg-gray-800 rounded border">
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Extracted Data:</p>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              {Object.entries(rename.extractedData).map(([key, value]) => (
                                <div key={key} className="flex justify-between">
                                  <span className="text-gray-500 capitalize">{key}:</span>
                                  <span className="text-gray-900 dark:text-white font-medium">{value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          className="px-3 py-1 text-xs rounded bg-green-600 hover:bg-green-700 text-white"
                          onClick={async () => {
                            await recordFeedback('rename', 'accept', rename);
                            removeFromSelection('renames', index);
                          }}
                        >
                          Apply
                        </button>
                        <button
                          className="px-3 py-1 text-xs rounded bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500"
                          onClick={async () => {
                            await recordFeedback('rename', 'reject', rename);
                            removeFromSelection('renames', index);
                          }}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Tag Suggestions */}
          {suggestions.tags && suggestions.tags.length > 0 && !dismissedSections.has('tags') && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-gray-900 dark:text-white flex items-center">
                  <svg className="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Suggested Tags
                </h4>
                <button
                  onClick={() => handleDismissSection('tags')}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Dismiss this suggestion"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-3">
                {suggestions.tags.map((tag, index) => (
                  <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {tag.file}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {tag.suggestedTags.map((tagName, tagIndex) => (
                        <span key={tagIndex} className="inline-block px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs rounded">
                          {tagName}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {tag.reason}
                    </p>
                    <div className="flex items-center space-x-2 mt-3">
                      <button
                        className="px-3 py-1 text-xs rounded bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={async () => {
                          await recordFeedback('tag', 'accept', tag);
                          removeFromSelection('tags', index);
                        }}
                      >
                        Apply
                      </button>
                      <button
                        className="px-3 py-1 text-xs rounded bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500"
                        onClick={async () => {
                          await recordFeedback('tag', 'reject', tag);
                          removeFromSelection('tags', index);
                        }}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Special content for Tags and Templates */}
          {type === 'tags' && suggestions.suggestedTags && (
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">New Tag Categories</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {suggestions.suggestedTags.map((tag, index) => (
                  <div key={index} className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                    <h5 className="font-medium text-gray-900 dark:text-white">{tag.name}</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{tag.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {type === 'templates' && suggestions.suggestedTemplates && !dismissedSections.has('suggestedTemplates') && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-gray-900 dark:text-white">Automation Templates</h4>
                <button
                  onClick={() => handleDismissSection('suggestedTemplates')}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Dismiss this suggestion"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                {suggestions.suggestedTemplates.map((template, index) => (
                  <div key={index} className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-900 dark:text-white flex items-center">
                          <svg className="w-4 h-4 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {template.name}
                        </h5>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{template.description}</p>
                        
                        {template.folderStructure && (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Folder Structure:</p>
                            <p className="text-xs font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-800 dark:text-gray-200">
                              📁 {template.folderStructure}
                            </p>
                          </div>
                        )}
                        
                        <div className="mt-3">
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Automation Rules:</p>
                          <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                            {template.rules.map((rule, ruleIndex) => (
                              <li key={ruleIndex} className="flex items-start">
                                <span className="w-1 h-1 bg-orange-400 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                {rule}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        {template.automation && (
                          <div className="mt-3">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              {template.automation}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          className="px-3 py-1 text-xs rounded bg-orange-600 hover:bg-orange-700 text-white"
                          onClick={async () => {
                            await recordFeedback('template', 'accept', template);
                            removeFromSelection('suggestedTemplates', index);
                          }}
                        >
                          Apply
                        </button>
                        <button
                          className="px-3 py-1 text-xs rounded bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500"
                          onClick={async () => {
                            await recordFeedback('template', 'reject', template);
                            removeFromSelection('suggestedTemplates', index);
                          }}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Content Analysis Results */}
          {suggestions.contentAnalysis && suggestions.contentAnalysis.length > 0 && !dismissedSections.has('contentAnalysis') && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-gray-900 dark:text-white flex items-center">
                  <svg className="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  AI Content Analysis Results
                </h4>
                <button
                  onClick={() => handleDismissSection('contentAnalysis')}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Dismiss this suggestion"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-3">
                {suggestions.contentAnalysis.map((analysis, index) => (
                  <div key={index} className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white mr-2">{analysis.originalFilename}</span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                            {(analysis.confidence * 100).toFixed(0)}% confidence
                          </span>
                        </div>
                        <p className="text-sm text-purple-700 dark:text-purple-300 mb-2 capitalize">
                          Document Type: <span className="font-medium">{analysis.documentType.replace('_', ' ')}</span>
                        </p>
                        {analysis.ocrText && (
                          <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border">
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">OCR Extracted Text:</p>
                            <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono">
                              {analysis.ocrText}
                            </pre>
                          </div>
                        )}
                        {analysis.extractedData && Object.keys(analysis.extractedData).length > 0 && (
                          <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border">
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Structured Data:</p>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              {Object.entries(analysis.extractedData).map(([key, value]) => (
                                <div key={key} className="flex justify-between">
                                  <span className="text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1').toLowerCase()}:</span>
                                  <span className="text-gray-900 dark:text-white font-medium">
                                    {value instanceof Date ? value.toLocaleDateString() : value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Insights */}
          {suggestions.insights && suggestions.insights.length > 0 && !dismissedSections.has('insights') && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-gray-900 dark:text-white flex items-center">
                  <svg className="w-4 h-4 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  AI Insights
                </h4>
                <button
                  onClick={() => handleDismissSection('insights')}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Dismiss this suggestion"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-2">
                {suggestions.insights.map((insight, index) => (
                  <div key={index} className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">{insight}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex items-center justify-between">
          <button
            onClick={onDismiss}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
          >
            Skip for Now
          </button>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleApplyAll}
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
            >
              Apply All Suggestions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// Duplicates Cleanup Modal Component
const DuplicatesModal = ({ duplicateGroups, onDelete, onDismiss }) => {
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0, currentFile: '' });



  const handleFileSelect = (fileKey, isSelected) => {
    const newSelected = new Set(selectedFiles);
    if (isSelected) {
      newSelected.add(fileKey);
    } else {
      newSelected.delete(fileKey);
    }
    setSelectedFiles(newSelected);
  };

  const handleGroupSelect = (groupId, isSelected, groupFiles) => {
    const newSelectedGroups = new Set(selectedGroups);
    const newSelectedFiles = new Set(selectedFiles);
    
    if (isSelected) {
      newSelectedGroups.add(groupId);
      // Auto-select all files except the recommended one to keep
      const group = duplicateGroups.find(g => g.id === groupId);
      groupFiles.forEach(file => {
        if (file.file_key !== group.recommendKeep.file_key) {
          newSelectedFiles.add(file.file_key);
        }
      });
    } else {
      newSelectedGroups.delete(groupId);
      // Deselect all files in this group
      groupFiles.forEach(file => {
        newSelectedFiles.delete(file.file_key);
      });
    }
    
    setSelectedGroups(newSelectedGroups);
    setSelectedFiles(newSelectedFiles);
  };
  const handleDeleteSelected = async () => {
    const filesToDelete = duplicateGroups
      .flatMap(group => group.files)
      .filter(file => selectedFiles.has(file.file_key));
    
    if (filesToDelete.length === 0) return;

    setIsDeleting(true);
    setDeleteProgress({ current: 0, total: filesToDelete.length, currentFile: '' });

    try {
      // Delete files one by one to show progress
      let deletedCount = 0;
      const errors = [];

      for (const file of filesToDelete) {
        setDeleteProgress(prev => ({
          ...prev,
          current: deletedCount,
          currentFile: file.filename
        }));

        try {
          // Import deleteFile for single file deletion to show progress
          const { deleteFile } = await import('../../services/api');
          await deleteFile(file.file_key);
          deletedCount++;
          
          // Update progress
          setDeleteProgress(prev => ({
            ...prev,
            current: deletedCount
          }));

          // Small delay to make progress visible
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (error) {
          errors.push({
            file: file.filename,
            error: error.message
          });
        }
      }

      // Call the parent's onDelete with successful results
      onDelete(filesToDelete.slice(0, deletedCount), errors);
      
    } catch (error) {
      console.error('Delete operation failed:', error);
    } finally {
      setIsDeleting(false);
      setDeleteProgress({ current: 0, total: 0, currentFile: '' });
    }
  };

  const totalSavings = duplicateGroups
    .flatMap(group => group.files)
    .filter(file => selectedFiles.has(file.file_key))
    .reduce((sum, file) => sum + (file.size || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-red-500 to-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white">
                🗑️ Clean Up Duplicate Files
              </h3>
              <p className="text-red-100 text-sm mt-1">
                Found {duplicateGroups.length} group(s) of potential duplicates
              </p>
            </div>
            <button
              onClick={onDismiss}
              className="text-white hover:text-red-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          <div className="space-y-6">
            {duplicateGroups.map((group) => (
              <div key={group.id} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedGroups.has(group.id)}
                      onChange={(e) => handleGroupSelect(group.id, e.target.checked, group.files)}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {group.type}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {group.reason} • Potential savings: {formatFileSize(group.potentialSavings)}
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
                    {group.files.length} files
                  </span>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {group.files.map((file) => {
                    const isRecommendedKeep = file.file_key === group.recommendKeep.file_key;
                    return (
                      <div
                        key={file.file_key}
                        className={`px-4 py-3 flex items-center justify-between ${
                          isRecommendedKeep 
                            ? 'bg-green-50 dark:bg-green-900/20' 
                            : 'bg-white dark:bg-gray-800'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedFiles.has(file.file_key)}
                            onChange={(e) => handleFileSelect(file.file_key, e.target.checked)}
                            disabled={isRecommendedKeep}
                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded disabled:opacity-50"
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {file.filename}
                              </span>
                              {isRecommendedKeep && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                                  ✓ Keep (Latest)
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 space-x-2">
                              <span>{formatFileSize(file.size || 0)}</span>
                              <span>•</span>
                              <span>Modified: {new Date(file.last_modified).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        
                        {!isRecommendedKeep && (
                          <div className="text-right">
                            <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                              Can delete
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Progress Bar */}
        {isDeleting && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/20">
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-blue-900 dark:text-blue-100">
                  Deleting duplicate files...
                </span>
                <span className="text-blue-700 dark:text-blue-300">
                  {deleteProgress.current} of {deleteProgress.total}
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                  style={{
                    width: `${deleteProgress.total > 0 ? (deleteProgress.current / deleteProgress.total) * 100 : 0}%`
                  }}
                ></div>
              </div>
              
              {/* Current File */}
              {deleteProgress.currentFile && (
                <div className="flex items-center space-x-2 text-xs text-blue-800 dark:text-blue-200">
                  <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Deleting: {deleteProgress.currentFile}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {selectedFiles.size > 0 && (
              <span>
                Selected {selectedFiles.size} file(s) to delete • 
                Will free up {formatFileSize(totalSavings)}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onDismiss}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteSelected}
              disabled={selectedFiles.size === 0 || isDeleting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {isDeleting ? (
                <div className="flex items-center space-x-2">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Deleting...</span>
                </div>
              ) : (
                `Delete Selected (${selectedFiles.size})`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// Upload Options Modal Component
const UploadOptionsModal = ({
  files,
  tags,
  templates,
  selectedTagIds,
  onToggleTag,
  selectedTemplateId,
  onSelectTemplate,
  newTagName,
  setNewTagName,
  onCreateTag,
  creatingTag,
  newTemplateName,
  setNewTemplateName,
  newTemplatePath,
  setNewTemplatePath,
  onCreateTemplate,
  creatingTemplate,
  onCancel,
  onConfirm,
  uploading = false,
  progress = [],
  uploadKind,
}) => {
  const [explicit, setExplicit] = React.useState({
    person: '', event: '', age: '', merchant: '', date: '', amount: '', project: '', version: '', docType: ''
  });
  // New KV-row based explicit builder
  const parseTag = (t) => {
    const raw = (t.name || t.label || '').toString();
    const parts = raw.split(/:\s*/);
    if (parts.length >= 2) return { key: parts[0].trim(), value: parts.slice(1).join(':').trim() };
    return null;
  };
  const [localNewTags, setLocalNewTags] = React.useState([]);
  const allTags = React.useMemo(() => ([...(tags || []), ...localNewTags]), [tags, localNewTags]);
  const keyToValues = React.useMemo(() => {
    const map = new Map();
    (allTags || []).forEach(t => {
      const kv = parseTag(t); if (!kv) return;
      const list = map.get(kv.key) || [];
      if (!list.includes(kv.value)) list.push(kv.value);
      map.set(kv.key, list);
    });
    return map;
  }, [allTags]);
  const keyOptions = React.useMemo(() => Array.from(keyToValues.keys()), [keyToValues]);
  const [kvRows, setKvRows] = React.useState([{ key: '', value: '' }]);
  const [kvSelectedIds, setKvSelectedIds] = React.useState(new Set());
  const [newTagKey, setNewTagKey] = React.useState('');
  const [newTagValue, setNewTagValue] = React.useState('');
  // Free-text inline auto-complete state
  const [kvDraft, setKvDraft] = React.useState('');
  const [valueSuggestions, setValueSuggestions] = React.useState([]);
  const [showValueSuggestions, setShowValueSuggestions] = React.useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = React.useState(-1);

  const updateValueSuggestions = React.useCallback((input) => {
    const str = String(input || '');
    const m = str.match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.*)$/);
    if (!m) {
      setValueSuggestions([]);
      setShowValueSuggestions(false);
      setActiveSuggestionIndex(-1);
      return;
    }
    const key = (m[1] || '').trim().toLowerCase();
    const prefix = (m[2] || '').toString().trim().toLowerCase();
    const candidates = keyToValues.get(key) || [];
    let filtered = candidates;
    if (prefix) filtered = candidates.filter(v => String(v).toLowerCase().includes(prefix));
    filtered = filtered.slice(0, 8);
    setValueSuggestions(filtered);
    setShowValueSuggestions(filtered.length > 0);
    setActiveSuggestionIndex(filtered.length ? 0 : -1);
  }, [keyToValues]);

  // Auto-build target path from section + kv values
  React.useEffect(() => {
    const section = (uploadKind || 'files').toString().toLowerCase();
    const sectionLabel = section === 'photo' ? 'Photos' : section === 'audio' ? 'Audio' : section === 'video' ? 'Videos' : 'Files';
    const values = (kvRows || []).map(r => r.value).filter(Boolean);
    if (values.length > 0) {
      const path = `${sectionLabel}/${values.map(v => v.replace(/\s+/g, '_')).join('/')}/`;
      if (!newTemplatePath || newTemplatePath.startsWith('Files/') || newTemplatePath.startsWith('Photos/') || newTemplatePath.startsWith('Audio/') || newTemplatePath.startsWith('Videos/')) {
        setNewTemplatePath(path);
      }
    }
  }, [kvRows, uploadKind]);

  React.useEffect(() => {
    const desiredNames = (kvRows || [])
      .map(r => (r.key && r.value) ? `${r.key}: ${r.value}` : null)
      .filter(Boolean);
    const nameToId = new Map((allTags || []).map(t => {
      const n = (t.name || t.label || '').toString().trim().toLowerCase();
      return [n, t.id || t.tag_id];
    }));
    const desiredIds = new Set(desiredNames
      .map(n => nameToId.get(n.toLowerCase()))
      .filter(Boolean));
    desiredIds.forEach(id => {
      if (!selectedTagIds.includes(id)) onToggleTag(id, true);
    });
    kvSelectedIds.forEach(id => {
      if (!desiredIds.has(id)) onToggleTag(id, false);
    });
    setKvSelectedIds(desiredIds);
  }, [kvRows, allTags, selectedTagIds, onToggleTag]);
  React.useEffect(() => { window.__upload_explicit_values__ = explicit; return () => { delete window.__upload_explicit_values__; }; }, [explicit]);
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Upload Options</h3>
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Create Tags</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">New tags appear in Apply Tags below.</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newTagKey}
                onChange={(e) => setNewTagKey(e.target.value)}
                placeholder="Tag key (e.g., Name)"
                className="w-48 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              />
              <input
                type="text"
                value={newTagValue}
                onChange={(e) => setNewTagValue(e.target.value)}
                placeholder="Tag value (e.g., Lewis)"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              />
              <button
                onClick={async () => {
                  const key = newTagKey.trim();
                  const value = newTagValue.trim();
                  if (!key || !value) return;
                  const composed = `${key}: ${value}`;
                  setLocalNewTags(prev => [...prev, { id: `local-${Date.now()}`, name: composed }]);
                  try { if (onCreateTag) await onCreateTag(composed); } catch (_) {}
                  setNewTagKey('');
                  setNewTagValue('');
                }}
                disabled={creatingTag || !newTagKey.trim() || !newTagValue.trim()}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
              >
                {creatingTag ? 'Adding...' : 'Add Tag'}
              </button>
            </div>
            {/* Free-text Key: Value with inline auto-complete */}
            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Quick add (Key: Value)</label>
              <div className="relative">
                <input
                  type="text"
                  value={kvDraft}
                  onChange={(e) => { setKvDraft(e.target.value); updateValueSuggestions(e.target.value); }}
                  onFocus={() => updateValueSuggestions(kvDraft)}
                  onKeyDown={(e) => {
                    if (!showValueSuggestions || valueSuggestions.length === 0) {
                      if (e.key === 'Enter') {
                        const str = String(kvDraft || '');
                        const m = str.match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.+)$/);
                        if (m) {
                          const key = m[1].trim();
                          const val = m[2].trim();
                          if (key && val) {
                            setKvRows(r => [...r, { key, value: val }]);
                            setKvDraft('');
                            setShowValueSuggestions(false);
                          }
                        }
                      }
                      return;
                    }
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setActiveSuggestionIndex((idx) => (idx + 1) % valueSuggestions.length);
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setActiveSuggestionIndex((idx) => (idx - 1 + valueSuggestions.length) % valueSuggestions.length);
                    } else if (e.key === 'Enter') {
                      const str = String(kvDraft || '');
                      const m = str.match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.*)$/);
                      if (m && activeSuggestionIndex >= 0) {
                        e.preventDefault();
                        const key = m[1].trim();
                        const chosen = valueSuggestions[activeSuggestionIndex];
                        setKvDraft(`${key}: ${chosen}`);
                        setShowValueSuggestions(false);
                      }
                    } else if (e.key === 'Escape') {
                      setShowValueSuggestions(false);
                    }
                  }}
                  placeholder="Name: Lewis"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                />
                {showValueSuggestions && valueSuggestions.length > 0 && (
                  <div className="absolute left-0 right-0 mt-1 max-h-48 overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg z-10">
                    {valueSuggestions.map((v, idx) => (
                      <div
                        key={v + idx}
                        className={`px-3 py-2 text-sm cursor-pointer ${idx === activeSuggestionIndex ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                        onMouseEnter={() => setActiveSuggestionIndex(idx)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const str = String(kvDraft || '');
                          const m = str.match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.*)$/);
                          const key = m ? m[1].trim() : '';
                          const composed = `${key}: ${v}`;
                          setKvDraft('');
                          setShowValueSuggestions(false);
                          if (key && v) setKvRows(r => [...r, { key, value: v }]);
                        }}
                      >
                        {v}
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">Press Enter to add as a row.</div>
              </div>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Apply Tags</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Select key/value pairs to apply as tags. Order determines: value1_value2.ext</p>
            <div className="space-y-2">
              {kvRows.map((row, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <select
                    value={row.key}
                    onChange={(e) => {
                      const key = e.target.value; const firstVal = (keyToValues.get(key) || [])[0] || '';
                      setKvRows(r => r.map((x,i) => i===idx ? { key, value: firstVal } : x));
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white w-48"
                  >
                    <option value="">Select key…</option>
                    {keyOptions.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <select
                    value={row.value}
                    onChange={(e) => setKvRows(r => r.map((x,i) => i===idx ? { ...x, value: e.target.value } : x))}
                    disabled={!row.key}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select value…</option>
                    {(keyToValues.get(row.key) || []).map(v => <option key={`${row.key}:${v}`} value={v}>{v}</option>)}
                  </select>
                  <button
                    onClick={() => setKvRows(r => r.filter((_,i) => i!==idx))}
                    className="px-2 py-2 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                onClick={() => setKvRows(r => [...r, { key: '', value: '' }])}
                className="px-3 py-2 text-sm rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
              >
                + Add Row
              </button>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Template</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Create new template name (moved to first position) */}
              <input
                type="text"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="Create new template name..."
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              />
              {/* Target path auto-builder */}
              <input
                type="text"
                value={newTemplatePath}
                onChange={(e) => setNewTemplatePath(e.target.value)}
                placeholder="Target path (auto-filled)"
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Path format: <span className="font-mono">Section/&lt;value1&gt;/&lt;value2&gt;/</span>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* Template dropdown */}
              <select
                value={selectedTemplateId || ''}
                onChange={(e) => onSelectTemplate(e.target.value || null)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              >
                <option value="">No template</option>
                {(templates || []).map(t => {
                  const id = t.id || t.template_id;
                  const name = t.name || `Template ${id}`;
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>
              {/* Read-only display of selected template path if available */}
              <input
                type="text"
                readOnly
                value={(() => {
                  const t = (templates || []).find(x => (x.id || x.template_id) === selectedTemplateId);
                  const path = t?.actions?.move_to_folder || t?.actions?.moveToFolder || '';
                  return path || newTemplatePath;
                })()}
                placeholder="Template path"
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/60 text-gray-700 dark:text-gray-200"
              />
            </div>
            <div className="mt-3">
              <button onClick={onCreateTemplate} disabled={creatingTemplate || !newTemplateName.trim()} className="px-3 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50">
                {creatingTemplate ? 'Creating...' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3">
          {/* Preview & Overrides */}
          {files && files.length > 0 && (
            <PreviewRenameList
              files={Array.from(files)}
              tags={tags}
              selectedTagIds={selectedTagIds}
              templatePath={newTemplatePath}
              explicit={explicit}
              kvRows={kvRows}
              uploading={uploading}
              progress={progress}
              onApprove={(overrides) => onConfirm(overrides)}
            />
          )}
          <div className="flex justify-end gap-3">
            <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200">Cancel</button>
            <button onClick={() => onConfirm([])} className="px-4 py-2 rounded-lg bg-blue-600 text-white">Upload</button>
          </div>
        </div>
      </div>
    </div>
  );
};
// Small preview list with inline overrides
const PreviewRenameList = ({ files, tags, selectedTagIds, templatePath, explicit, kvRows = [], uploading = false, progress = [], onApprove }) => {
  const [rows, setRows] = React.useState(() => files.map((f, i) => ({
    original: f.name || f.filename || `file_${i+1}`,
    override: ''
  })));

  const buildProposedName = (fileIndex) => {
    const f = files[fileIndex];
    const ext = ((f.name || f.filename || '').split('.').pop() || '').toLowerCase();
    const imageExts = ['jpg','jpeg','png','gif','webp','bmp','svg'];
    const sanitize = (s) => (s || '').toString().trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_\-]+/g, '').replace(/^_+|_+$/g, '') || '';
    const selected = (tags || []).filter(t => selectedTagIds.includes(t.tag_id || t.id));
    const names = selected.map(t => (t.name || t.label || ''));
    // Extract value from key:value style tags
    const extractKV = (raw) => {
      const s = (raw || '').toString();
      const parts = s.split(/[:=]+/);
      if (parts.length >= 2) {
        const key = (parts[0] || '').trim();
        const val = parts.slice(1).join(':').trim();
        return { key: key.toLowerCase(), value: val };
      }
      return { key: '', value: s.trim() };
    };
    const kvs = names.map(extractKV);
    const getValueForKey = (rx) => {
      const hit = kvs.find(kv => rx.test(kv.key));
      return hit ? hit.value : '';
    };
    const findInValues = (rx) => {
      const hit = kvs.find(kv => rx.test(kv.value));
      return hit ? hit.value : '';
    };
    const normalizeAge = (val) => {
      const s = (val || '').toString().trim();
      // direct patterns like "1 month", "1 mo", "1mo", "2 years", "2y"
      const m = s.match(/(\d+)\s*(months?|mos?|mo)\b/i);
      if (m) return `${m[1]}month`;
      const y = s.match(/(\d+)\s*(years?|yrs?|y)\b/i);
      if (y) return `${y[1]}y`;
      // bare like "18mo" or "2y"
      const bare = s.match(/^(\d+)(mo|mos|month|months|y|yr|yrs|year|years)$/i);
      if (bare) {
        const num = bare[1];
        const unit = bare[2].toLowerCase();
        if (unit.startsWith('m')) return `${num}month`;
        return `${num}y`;
      }
      return s;
    };
    const find = (rx) => names.find(n => rx.test(n || ''));
    // If KV rows present, build filename strictly from their values in order
    const kvValues = (kvRows || []).map(r => r.value).filter(Boolean).map(sanitize);
    if (kvValues.length > 0) {
      return `${kvValues.join('_')}.${ext}`;
    }
    if (imageExts.includes(ext)) {
      const person = explicit.person || getValueForKey(/^(name|person|who)$/i) || findInValues(/^[a-zA-Z]+$/) || '';
      const ageRaw = explicit.age || getValueForKey(/^age$/i) || findInValues(/\d+\s*(y|yr|yrs|year|years|m|mo|mos|month|months)\b/i) || '';
      const age = normalizeAge(ageRaw);
      const eventVal = explicit.event || getValueForKey(/^event$/i) || find(/birthday|wedding|anniversary|graduation|vacation|party|holiday|trip|concert/i) || '';
      const second = age || eventVal || '';
      if (person || second) {
        return `${sanitize(person) || 'person'}_${sanitize(second) || 'event'}_${fileIndex+1}.${ext}`;
      }
    }
    // receipts
    if ((/receipt|invoice|bill/i.test(f.name || '') || ['pdf','jpg','jpeg','png'].includes(ext)) && (explicit.merchant || explicit.date || explicit.amount)) {
      const m = sanitize(explicit.merchant) || 'merchant';
      const d = sanitize(explicit.date) || 'date';
      const a = sanitize(explicit.amount) || 'amt';
      return `${m}_${d}_${a}.${ext}`;
    }
    // spreadsheets/design
    if (['csv','xls','xlsx'].includes(ext) || /design|figma|sketch|ui|ux/i.test(f.name || '')) {
      if (explicit.project || explicit.docType || explicit.version) {
        const proj = sanitize(explicit.project) || 'project';
        const kind = sanitize(explicit.docType) || 'doc';
        const ver = sanitize(explicit.version) || 'v1';
        return `${proj}_${kind}_${ver}.${ext}`;
      }
    }
    return f.name || f.filename || '';
  };
  const proposed = rows.map((_, i) => buildProposedName(i));
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300">Filename Preview</div>
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {rows.map((row, i) => (
          <div key={i} className="p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
            <div className="md:col-span-5 text-xs text-gray-500 dark:text-gray-400 truncate" title={row.original}>{row.original}</div>
            <div className="md:col-span-5">
              <input
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                placeholder={proposed[i]}
                value={row.override}
                onChange={(e) => setRows(r => r.map((x, idx) => idx === i ? { ...x, override: e.target.value } : x))}
              />
              {uploading && (
                <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded h-1.5">
                  <div className="h-1.5 rounded bg-blue-600" style={{ width: `${progress[i] || 0}%` }} />
                </div>
              )}
            </div>
            <div className="md:col-span-2 text-right">
              <span className="text-xs text-gray-400 dark:text-gray-500">{proposed[i] && !row.override ? 'auto' : row.override ? 'custom' : ''}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 flex justify-end">
        <button
          onClick={async () => {
            const finalNames = rows.map((r, i) => (r.override?.trim() ? r.override.trim() : proposed[i]));
            try {
              const { sendSuggestionFeedback } = await import('../../services/api');
              for (let i = 0; i < rows.length; i++) {
                const original = files[i]?.name || files[i]?.filename || '';
                const suggested = proposed[i] || '';
                const chosen = finalNames[i] || original;
                const action = chosen === suggested ? 'accept' : 'override';
                // fire-and-forget; don't block UI
                sendSuggestionFeedback('rename_preview', action, { original, suggested, chosen }).catch(() => {});
              }
            } catch (_) {}
            onApprove(finalNames);
          }}
          className="px-3 py-2 text-sm rounded bg-blue-600 text-white"
        >
          Use These Names
        </button>
      </div>
    </div>
  );
};

// Template Creation Modal Component
const TemplateCreationModal = ({ template, onTemplateChange, onPathChange, onSave, onCancel }) => {
  const suggestions = React.useMemo(() => {
    // This hook will be replaced by injected suggestions prop if needed
    return [];
  }, []);
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Create Sorting Template
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Template Name *
            </label>
            <input
              type="text"
              value={template.name}
              onChange={(e) => onTemplateChange(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Family Photos by Age"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Directory Path *
            </label>
            <input
              type="text"
              value={template.directoryPath}
              onChange={(e) => onPathChange(e.target.value)}
              placeholder="e.g., Photos/<Child>/<Age>/"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Use &lt;VariableName&gt; for dynamic values. Example: Photos/&lt;Child&gt;/&lt;Age&gt;/
            </p>

            {/* Live path guidance */}
            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded">
              <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">Structure insights</p>
              <ul className="text-xs text-gray-600 dark:text-gray-300 list-disc list-inside space-y-1">
                <li>Nesting depth: <span className="font-medium">{template.styleHints?.nestingDepth ?? 0}</span></li>
                <li>Uses year segment: <span className="font-medium">{template.styleHints?.usesYearSegment ? 'Yes' : 'No'}</span></li>
                <li>Uses category segment: <span className="font-medium">{template.styleHints?.usesCategorySegment ? 'Yes' : 'No'}</span></li>
                <li>Variables detected: <span className="font-medium">{template.styleHints?.variablesCount ?? 0}</span></li>
              </ul>
            </div>

            {/* Suggested structures */}
            {template.suggestions && template.suggestions.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">Suggested structures</p>
                <div className="flex flex-wrap gap-2">
                  {template.suggestions.map((sug) => (
                    <button
                      key={sug}
                      type="button"
                      onClick={() => {
                        onPathChange(sug);
                        // record user picked suggestion for learning
                        import('../../services/api').then(({ sendSuggestionFeedback }) => {
                          sendSuggestionFeedback('template_suggestion', 'accept', { suggestion: sug }).catch(() => {});
                        });
                      }}
                      className="px-2.5 py-1 text-xs rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 hover:bg-orange-200 dark:hover:bg-orange-800"
                      title="Use this structure"
                    >
                      {sug}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={template.description}
              onChange={(e) => onTemplateChange(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what this template does..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Apply to File Types
            </label>
            <div className="flex flex-wrap gap-2">
              {['all', 'photos', 'documents', 'audio', 'videos'].map((type) => (
                <label key={type} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={template.fileTypes.includes(type)}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      onTemplateChange(prev => ({
                        ...prev,
                        fileTypes: isChecked
                          ? [...prev.fileTypes, type]
                          : prev.fileTypes.filter(t => t !== type)
                      }));
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{type}</span>
                </label>
              ))}
            </div>
          </div>
          {template.variables.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Detected Variables
              </label>
              <div className="space-y-3">
                {template.variables.map((variable, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {variable.name}
                      </span>
                      <select
                        value={variable.source}
                        onChange={(e) => {
                          const newVariables = [...template.variables];
                          newVariables[index] = { ...variable, source: e.target.value };
                          onTemplateChange(prev => ({ ...prev, variables: newVariables }));
                        }}
                        className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                      >
                        <option value="tag">From Tag</option>
                        <option value="ai">AI Extract</option>
                        <option value="manual">Manual Entry</option>
                      </select>
                    </div>
                    {variable.source === 'tag' && (
                      <input
                        type="text"
                        value={variable.tagName}
                        onChange={(e) => {
                          const newVariables = [...template.variables];
                          newVariables[index] = { ...variable, tagName: e.target.value };
                          onTemplateChange(prev => ({ ...prev, variables: newVariables }));
                        }}
                        placeholder="Tag name to look for"
                        className="w-full text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={!template.name.trim() || !template.directoryPath.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Create Template
          </button>
        </div>
      </div>
    </div>
  );
};
// Template Variable Resolution Modal Component
const TemplateVariableModal = ({ file, template, missingVariables, onResolve, onCancel }) => {
  const [resolvedValues, setResolvedValues] = React.useState({});
  const [isProcessing, setIsProcessing] = React.useState(false);

  const handleValueChange = (variableName, value) => {
    setResolvedValues(prev => ({
      ...prev,
      [variableName]: value
    }));
  };

  const handleAIExtract = async (variable) => {
    setIsProcessing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const filename = file.filename?.toLowerCase() || '';
      let extractedValue = '';

      if (variable.name.toLowerCase().includes('age')) {
        if (filename.includes('baby')) extractedValue = 'Baby';
        else if (filename.includes('toddler')) extractedValue = 'Toddler';
        else if (filename.includes('child')) extractedValue = 'Child';
        else extractedValue = '5 Years';
      } else if (variable.name.toLowerCase().includes('child') || variable.name.toLowerCase().includes('person')) {
        const namePatterns = ['Emma', 'Noah', 'Olivia', 'Liam', 'Ava'];
        extractedValue = namePatterns[Math.floor(Math.random() * namePatterns.length)];
      } else {
        extractedValue = `AI_${variable.name}`;
      }

      handleValueChange(variable.name, extractedValue);
    } catch (error) {
      console.error('AI extraction error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResolve = () => {
    onResolve(resolvedValues);
  };

  const getFinalPath = () => {
    let path = template.directoryPath;
    missingVariables.forEach(variable => {
      const value = resolvedValues[variable.name] || `<${variable.name}>`;
      path = path.replace(`<${variable.name}>`, value);
    });
    return path;
  };

  const allVariablesResolved = missingVariables.every(variable => 
    resolvedValues[variable.name]?.trim()
  );
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Resolve Template Variables
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-6">
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">File</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{file.filename}</p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Final Path Preview</h3>
            <p className="text-sm font-mono text-blue-700 dark:text-blue-300 mb-2">
              {getFinalPath()}
            </p>
            <div className="flex items-center space-x-1 text-xs text-green-600 dark:text-green-400">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              <span>AI will create any missing folders in this path</span>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Resolve Missing Variables
            </h3>
            <div className="space-y-4">
              {missingVariables.map((variable) => (
                <div key={variable.name} className="border border-gray-200 dark:border-gray-600 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {variable.name}
                    </label>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Source: {variable.source}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={resolvedValues[variable.name] || ''}
                      onChange={(e) => handleValueChange(variable.name, e.target.value)}
                      placeholder={`Enter ${variable.name}...`}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <button
                      onClick={() => handleAIExtract(variable)}
                      disabled={isProcessing}
                      className="px-3 py-2 text-xs font-medium text-white bg-purple-600 border border-transparent rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isProcessing ? '...' : 'AI'}
                    </button>
                  </div>
                  
                  {variable.source === 'tag' && (
                    <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs">
                      <div className="flex items-center space-x-1 text-blue-700 dark:text-blue-300">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Missing tag: <strong>{variable.tagName}</strong></span>
                      </div>
                      <div className="flex items-center space-x-1 mt-1 text-green-600 dark:text-green-400">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        <span>AI will create this tag automatically</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleResolve}
            disabled={!allVariablesResolved || isProcessing}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Apply Template
          </button>
        </div>
      </div>
    </div>
  );
};